import { CubeSide } from "@aec-craft/nb-generative";
import {
	BimType,
	DefaultRegionType,
	FragmentGapFillMethod,
} from "@aec-craft/nb-generative-core";
import type { BillingStatus } from "@be/z/z-billing";
import { ExportStatus } from "@be/z/z-export";
import { Lang } from "./lang";

export type Translations = Readonly<typeof en>;

const mappingCategoryLabelsEn: Record<string, string> = {
	walls: "Walls",
	doors: "Doors",
	windows: "Windows",
	floors: "Floors",
	roof: "Roof",
};

const mappingSlotLabelsEn: Record<string, string> = {
	"wall:facade": "Facade wall",
	"wall:partition": "Partition wall",
	door: "Door",
	window: "Window",
	"floor:structural": "Structural floor",
	"floor:finish": "Finish floor",
	roof: "Roof",
};

const mappingCategoryLabelsDe: Record<string, string> = {
	walls: "Wände",
	doors: "Türen",
	windows: "Fenster",
	floors: "Böden",
	roof: "Dach",
};

const mappingSlotLabelsDe: Record<string, string> = {
	"wall:facade": "Fassadenwand",
	"wall:partition": "Trennwand",
	door: "Tür",
	window: "Fenster",
	"floor:structural": "Tragende Decke",
	"floor:finish": "Bodenbelag",
	roof: "Dach",
};

const en = {
	bimTypes: {
		[BimType.world]: "World",
		[BimType.site]: "Site",
		[BimType.plot]: "Plot",
		[BimType.building]: "Building",
		[BimType.wing]: "Wing",
		[BimType.fragment]: "Fragment",
		[BimType.load]: "Load",
		[BimType.level]: "Level",
		[BimType.corridor]: "Corridor",
		[BimType.roof]: "Roof",
		[BimType.commercialSpace]: "Commercial Section",
		[BimType.apartment]: "Apartment",
		[BimType.core]: "Core",
		[BimType.wall]: "Facade walls",
		[BimType.door]: "Door",
		[BimType.window]: "Window",
		[BimType.opening]: "Opening",
		[BimType.floor]: "Floor",
		[BimType.ceiling]: "Ceiling",
		[BimType.room]: "Room",
		[BimType.box]: "Box",
		[BimType.massingConfig]: "Massing Config",
		[BimType.wingConfig]: "Wing Config",
		[BimType.extrusion2D]: "Extrusion 2D",
		[BimType.genericTechnicalSpace]: "Generic Technical Section",
		[BimType.railing]: "Railing",
		[BimType.parkingSpot]: "Parking Spot",
		[BimType.meta]: "Meta",
		[BimType.stairs]: "Stairs",
		[BimType.region]: "Region",
		[BimType.path]: "Path",
		[BimType.lineParking]: "Line parking",
	},
	regionTypes: {
		[DefaultRegionType.Parking]: "Parking",
		[DefaultRegionType.GreenSpace]: "Green space",
		[DefaultRegionType.PavedArea]: "Paved area",
		[DefaultRegionType.Recreation]: "Recreation",
		[DefaultRegionType.ServicesUtilities]: "Services/utilities",
	},
	cubeFaces: {
		[CubeSide.top]: "TOP",
		[CubeSide.bottom]: "BOTTOM",
		[CubeSide.left]: "LEFT",
		[CubeSide.right]: "RIGHT",
		[CubeSide.front]: "FRONT",
		[CubeSide.back]: "BACK",
	},
	displaySettings: {
		mapViewMode: "Map View Mode",
		noMap: "No Map",
		map2D: "2D Map",
		map2DOsmBuildings: "2D Map + OSM Buildings",
		map2DBasemapDe: "2D Map + basemap.de LoD2",
		map3D: "3D Map",
		camera: "Camera",
		orthographic: "Orthographic",
		perspective: "Perspective",
		display: "Display",
		loadParcels: "Load Parcels",
		loadParcelsHint: "ALKIS shapefile .zip – click or drop here",
		opacity: "Opacity",
		outline: "Outline",
		ambientOcclusion: "Ambient occlusion",
	},
	underlays: {
		title: "Underlays",
		upload: "Upload PDF or image",
		uploadHint: (limit: number) =>
			`PDF, PNG, JPG or WebP up to ${limit} MB – or drop onto the map`,
		uploading: (name: string) => `Preparing ${name}…`,
		empty: "Upload a Bebauungsplan or site plan to place it under your site.",
		show: "Show",
		hide: "Hide",
		lock: "Lock",
		unlock: "Unlock",
		moreActions: "More actions",
		delete: "Delete",
		uploadFailed: (name: string, reason: string) =>
			`Could not upload ${name}: ${reason}`,
		fileTooLarge: (limit: number) => `File must be ${limit} MB or less`,
		unsupportedFile: "Only PDF, PNG, JPG and WebP files are supported",
	},
	navigation: {
		infoMode: "Info mode - view building information",
		settingsTooltip: "Settings",
		generationTooltip: "Generation",
		visibilityTooltip: "Visibility",
		sunTooltip: "Sun position",
		elementsTooltip: "Elements",
		valuationTooltip: "Valuation",
		enhanceImageTooltip: "Enhance Image",
		exportTooltip: "Export",
		variantsTooltip: "Variants",
		variantsPanelTitle: "Variants",
		variantsPanelDescription:
			"Compare and manage design options by efficiency, coverage, and GFA.",
		metricsTooltip: "Metrics",
		metricsPanelTitle: "Metrics",
		metricsPanelDescription:
			"View project metrics including areas, ratios, and unit mix.",
		chatTooltip: "Chat",
		chatPanelTitle: "Chat",
		chatPanelDescription: "Ask questions about your project",
		paragraph34Tooltip: "§34 German Building Code",
		paragraph34PanelTitle: "§34 German Building Code",
		paragraph34PanelDescription:
			"Neighbourhood context targets (first site only)",
		underlaysTooltip: "Underlays",
		reportTooltip: "Report",
		warningsTooltip: "Warnings",
		warningsPanelTitle: "Warnings",
		warningsPanelDescription: "Validation warnings for the current layout",
		elements: "Elements",
		variants: (layout: string) => `Derived from ${layout}`,
		layouts: "Layouts",
		sortBy: "Sort by",
		compareFields: "Compare fields",
		duplicateLayout: "Duplicate layout",
		renameLayout: "Rename layout",
		pinVariant: "Save as layout",
		removeLayout: "Remove layout",
		removeVariant: "Remove variant",
		variantFieldShortLabels: {
			gfa: "GFA",
			layoutEfficiency: "Efficiency",
			gsi: "GSI",
			buildingFootprint: "Footprint",
			far: "FAR",
			avr: "AVR",
			constructionCost: "Cost",
		},
	},
	tools: {
		moreTools: "More tools",
		viewOnlyMode: "View only mode",
		editMode: "Edit mode",
		commentMode: "Comment mode",
		selectParcelAsSite: "Select parcel as a new site",
		drawNewSite: "Draw a new site",
		cutSites: "Cut sites",
		unionSites: "Union sites",
		deleteSite: "Delete site",
		editSite: "Edit site",
		editSiteOffset: "Edit site offset",
		editSiteSetback: "Edit Site Setbacks",
		addNonBuildableArea: "Add no-build region",
		deleteNonBuildableArea: "Delete no-build region",
		drawBuildableArea: "Draw buildable area",
		deleteBuildableArea: "Delete buildable area",
		addPath: "Add path",
		deletePath: "Delete path",
		pathWidth: "Width",
		pathTurningRadius: "Turning radius",
		pathAffectsBuildingSetback: "Affects building setback",
		addLineParking: "Add line parking",
		deleteLineParking: "Delete line parking",
		lineParkingStallWidth: "Stall width",
		lineParkingStallDepth: "Stall depth",
		lineParkingAccessDepth: "Access depth",
		lineParkingType: "Type",
		lineParkingTilt: "Tilt",
		lineParkingFlipSide: "Flip side",
		regionFill: "Fill",
		regionType: "Type",
		regionAffectsBuildingSetback: "Affects building setback",
		unknownRegionType: "Unknown",
		addCustomRegionType: "Add custom region type",
		customRegionTypePlaceholder: "New region type",
		addNewBuilding: "Add new building",
		undo: "Undo",
		enhancementStarted: "Image enhancement started",
		enhancementComplete: "Image enhancement complete",
		redo: "Redo",
		deleteBuilding: {
			enabled: "Delete building",
			disabled: "Only saved buildings can be deleted",
		},
		moveRotateBuilding: "Move/Rotate Building",
		changeWingsLength: "Change Wings Length",
		addNewWings: {
			enabled: "Add new wings",
			disabled: "Wings cannot be added to this shape of building",
		},
		editLevels: "Edit Levels",
		editFragmentLevels: "Edit Fragment Levels",
		editFragmentBoundaries: {
			enabled: "Edit Fragment Boundaries",
			disabled: "Only wings split into several fragments have boundaries",
		},
		flipLoad: {
			enabled: "Flip Load",
			disabled: "Only single-load buildings can be load-flipped",
		},
		coreSide: {
			enabled: "Change Core Side",
			disabled: "Only double-load buildings have two sides for cores",
		},
		cornerCoreSide: {
			enabled: "Change Corner Core Side",
			disabled: "Only shifted corners have a corner core side to change",
		},
		editCore: "Edit Core",
		floorplanEditor: "Floorplan editor",
		autoRotate: "Auto rotate camera",
		focusCamera: "Focus camera",
		orientateCamera: "Orientate camera",
		captureCurrentView: "Capture Current View",
		captureAndEnhance: "Capture and Enhance",
		enhanceImage: "Enhance Image",
		imageHistory: "Image History",
		clear: "Clear",
		openInNewTab: "Open in new tab",
		downloadImage: "Download image",
		enhancing: "Enhancing",
		enhanced: "Enhanced",
		original: "Original",
		failed: "Failed",
		loadMore: "Load More",
		deleteImage: "Delete image",
		generateVideo: "Generate Video",
		viewVideo: "View Video",
		enhancementOptions: "Enhancement Options",
		solarPanels: "Solar Panels",
		landscaping: "Landscaping",
		whiteModel: "White Model",
		customEnhancement: "Custom Enhancement",
		customPromptPlaceholder: "Describe how it should look...",
		generationSteps: {
			enhance: "Enhancing",
			customPrompt: "Applying enhancements",
		},
		stepMayTakeMinutes: "This step may take a few minutes",
		cancelOperation: "Cancel operation",
		finishOperation: "Finish operation",
		measureDistance: "Measure",
	},
	errors: {
		canvasNotFound: "Canvas not found. Please ensure the 3D view is loaded.",
		imageEnhancementFailed: "Failed to enhance image.",
		imageDeleteFailed: "Failed to delete image. Please try again.",
		liveSessionFailed: "Live session connection failed",
		liveSessionDisconnected:
			"The live session ended and couldn't reconnect, so changes from now on aren't saved. Reload the page; if it still fails, check your access to this project and its organisation's plan.",
	},
	feedback: {
		tooltip: "Feedback",
		title: "Give us your feedback",
		description:
			"Are you experiencing any errors? Have a feature request? Send us a message and a member of the team will contact you.",
		message: "Message",
		send: "Send",
		sending: "Sending",
		cancel: "Cancel",
		error: "There was an error sending your message, please try again later.",
		success: "Your message has been successfully sent.",
		ok: "Ok",
	},
	contact: {
		title: "Contact us",
		description:
			"If you have any questions, feedback or need assistance, please reach out to us.",
		message: "Message",
		send: "Send",
		sending: "Sending",
		cancel: "Cancel",
		error: "There was an error sending your message, please try again later.",
		success: "Message successfully sent, one of our team will be in touch",
		ok: "Ok",
	},
	chatPanel: {
		newChat: "New Chat",
		deleteChat: "Delete chat",
	},
	buildingSettings: {
		title: "Building Settings",
		levels: "Levels",
		global: "Global",
		building: "Building",
	},
	generationPanel: {
		generation: "Generation",
		target: "Target",
		wingLengthRange: "Length Range",
		wingLengthDescription: "Minimum and maximum length for building wings",
		wingMinLength: "Min. Length",
		wingMinLengthDescription: "Minimum length for building wings",
		wingMaxLength: "Max. Length",
		wingMaxLengthDescription: "Maximum length for building wings",
		maxFragmentLength: "Max. Regular Section Length",
		maxFragmentLengthDescription:
			"Maximum length of every section, corners too unless they have their own limit",
		maxCornerFragmentLength: "Max. Corner Section Length",
		maxCornerFragmentLengthDescription:
			"Own maximum length for corner sections, measured from the outer facade of the crossing wing",
		corridorEndApartments: "Apartments at Corridor Ends",
		corridorEndApartmentsDescription:
			"Let an apartment close the corridor at the ends of the building. Off keeps the corridor open to the end facade, e.g. for a corridor window.",
		shiftedCorners: "Shifted Corners",
		shiftedCornersDescription:
			"Fill L-corners of double-load wings with the set's corner units around a core shifted out of the corner. Off solves corners like the rest of the wing.",
		wingMinSetback: "Min. Setback",
		wingMinSetbackDescription:
			"Minimum reserved distance on each building side",
		wingSetbackFactor: "Setback Factor",
		wingSetbackFactorDescription:
			"Multiplied by building height to calculate setback distance",
		balconiesAffectMassing: "Balconies Affect Massing",
		balconiesAffectMassingDescription:
			"Reserve the balcony-inflated setback during generation instead of the base setback",
		avoidNorthFactor: "Avoid North",
		avoidNorthFactorDescription:
			"How strongly generation avoids wings with apartments facing north (±30°). 0 = off, 1 = always avoid",
		corridorWidth: "Corridor Width",
		corridorWidthDescription: "Default corridor width for all buildings",
		globalBuildingHeight: "Max Levels",
		levels: "levels",
		globalBuildingHeightDescription:
			"Maximum number of levels for all generated buildings",
		unitHeight: "Height",
		unitHeightDescription: "Unit height for residential units",
		unitDepth: "Depth",
		unitDepthDescription: "Unit depth for residential units",
		unitMix: "Unit Mix",
		unitMixEnabled: "Enabled",
		lockRatio: "Lock ratio",
		unlockRatio: "Unlock ratio",
		resetToEqualDistribution: "Reset to Equal Distribution",
		commercialSection: "Commercial Section",
		commercialSectionDescription:
			"Adds a commercial section on the ground level with the specified height",
		commercialHeight: "Commercial Height",
		site: "Site",
		maxGsi: "Max GSI",
		maxGsiDescription:
			"Maximum share of the plot area that buildings may cover.",
		maxAncillaryGsi: "Max GSI II",
		maxAncillaryGsiDescription:
			"Maximum share of the plot area covered by buildings plus sealed ancillary surfaces: parking, paved and utility regions, and paths. Turned off, the legal default applies: Max GSI × 1.5, capped at 0.8. Requires Max GSI — without a main coverage limit there is nothing to exceed.",
		farValue: "Max FAR",
		farValueDescription:
			"Target floor area ratio. When the limit is on, levels are added until the target is reached — bounded by Max Levels. Turn it off to remove the FAR cap.",
		noLimit: "No limit",
		loadType: "Load Type",
		loadTypeDouble: "Double",
		loadTypeSingle: "Single",
		loadTypeCorner: "Corner",
		buildingGroup: "Building",
		wingsGroup: "Wings",
		unitsGroup: "Units",
		bimObjects: "BIM Objects",
		bimObjectThicknessDescription:
			"Thickness in metres used when generating this element. Kept in sync with the mapped Revit family size.",
		gapFillingGroup: "Gap Filling",
		gapFillingDoubleLoadOnly: "For double-load buildings only",
		fragmentGapFillMethod: "Method",
		fragmentGapFillMethodDescription:
			"How gaps left in a section after placing apartments are closed:\n• Expand Largest Unit – widens the largest apartment that can grow into the gap.\n• Expand Smallest Unit – widens the smallest apartment that can grow into the gap.\n• Distribute Equally – shares the gap evenly between all apartments that can grow into it.\n• Technical Spaces – fills the gap with technical spaces.\n• None – leaves the gap to the following steps.\nAny gaps still remaining are filled by expanding the corner core (if enabled) and then with generic technical spaces.",
		fragmentGapFillMethods: {
			[FragmentGapFillMethod.expandLargestUnit]: "Expand Largest Unit",
			[FragmentGapFillMethod.expandSmallestUnit]: "Expand Smallest Unit",
			[FragmentGapFillMethod.distributeEqually]: "Distribute Equally",
			[FragmentGapFillMethod.technicalSpaces]: "Technical Spaces",
			[FragmentGapFillMethod.none]: "None",
		},
		resizeCornerCores: "Expand Corner Cores",
		resizeCornerCoresDescription:
			"If there are still gaps after the gap filling method, tries to fill them by expanding the corner core. The core can only expand in one direction – towards the larger gap. Any gaps still left over are filled with generic technical spaces. Always disabled when the Technical Spaces method is used.",
		parkingGroup: "Parking",
		parkingFactors: "Parking factors",
		parkingFactorsInfo:
			"Parking factors apply in order: apartment type, then apartment area range, then the remaining total floor area. Each apartment is counted by the first level that matches it — n is the number of apartments a level counts. Area ranges use living area and exclude their lower bound but include their upper bound, such as small/medium/large bands in a municipal parking bylaw. Total floor area applies per editable area basis to the living area of apartments not counted by earlier factors. Please check your municipality's regulations to determine the correct values for your project.",
		parkingFactorsInfoLabel: "About parking factors",
		parkingFactorApartmentType: "Apartment type",
		parkingFactorApartmentArea: "Apartment area range",
		parkingFactorTotalArea: "Total floor area",
		parkingAddFactor: "Add factor",
		parkingRemoveFactor: "Remove factor",
		parkingRemoveRow: "Remove row",
		parkingAddType: "Add type",
		parkingAddRange: "Add range",
		parkingType: "Type",
		parkingSelectType: "Select type",
		parkingUnknownApartmentType:
			"A row's apartment type isn't in the current apartment set. It counts no apartments until you pick a type.",
		parkingMinArea: "(Min",
		parkingMaxArea: "Max]",
		parkingAreaBasis: "Per",
		parkingRemainingArea: "Area",
		parkingCount: "n",
		parkingSpaces: "Spaces",
		parkingSpacesPerApartmentTypeDescription:
			"Number of parking spaces required per apartment of this type.",
		parkingSpacesPerAreaRangeDescription:
			"Number of parking spaces required per apartment within this living area range.",
		parkingSpacesPerFloorAreaDescription:
			"Number of parking spaces required per area basis of the remaining living area.",
		parkingAllApartmentTypesAdded: "All apartment types are already included.",
		parkingAreaRangeCovered:
			"The ranges already reach the maximum apartment area. Lower the highest upper bound to add another range.",
		parkingInvalidRange: "A range's Max must be greater than its Min.",
		parkingOverlappingRanges:
			"Ranges overlap. Apartments in the overlap are counted by the first matching range only.",
		parkingIncompleteRanges: (maxArea: number) =>
			`Ranges don't cover every apartment area from 0 to ${maxArea} m². Apartments outside them fall through to the total floor area factor, or aren't counted.`,
		parkingMultiplier: "Multiplier",
		parkingMultiplierDescription:
			"Multiplier that adjusts parking demand based on the site context, such as public transport access, centrality, parking pressure, car-sharing, or mobility concept.",
		requiredParkingSpots: "Required parking spots",
		requiredParkingSpotsDescription:
			"Sum of the parking spaces from all factors, adjusted by the multiplier.",
		totalParkingSpots: "Total Parking Spots",
		totalParkingSpotsDescription: "Total number of parking spots available.",
		apartmentTypes: "Apartment Types",
		newType: "New Type",
		editType: "Edit type",
		removeType: "Remove type",
		name: "Name",
		rooms: "Rooms",
		inhabitants: "Inhabitants",
		width: "Width",
		depth: "Depth",
		cancel: "Cancel",
		save: "Save",
	},
	targets: {
		suggested: "§34 German Building Code",
		useSuggested: "use suggested",
	},
	hierarchyPanel: {
		site: "Site",
		building: "Building",
	},
	visibilityPanel: {
		elementVisibility: "Element Visibility",
		site: "Site",
		outerBuilding: "Building",
		units: "Units",
		unitElements: "Unit Elements",
		resetToDefaults: "Reset to Defaults",
		buildingSetbacks: "Building Setbacks",
		plotSetbacks: "Site Setbacks",
		warnings: "Warnings",
		logo: "Organisation Logo",
		dimensions: "Dimensions",
		streets: "Streets",
		general: "General",
		facade: "Facade",
		structuralFloors: "Structural Floors",
		finishFloors: "Finish Floors",
	},
	sunPanel: {
		resetToDefaults: "Reset to Defaults",
		shadows: "Shadows",
		date: "Date",
		time: "Time",
		sunHoursAnalysis: "Sun Hours Analysis",
		daylightPotential: "Daylight Potential",
		solarRadiation: "Solar Radiation",
		sunHoursAnalysisCaption: "Hours of direct unobstructed sunlight",
		sunHoursAnalysisDescription:
			"Hours of direct sun on the selected date, counting shade cast by the scheme itself and by mapped buildings within 500 m. The scale stops at 9 hours; anything above that shares the top colour.",
		daylightPotentialCaption: "Visible portion of sky (%)",
		daylightPotentialDescription:
			"How much of the sky each surface can see, weighted for an overcast sky and measured against an unobstructed horizontal surface — the Vertical Sky Component used in BRE daylight guidance. A flat roof reaches 100%, a facade with nothing in front of it about 40%, where the scale ends. BRE regards 27% or more as enough skylight for a window.",
		solarRadiationCaption: "Annual energy received (kWh/m²)",
		solarRadiationDescription:
			"Solar energy landing on each surface over a year, including shade from the scheme itself and from mapped buildings within 500 m. The top of the scale is what a perfectly angled PV panel could receive at this location.\n\nWeather data: PVGIS © European Communities, 2001-2026.",
		hours: "Hours",
		percentOfSky: "% of Sky",
		kWhPerSqm: "kWh/m²",
		analysisFailed: "Analysis failed. Please try again.",
		total: "Total",
		facade: "Facade",
		roof: "Roof",
		ground: "Ground",
	},
	valuationPanel: {
		valuation: "Valuation",
		total: "Total",
		view: "View",
		project: "Project",
		statistics: "Statistics",
		livingArea: "Living Area",
		facadeArea: "Facade Area",
		windowArea: "Window Area",
		buildingYear: "Year of Completion",
		buildingsYear: "Year of Completion",
		defaultBuildingYear: "Default Year",
		buildingYearHelperDefault: "Default year of completion for new buildings",
		buildingYearPastIndicator: "Past year",
		buildingYearPresentIndicator: "Present year",
		residentialUnits: "Residential Units",
		buildings: "Buildings",
		constructionCost: "Construction Cost",
		totalCost: "Total Cost",
		marketValue: "Market Value",
		price: "Price",
		salePrice: "Sale price",
		priceRange: "Price Range",
		priceRangeMin: "Price Range Min",
		priceRangeMax: "Price Range Max",
		rentPrice: "Rent Price",
		netRent: "Net Rent",
		rentRange: "Rent Range",
		rentRangeMin: "Rent Range Min",
		rentRangeMax: "Rent Range Max",
		valuationUpdated: "Valuation updated:",
		confidence: "Confidence:",
		// New Valuation Section translations
		projectGeometry: "Project Geometry",
		residentialArea: "Residential Area",
		residentialAreaHelper: "Living area (net floor area)",
		grossFloorArea: "Gross Floor Area",
		grossFloorAreaHelper: "Total floor area including walls",
		footprint: "Building Footprint",
		footprintHelper: "Ground floor area covered by building",
		apartments: "Apartments",
		levels: "Levels",
		constructionCosts: "Construction Costs",
		basebuild: "Base Build",
		electrical: "Electrical",
		hvac: "HVAC",
		hvacFullName: "Heating, Ventilation & Air Conditioning",
		shellConstruction: "Shell Construction",
		siteSetup: "Site Setup",
		drainage: "Drainage",
		facade: "Facade",
		costsPerSqmResidential: "Costs per m² (Residential Area)",
		ancillaryCosts: "Ancillary Building Costs",
		costsPerItem: "Costs per Item",
		lift: "Elevator",
		doors: "Doors",
		bathrooms: "Bathrooms",
		balcony: "Balcony",
		parking: "Parking",
		costsPerSqmFootprint: "Costs per m² (Footprint)",
		roof: "Roof",
		cellar: "Cellar",
		totalConstructionCost: "Total Construction Cost",
		totalProjectCosts: "Total Project Costs",
		landCostPerSqm: "Land Cost per m²",
		landCostTotal: "Total Land Cost",
		landCostTotalHelper: "Edit total to back-calculate per m² price",
		sideCosts: "Side Costs",
		sideCostsHelper: "Permits, fees, etc. (% of construction cost)",
		totalCostsPreFinance: "Total Costs (Pre-Finance)",
		financing: "Financing",
		buildTime: "Build Time",
		equityShare: "Equity Share",
		loanInterestRate: "Loan Interest Rate",
		loanShare: "Loan Share",
		equity: "Equity",
		loan: "Loan",
		totalInterestCost: "Total Interest Cost",
		totalInterestCostHelper:
			"Progressive financing - funds drawn evenly over build period. Interest calculated on average outstanding balance (50% of total loan)",
		totalProjectCostAllIn: "Total Project Cost (All-in)",
		revenueAssumptions: "Revenue Assumptions",
		salesPricePerSqm: "Sales Price per m²",
		salesPriceOverride: "Override Price",
		totalSalesValue: "Total Sales Value",
		summaryResults: "Summary & Results",
		netProfit: "Net Profit",
		equityInvested: "Equity Invested",
		returnOnEquity: "Return on Equity (ROE)",
		returnOnEquityHelper: "Net profit / Equity invested",
		returnOnCost: "Return on Cost (ROC)",
		returnOnCostHelper: "Net profit / Total project cost",
	},
	metricsPanel: {
		projectOverview: "Site Metrics",
		projectOverviewDescription:
			"Basic project-wide values for land and apartment count.",
		buildingArea: "Building Area",
		buildingAreaDescription:
			"Total built-up, footprint, and usable internal space.",
		developmentRatios: "Development Ratios",
		developmentRatiosDescription:
			"Key performance indicators derived from the building layout.",
		regionAreasDescription:
			"Total no-build area and breakdown by type. Each type shows its share of the site and total designated no-build area. Overlapping types can total more than 100%.",
		noBuildRegions: "No-Build Regions",
		area: "Area",
		totalRegionArea: "Total Designated Area",
		siteAreaFraction: "Site Area Fraction",
		shareOfTotalDesignatedArea: "Share of Total Designated Area",
		noWorldLoaded: "No world loaded.",
		unitMixDistribution: "Unit Mix Distribution",
		unitMixDistributionDescription:
			"Breakdown of unit types by percentage and quantity.",
		siteArea: "Site Area",
		siteAreaDescription: "Total site area",
		apartments: "Apartments",
		apartmentsDescription: "Total number of apartments",
		buildingsDescription: "Total number of buildings",
		buildingFootprint: "Building Footprint",
		buildingFootprintDescription: "Total building footprint area",
		layoutEfficiency: "Layout Efficiency",
		layoutEfficiencyDescription:
			"Ratio of net usable area to gross floor area (includes corridors, walls, shafts)",
		gsi: "Ground Space Index (GSI)",
		gsiDescription: "Ratio of building footprint to site area",
		ancillaryGsi: "Ground Space Index II (GSI II)",
		ancillaryGsiDescription:
			"Ratio of building footprint plus sealed ancillary ground to site area",
		grossFloorArea: "Gross Floor Area (GFA)",
		grossFloorAreaDescription:
			"Total floor area of all floors (includes walls, corridors, shafts)",
		netInternalArea: "Net Internal Area (NIA)",
		netInternalAreaDescription:
			"Total usable floor area (excludes walls, corridors, shafts)",
		livingArea: "Living Area (WoFlV)",
		livingAreaDescription:
			"Living area per German Wohnflächenverordnung (WoFlV §4): apartment floor area at 100 %, balconies at 25 %. No ceiling-height reductions applied.",
		floorAreaRatio: "Floor Area Ratio (FAR)",
		floorAreaRatioDescription: "Ratio of gross floor area to site area",
		areaToVolumeRatio: "Area-to-Volume Ratio (AVR)",
		areaToVolumeRatioDescription:
			"Ratio of building envelope area to building volume",
		unitMixDescription: "Apartment count by bedrooms and inhabitants",
		layoutEfficiencyPercent: "Layout Efficiency (%)",
		layoutEfficiencyTooltip:
			"Layout Efficiency: Ratio of net usable area to gross floor area.\n\nNet Internal Area (NIA): Total usable floor area (excludes walls, corridors, shafts)\n\nGross Floor Area (GFA): Total floor area of all floors (includes walls, corridors, shafts)",
		buildingFootprintSqm: "Building Footprint (m²)",
		buildingFootprintTooltip: "Total area covered by buildings at ground level",
		grossFloorAreaSqm: "Gross Floor Area (m²)",
		grossFloorAreaTooltip:
			"Total floor area of all floors (includes walls, corridors, shafts)",
		areaToVolumeRatioTooltip:
			"Ratio of building envelope area to building volume",
		variantCount: "Variant count",
		providedRequiredParking: "Parking (provided / required)",
		providedRequiredParkingDescription:
			"Total number of parking spots provided versus the number required by the parking factors and multiplier.",
		parking: "Parking",
		parkingDescription:
			"Parking spots and the ground they cover, split by street parking and car parks.",
		streetParking: "Street parking",
		carParks: "Car parks",
		spotsAvailable: "Spots available",
		total: "Total",
		streetParkingAreaDescription: "Ground area covered by street parking",
		streetParkingSpotsDescription: "Number of street parking spots",
		carParkAreaDescription: "Ground area covered by car park regions",
		carParkSpotsDescription: "Number of parking spots inside car park regions",
		totalParkingAreaDescription:
			"Ground area covered by street parking and car parks",
		pavedSurfaces: "Paved Surfaces",
		pavedSurfacesDescription:
			"Ground area sealed by paths, parking and paved regions.",
		paths: "Paths",
		pathsAreaDescription: "Ground area covered by paths",
		pavedRegions: "Paved regions",
		pavedRegionAreaDescription: "Ground area covered by paved area regions",
		totalPavedAreaDescription:
			"Combined area of paths, parking and paved regions",
	},
	paragraph34Panel: {
		far: "FAR",
		gsi: "GSI",
		height: "Height",
		noData: "No data. Ensure a site and parcels are loaded.",
		uploadedParcelsOnly:
			"§34 needs the surrounding cadastral parcels. Uploaded parcels only cover the plot, so they are not used.",
		loadingBuildings: "Loading buildings…",
		withinLimits: "within limits",
		parcels: "Parcels",
		parcel: "Parcel",
		referenceLabel: "Reference",
		selectParcelHint: "Click a parcel to use it as the reference.",
		pickOnMap: "Pick on map",
		pickOnMapCancel: "Cancel",
		pickOnMapHint:
			"Click any parcel in the scene to use it as the reference. Hover to preview its values.",
		manualReferenceInfo:
			"is the reference — its FAR, GSI and height set the §34 limits and suggested targets.",
		resetReference: "Reset to auto",
		legendTitle: "Color legend",
		legendInRange: "At or below ref — within §34 limits",
		legendAboveMax: "Above ref — exceeds reference parcel",
		infoTitle: "How §34 targets are computed",
		infoNeighbours:
			"Neighbours: cadastral parcels whose outline is within 200 m of your site's parcel outline define the context.",
		infoFilter:
			"Filtering: empty parcels (no buildings) and statistical outliers on FAR and height are dropped.",
		infoReference:
			"Reference parcel: among the remaining neighbours, the one with the highest FAR. Its FAR, GSI and height set the Ref (ceiling).",
		infoCompliance:
			"Your project is within §34 limits when each metric stays at or below Ref.",
	},
	report: {
		title: "Project Report",
		noBuildings: "No buildings in this layout",
		noBuildingsDescription:
			"A report can't be created without units. Generate a building first.",
		generating: "Generating report...",
		reportDate: "Report date",
		developmentSummary: "Development summary",
		basis: "Basis",
		basisNote:
			"Figures are generated from the active layout and the valuation assumptions set in the project.",
		siteAndDesignMetrics: "Site and Design Metrics",
		siteAndDesignMetricsSubtitle:
			"What the site allows, what the design delivers, and how efficiently the built area is used.",
		costAndRevenueModel: "Cost and revenue model",
		costAndRevenueModelSubtitle:
			"Construction cost build-up, project costs, financing, and the resulting return.",
		feasibilityReport: "Feasibility report",
		feasibilityStudy: "Feasibility study",
		coverSubtitle:
			"Potential analysis for the development of high-quality residential space in an urban setting.",
		massingTopView: "Site massing — top view",
		estimate: "Estimate",
		range: "Range",
		perSqmLivingArea: "Per m² living area",
		monthly: "monthly",
		subtotalPreFinance: "Subtotal before financing",
		priceSourceMarket: "market estimate",
		priceSourceManual: "manual",
		page: "Page",
		printReport: "Print report",
		units: "Units",
		share: "Portion",
		region: "Region",
		area: "Area",
		ofSite: "Of site",
		shareOfDesignatedArea: "Share of Designated Area",
		item: "Cost group / item",
		rate: "Rate / Unit",
		amount: "Total",
		unitApartment: "unit",
		unitLevel: "level",
		unitYears: "y",
		unitFootprint: "m² FP",
		disclaimer: "Disclaimer & Reliance",
		disclaimerBasisTitle: "Data and methodology basis",
		disclaimerBasis1:
			"This feasibility study was prepared to the best of our knowledge and belief, and with due diligence. All analyses, assessments, key figures, forecasts, and conclusions are based on the information available at the time of preparation, in particular on documents and information provided by the client, publicly accessible sources, and – where applicable – on data-driven evaluations using software solutions and artificial intelligence (AI) methods.",
		disclaimerBasis2:
			"A comprehensive, independent review of all underlying assumptions, data sources, and other factors, in the sense of a technical, economic, tax, or legal due diligence, was neither required nor conducted. Only a careful plausibility check was performed within the framework of a preliminary, indicative analysis.",
		disclaimerWarrantyTitle: "Warranty disclaimer",
		disclaimerWarranty1:
			"Despite careful consideration, no guarantee can be given for the completeness, accuracy, and timeliness of the data used, the underlying assumptions, and the results derived therefrom. This applies in particular given that (i) parts of the content are based on probabilistic and algorithmic methods, including AI-supported models, which are subject to inherent uncertainties and sources of error, and (ii) framework conditions (e.g., market, cost, interest rate, legal, or funding environments) can change at any time.",
		disclaimerWarranty2:
			"The statements contained in this feasibility study – in particular regarding costs, deadlines, revenues, profitability, risks or feasibility – therefore represent non-binding assessments at the time of preparation and are expressly not to be understood as a promise, guarantee or agreement as to quality.",
		disclaimerUseTitle: "Intended Use & Liability",
		disclaimerUse1:
			"The feasibility study serves solely as a guide and preparation for further considerations by the client. It does not replace in-depth technical, legal, tax, or economic expert advice, nor does it replace detailed project planning. The client remains obligated to make independent decisions based on their own review and, where necessary, supplementary expert opinions and consulting services. Any direct or indirect use of the results for investment, financing, procurement, or acquisition decisions is at the client's own risk.",
		disclaimerUse2:
			'Any liability of the contractor for the accuracy, completeness, and timeliness of the feasibility study, its underlying assumptions, and for decisions or omissions made therefrom by the client or third parties is excluded to the extent permitted by law. In particular, claims arising from so-called "reliance" (dependence on the content of this study), including any indirect or consequential damages, are excluded. The foregoing limitations of liability apply equally to all contractual, pre-contractual, and extra-contractual claims and also extend to the benefit of the contractor\'s legal representatives, employees, and other agents.',
	},
	statusMessages: {
		siteSaved: "Site saved",
		siteNotSaved: "Site not saved",
		buildingSaved: "Building saved",
		buildingNotSaved: "Building not saved",
		saving: "Saving...",
		savedToCloud: "Saved to cloud",
	},
	buildingInfo: {
		residential: "Residential",
		units: "units",
	},
	validation: {
		buildingExceedsPlot: "Building exceeds plot",
		buildingSetbacksOverlap: "Building setbacks overlap",
		buildingSetbackExceedsSiteLimit:
			"Building setback exceeds the site setback limit",
		apartmentFacesPureNorth: "Apartments face pure north",
		siteExceedsMaxGsi: "Coverage exceeds Max GSI",
		siteExceedsMaxAncillaryGsi:
			"Coverage incl. sealed surfaces exceeds Max GSI II",
		coreExceedsMaxApartments: "Core serves too many apartments",
		apartmentModifiedToFillGap: "Apartment modified to fill gap",
		apartmentModifiedToFillGapDetails: (rooms: number, livingArea: string) =>
			`${rooms} rooms, ${livingArea} m² living area`,
	},
	warningsPanel: {
		noWarnings: "No warnings",
		archived: "Archived",
		archive: "Archive",
		restore: "Restore",
		showDetails: "Show details",
		hideDetails: "Hide details",
		moreDetails: (count: number) => `+${count} more`,
		collapse: "Collapse",
		expand: "Expand",
		archiveAll: "Archive all",
		groups: {
			building: "Building",
			gsi: "GSI",
			unitOrientation: "Unit orientation",
			unitModified: "Unit modified",
			core: "Core",
		},
		warningTypes: "Warning types",
		warningsWhileEditing: "Warnings while editing",
	},
	userMenu: {
		name: "Name",
		cursorColor: "Cursor colour",
		cursorColorInfo: "Choose the colour others see for your cursor.",
		language: "Language",
		theme: "Theme",
		themeSystem: "System",
		themeLight: "Light",
		themeDark: "Dark",
		organisation: "Organisation",
		manageAccount: "Manage account",
		createOrganisation: "Create organisation",
		organisationSettings: "Organisation settings",
		appAdmin: "App admin",
		contactUs: "Contact us",
		signOut: "Sign out",
		signIn: "Sign in",
		legal: "Legal",
		privacyPolicy: "Privacy Policy",
		cookiePolicy: "Cookie Policy",
		impressum: "Impressum",
		terms: "Terms and conditions",
	},
	locationPicker: {
		selectLand: "Select a location on land",
		cannotVerifyLocation:
			"Could not verify location. Select it again to retry.",
		useThisLocation: "Use this location",
	},
	exportPanel: {
		generate: "Generate",
		exportType: "Export type",
		generateRevit: "Generate Revit file",
		generateIfc: "Generate IFC file",
		revitJobLabel: "Revit export",
		ifcJobLabel: "IFC export",
		generating: "Generating...",
		[ExportStatus.pending]: "Pending",
		[ExportStatus.inprogress]: "In progress",
		[ExportStatus.success]: "Complete",
		failed: "Failed",
		download: "Download file",
		delete: "Delete",
		startNewJob: "Start new job",
		rawData: "Raw Data",
		export: "Export",
		revitFiles: "Revit files",
		uploadRevitFile: "Upload Revit file",
		uploading: "Uploading...",
		revitFileTooLarge: "File exceeds the size limit",
		revitFileExists:
			"A file with this name already exists — delete it first or rename the file",
		revitFileHint: "Revit 2025 reference project",
		referenceFile: "Reference file",
		defaultReferenceFile: "Default (built-in)",
		notMapped: "(not mapped)",
		noBuildings: "Add at least one building to export",
		extracting: "Extracting...",
		extractionFailed: "Extraction failed",
		mapFamilies: "Map families",
		downloadIfc: "Download IFC",
		mappingTitle: "Map families",
		mappingDescription:
			"Choose which family to use for each element type when exporting",
		mappingNone: "—",
		mappingRequired: "Map the families for this file before exporting",
		mappingOutOfSync:
			"BIM Object properties changed — update the family mapping first",
		mappingSearchPlaceholder: "Search families...",
		mappingNoResults: "No families found",
		mappingFixedSize: "(fixed size)",
		mappingCategoryLabels: mappingCategoryLabelsEn,
		mappingSlotLabels: mappingSlotLabelsEn,
		mappingLoading: "Loading families...",
		mappingLoadFailed: "Could not load the extracted families",
		mappingSaveFailed: "Could not save the mapping",
		mappingSave: "Save",
		mappingSaving: "Saving...",
		mappingCancel: "Cancel",
		applySizesTitle: "Update BIM Object properties?",
		applySizesDescription:
			"The mapped families have different sizes than the current generation settings. Apply them to this layout and regenerate?",
		applySizesApply: "Apply & regenerate",
		applySizesKeep: "Keep current values",
		revitFileUploadFailed:
			"Uploading the file to storage failed — please try again",
	},
	org: {
		apartmentSets: "Apartment Sets",
		globalApartmentSet: "Built-in",
		globalApartmentSetDescription:
			"Provided by gen.os.build and copied into every new project. Add your own apartment sets to customise.",
		defaultApartmentSet: "New projects default",
		defaultApartmentSetDescription:
			"New projects start with this apartment set selected.",
		setDefaultApartmentSet: "Set as new projects default",
		emptyDefaultApartmentSet:
			"Upload at least one apartment template before making this set the default.",
		createApartmentSet: "New Apartment Set",
		defaultCorridorWidth: "Default corridor width",
		createApartmentGroup: "New Apartment Group",
		createApartmentTemplate: "New Apartment Template",
		bulkUploadApartmentTemplates: "Bulk upload",
		bulkUploadApartmentTemplatesDescription:
			"Upload several apartment template JSON files at once. Each one is added to the group named in the file, creating that group when it doesn't exist yet.",
		bulkUploadReadyCount: (count: number) =>
			`${count} apartment template${count === 1 ? "" : "s"} ready to upload`,
		bulkUploadSkippedCount: (count: number) =>
			`${count} file${count === 1 ? "" : "s"} skipped — not a valid apartment template`,
		bulkUploadProgress: (uploaded: number, total: number) =>
			`Uploaded ${uploaded} of ${total}`,
		bulkUploadFailedCount: (count: number) =>
			`${count} upload${count === 1 ? "" : "s"} failed`,
		upload: "Upload",
		close: "Close",
		name: "Name",
		color: "Color",
		shuffleColor: "Shuffle color",
		create: "Create",
		cancel: "Cancel",
		remove: "Remove",
		update: "Update",
		apartmentGroup: "Apartment group",
		newApartmentGroup: "New group…",
		addApartmentTemplate: "Add apartment template",
		chooseApartmentGroup: "Choose which group this apartment belongs to.",
		add: "Add",
		removeApartmentSet: "Remove apartment set",
		removeApartmentSetDescription:
			"This apartment set and all of its groups and templates will be permanently removed.",
		removeApartmentTemplate: "Remove apartment template",
		removeApartmentTemplateDescription:
			"This apartment template will be permanently removed.",
	},
	appAdmin: {
		defaultApartmentSets: "Default Apartment Sets",
		autodeskExports: "Autodesk Exports",
		disabled: "Disabled",
		cancel: "Cancel",
		typeToConfirm: { before: "Type ", after: " to confirm" },
		blockersChecking: "Checking what still has to end…",
		blockersFailed: "Couldn't check what still has to end.",
		blockersIntro: "It can't be disabled yet:",
		openInStripe: "Open in Stripe",
		disableBlockers: {
			subscription: (id: string, status: string, appEnv?: string) =>
				`Subscription ${id} (${status}${appEnv ? `, ${appEnv}` : ""}) hasn't ended. One set to cancel later still counts until it ends.`,
			schedule: (id: string, appEnv?: string) =>
				`Subscription schedule ${id}${appEnv ? ` (${appEnv})` : ""} hasn't finished.`,
			checkout: (id: string, appEnv?: string) =>
				`Checkout ${id}${appEnv ? ` (${appEnv})` : ""} is still open.`,
			unknownCustomer: (id: string) =>
				`Stripe customer ${id} isn't in this Stripe account, so its billing can't be checked.`,
			otherOwnerCustomer: (id: string) =>
				`Stripe customer ${id} names someone else in its metadata.`,
			seatOperation: (id: string) =>
				`A member or seat change (${id}) is in progress or unsettled.`,
			seatReservation: "A seat is waiting for its invoice to be paid.",
			scheduleSync: "A scheduled change is waiting to be synced to Stripe.",
			self: "You can't disable yourself.",
			appAdmin: "App admins can't be disabled.",
			lastOrgAdmin: (org: string) =>
				`They're the only admin or member of ${org} who isn't disabled.`,
		},
		apsNoTransactions: "No Design Automation runs recorded yet",
		apsStarted: "Started",
		apsOrganisation: "Organisation",
		apsUser: "User",
		apsProject: "Project",
		apsAction: "Action",
		apsReferenceFile: "Reference file",
		apsStatus: "Status",
		apsDuration: "Duration",
		apsProcessing: "Engine time",
		apsCredits: "Cloud credits",
		apsCost: "Est. cost",
		apsFailureReason: "Reason",
		apsReport: "Report & world json",
		apsFiles: "Files",
		apsResultFile: "Exported file",
		apsNotAvailable: "not available",
		apsOwnFile: "Own upload",
		apsDefaultFile: "Built-in default",
		apsKinds: {
			exportRevit: "Export Revit",
			exportIfc: "Export IFC",
			familyExtraction: "Family extraction",
		} as Record<string, string>,
		apsStatuses: {
			pending: "Pending",
			inprogress: "Running",
			success: "Succeeded",
			failed: "Failed",
			failedToStart: "Failed to start",
		} as Record<string, string>,
		apsStatusFilters: {
			__all__: "All runs",
			pending: "Pending",
			inprogress: "Running",
			success: "Succeeded",
			failed: "Failed",
			failedToStart: "Failed to start (quota / rejected)",
		} as Record<string, string>,
	},
	orgMembers: {
		title: "Members",
		inProgress:
			"A member or seat change is still being confirmed. Other changes wait until it's done; reload in a moment.",
		needsSupport:
			"An earlier member or seat change couldn't be confirmed, so member and seat changes are paused. Contact support to resolve it.",
		seatUsage: (tier: string, used: number, paid: number) =>
			`${tier}: ${used} of ${paid} used`,
		standard: "Standard",
		premium: "Premium",
		admin: "Admin",
		member: "Editor",
		finance: "Finance",
		all: "All",
		active: "Active",
		pending: "Pending",
		filter: "Filter members",
		memberColumn: "Member",
		roleColumn: "Role",
		seatColumn: "Seat",
		noSeat: "No seat",
		noPending: "No pending invitations.",
		cancelInvite: "Cancel invite",
		roleFor: (name: string) => `Role for ${name}`,
		seatFor: (name: string) => `Seat for ${name}`,
		chooseSeat: "Choose seat",
		chooseSeatForRole:
			"Their usual seat type is full. Choose a seat to change the role.",
		trialSeatUntil: (trialTier: string, date: string) =>
			`${trialTier} until the trial ends on ${date}`,
		remove: "Remove",
		lastAdmin:
			"An organisation needs at least one admin. Make someone else admin first.",
		removeTitle: (name: string) => `Remove ${name}?`,
		removeDescription: (name: string, org: string) =>
			`${name} loses access to ${org}. Their other organisations aren't affected.`,
		cancel: "Cancel",
		invite: "Invite",
		inviteTitle: (org: string) => `Invite to ${org}`,
		inviteDescription:
			"Admins and editors use a seat of one of the plan's seat types; finance members don't. Invitations reserve the seat, and cancelling one frees it.",
		email: "Email",
		emailInvalid: "Enter a valid email address.",
		emailTaken:
			"This email already belongs to a member, invitation, or invitation awaiting payment.",
		emailPlaceholder: "name@acme.example",
		adminsOnly: "Only admins manage members.",
		role: "Role",
		seat: "Seat",
		invitationRole: "Invitation role",
		invitationSeat: "Invitation seat",
		sendInvite: "Send invite",
		addSeatInvoiced: (tier: string, dueNow: string) =>
			`Every ${tier} seat is used or reserved. This adds 1 extra ${tier} seat (${dueNow} due now, prorated) and sends the invite once it's paid.`,
		addSeatInvoicedPrice: (tier: string, price: string, dueNow: string) =>
			`Every ${tier} seat is used or reserved. This adds 1 extra ${tier} seat (${price}; ${dueNow} due now, prorated) and sends the invite once it's paid.`,
		tierIncludesFeature: (tier: string, plan: string, feature: string) =>
			`${tier} seats on ${plan} include ${feature}.`,
		tierExcludesFeature: (tier: string, plan: string, feature: string) =>
			`${tier} seats on ${plan} don't include ${feature}.`,
		addSeatTrial: (tier: string) =>
			`Every ${tier} seat is used or reserved. This adds 1 to the trial right away and sends the invite.`,
		seatChangeWaiting: (tier: string) =>
			`Every ${tier} seat is used or reserved, and a seat change or seat offer is already waiting. Settle it on Billing first.`,
		seatAddPaused: (tier: string) =>
			`Every ${tier} seat is used or reserved, and adding seats is paused until the issue shown on Billing is resolved. Resolve it there or contact gen.os.build support, or invite to a tier with a free seat.`,
		addSeatAndInvite: "Add 1 seat & invite",
		reviewOnBilling: "Review on Billing",
		choosePlan: "Choose a plan",
		inactive:
			"The organisation has no active plan. Choose one on Billing before adding seats; finance members can still be invited.",
		loadFailed: "Failed to load members.",
		saveFailed: "Failed to update members.",
		retry: "Retry",
		loading: "Loading members…",
	},
	orgSettings: {
		organisation: "Organisation",
		general: "General",
		billing: "Billing",
		people: "People",
		members: "Members",
		name: "Name",
		save: "Save",
		nameRequired: "Enter a name for the organisation.",
		renameFailed: "Failed to rename the organisation.",
	},
	billingStatuses: {
		trialing: "Trial",
		active: "Active",
		incomplete: "Incomplete",
		incomplete_expired: "Expired",
		past_due: "Payment overdue",
		canceled: "Cancelled",
		unpaid: "Unpaid",
		paused: "Paused",
	},
	adminCoupons: {
		combines: "Combines",
		combinesLabel: (code: string) => `${code} combines with other discounts`,
		combinesWithDiscounts: "Can be combined with other discounts",
		combinesDescription:
			"Off by default. When off, the code can't be used together with a seat discount: accepting a seat discount asks before ending the code. Changing it later only affects future combinations.",
		combinesFailed: "The coupon couldn't be updated.",
		dashboard: "Stripe Dashboard",
		dashboardNotCombinable: "Not set – treated as not combinable",
		yes: "Yes",
		coupons: "Coupons",
		back: "App admin",
		loading: "Loading coupons…",
		loadFailed: "Failed to load coupons.",
		retry: "Retry",
		plansLoadFailed:
			"Plans couldn't be loaded, so coupons show their plan's Stripe ID, and new coupons can't be created until the plans load.",
		none: "No coupons yet.",
		code: "Code",
		discount: "Discount",
		plan: "Plan",
		expires: "Expires",
		redemptions: "Redemptions",
		status: "Status",
		never: "Never",
		statuses: {
			active: "Active",
			inactive: "Disabled",
			usedUp: "Used up",
			expired: "Expired",
		},
		allPlans: "All plans",
		deletedPlan: "Deleted plan",
		redemptionsUnlimited: (redemptions: number) =>
			`${redemptions} of unlimited`,
		redemptionsLimited: (redemptions: number, max: number, left: number) =>
			`${redemptions} of ${max} · ${left} left`,
		deactivate: "Disable",
		deactivateTitle: (code: string) => `Disable ${code}?`,
		deactivateDescription:
			"Organisations already using it keep their discount. It can't be used for new checkouts until it's enabled again.",
		deactivateFailed: "Failed to disable the coupon.",
		enable: "Enable",
		enableTitle: (code: string) => `Enable ${code}?`,
		enableDescription:
			"It can be used for new checkouts again. Stripe refuses this once its coupon has expired or is used up, or when another active code has the same name.",
		enableFailed: "Failed to enable the coupon.",
		cancel: "Cancel",
		newCoupon: "New coupon",
		newCouponDescription:
			"Creates a promotion code in Stripe. Organisations enter it at checkout.",
		codeInvalid: "Use letters and digits, optionally split by hyphens.",
		codeTakenInStripe:
			"This code is already an active promotion code in Stripe, possibly from the Stripe Dashboard. Choose another code.",
		prefix: "Prefix for generated code",
		prefixPlaceholder: "Prefix",
		generate: "Generate single-use code",
		discountType: "Discount type",
		percent: "Percent",
		fixedAmount: "Fixed amount",
		percentOff: "Percent off",
		euroOff: "Euro off each invoice",
		percentInvalid: "Enter a whole percentage from 1 to 100.",
		amountInvalid:
			"Enter an amount of at least €0.01, with at most two decimals.",
		expiresOptional: "Expires (optional)",
		expiresDescription: "Last day it can be redeemed.",
		maxRedemptions: "Max redemptions (optional)",
		unlimited: "Unlimited",
		maxRedemptionsDescription: "Total uses across all organisations.",
		maxRedemptionsInvalid:
			"Enter a whole number of at least 1, or leave it empty for no limit.",
		duration: "Duration",
		durations: {
			"1": "Once",
			"3": "3 months",
			"12": "12 months",
			forever: "Forever",
		},
		durationDescription:
			"Stripe discounts the invoices created within this time after redeeming; on a yearly plan, that's usually just the first invoice.",
		create: "Create coupon",
		createFailed: "Failed to create the coupon.",
	},
	serverNotices: {
		seatChangesPaused:
			"Stripe didn't confirm a change yet. Member and seat changes stay paused until it's confirmed; check Billing in a few minutes.",
		seatChangeChecking:
			"The seat change couldn't be confirmed and is still being checked. Look at Billing before trying again.",
		seatChangeNotStarted:
			"The seat change couldn't be started and nothing was charged. Try again.",
		seatRevertFailed:
			"The seat was changed but the role wasn't, and the seat couldn't be changed back. Member changes stay paused until support resolves it.",
		planChangeUnconfirmed:
			"This plan change couldn't be confirmed with Stripe. Resume it on the plans page; nothing else can change the plan until then.",
	},
	adminPlans: {
		plans: "Plans",
		outsideCatalog: "Outside catalog",
		outsideCatalogHelp:
			"Stripe prices with subscriptions that haven't ended whose products aren't app plans, such as the earlier personal subscription. The app can't change or cancel them; manage them in the Stripe Dashboard. Environments sharing this Stripe account list the same ones.",
		outsideCatalogLoading: "Loading prices outside the catalog…",
		outsideCatalogLoadFailed: "Couldn't load the prices outside the catalog.",
		outsideCatalogNone:
			"Every subscription that hasn't ended is on an app plan.",
		amount: "Amount",
		subscriptions: "Subscriptions",
		statuses: "Statuses",
		issues: "Issues",
		customAmount: "Tiered or custom",
		amountPerInterval: (amount: string, interval: string, count: number) =>
			count === 1
				? `${amount} / ${interval}`
				: `${amount} / ${count} ${interval}s`,
		notAppPlan: "Not an app plan",
		archivedInStripe: "Archived in Stripe · subscriptions remain",
		personal: (count: number) => `Personal (${count})`,
		unknownEnvironment: "Environment unknown",
		unknownEnvironmentCount: (count: number) =>
			`Environment unknown (${count})`,
		showSubscriptions: "Show subscriptions",
		hideSubscriptions: "Hide subscriptions",
		stripePrice: "Price in Stripe",
		stripeProduct: "Product in Stripe",
		customer: "Customer",
		invalidPlan: (planId: string, reason: string) =>
			`Plan ${planId} isn't listed or offered to customers, as it's invalid in Stripe: ${reason}. Fix it in the Stripe Dashboard.`,
		newPlan: "New plan",
		showArchived: "Show disabled plans",
		plan: "Plan",
		visibility: "Visibility",
		enrollment: "Enrollment",
		currentPrice: "Current price",
		organisations: "Organisations",
		edit: "Edit",
		archived: "Disabled",
		public: "Public",
		private: "Private",
		open: "Open",
		closed: "Closed",
		aboutVisibility: "About visibility",
		visibilityHelp:
			"Public: customers can choose it. Private: only App Admins can assign it.",
		aboutEnrollment: "About enrollment",
		enrollmentHelp:
			"Allow new subscriptions. Existing subscriptions renew as usual.",
		name: "Name",
		nameRequired: "Enter a plan name.",
		allowTrials: "Allow trials",
		trialDays: "Trial days",
		trialDaysHelp: "Self-serve trials and the default for App Admin trials.",
		trialSeat: "Trial seat",
		chooseTrialSeat: "Choose a seat type",
		trialSeatHelp:
			"When an organisation's only member starts a trial, their seat moves to this seat type until the trial ends.",
		trialSeatInvalid: "Choose one of this plan's seat types.",
		priceInvalid:
			"Enter every price in euros with at most 2 decimals, every included seat count as a whole number, and a different name of at most 40 characters for each seat type. Empty fields aren't allowed.",
		seatTypes: "Seat types",
		seatTypesNote:
			"Names, included seats and extra seat prices are part of the price.",
		seatTypeName: "Seat type name",
		removeSeatType: (name: string) => `Remove ${name} seats`,
		extraSeatPrice: "Extra seat price",
		addSeatType: "Add seat type",
		couponsFailed: "Couldn't load the coupons restricted to this plan.",
		newSeatTypeCoupons: (plan: string, codes: string) =>
			`Promotion codes restricted to ${plan} (${codes}) won't discount the new seat types' seats, as Stripe can't change which products a coupon applies to. Create new codes if they should.`,
		features: "Features",
		featureA: "Placeholder Feature A",
		featureChangeNote: (count: number, name: string) =>
			`Feature changes apply immediately to ${count === 1 ? "the 1 organisation" : `all ${count} organisations`} on ${name}, whatever price ${count === 1 ? "it's" : "they're"} on.`,
		price: "Price",
		versionNote: (version: number) =>
			`A price change saves as version ${version} for new subscriptions.`,
		billing: "Billing",
		billingIntervals: "Billing intervals",
		monthly: "Monthly",
		yearly: "Yearly",
		month: "month",
		year: "year",
		intervalsHelp:
			"Organisations already billed on an interval you remove keep their price and interval.",
		includedInBase: "Included in base",
		baseFee: "Base fee",
		baseFeeFor: (interval: string) => `Base fee ${interval}`,
		includedFor: (tier: string) => `${tier} seats included`,
		extraSeatFor: (tier: string, interval: string) =>
			`Extra ${tier} seat ${interval}`,
		example: (seats: string, interval: string) =>
			`Example: ${seats} beyond those included, ${interval}`,
		perInterval: (amount: string, interval: string) =>
			`${amount} / ${interval}`,
		or: " or ",
		priceSummary: (amounts: string, seats: string) =>
			`${amounts} · includes ${seats}`,
		seatCounts: (parts: SeatPart[]) =>
			formatSeatParts({ parts, joiner: " and " }),
		priceHistory: "Price history",
		sharedCatalogNote:
			"Every environment using this Stripe account shares these plans, their organisations' subscriptions and coupons: changes apply in all of them, and saving or disabling a plan expires its open checkouts in all of them.",
		current: "Current",
		orgCount: (count: number) =>
			`${count} ${count === 1 ? "organisation" : "organisations"}`,
		archivePlan: "Disable plan",
		archiveTitle: (name: string) => `Disable ${name}?`,
		archiveDescription: (count: number) =>
			`It closes enrollment, leaves the catalog and its open checkouts expire. ${count === 1 ? "The 1 organisation on it keeps its" : `The ${count} organisations on it keep their`} plan, price and features, and their subscriptions keep renewing and being billed. You can enable it again.`,
		sharedArchiveNote:
			" Every environment using this Stripe account shares this plan: it leaves their catalogs too and their open checkouts for it expire.",
		archive: "Disable",
		restorePlan: "Enable plan",
		archivedMoveNote:
			"Organisations can't be moved to another version of a disabled plan. Enable it first to move them.",
		restoreTitle: (name: string) => `Enable ${name}?`,
		restoreDescription: "It returns to the catalog with enrollment closed.",
		restore: "Enable",
		cancel: "Cancel",
		save: "Save",
		create: "Create plan",
		confirmTitle: (name: string) => `Save changes to ${name}?`,
		confirmDescription: (count: number) =>
			`${count} ${count === 1 ? "organisation is" : "organisations are"} on this plan.`,
		newVersion: (version: number) => `New price version ${version}`,
		newVersionNote:
			"Existing subscriptions keep their price unless moved. After saving, you choose which organisations to move: paying ones get a scheduled change, trials switch now, and none is asked to accept.",
		newVersionArchivedNote:
			"Existing subscriptions keep their price. Organisations can't be moved to it while the plan is disabled; enable the plan first to move them.",
		moveTitle: (plan: string, version: number) =>
			`Move organisations to ${plan} v${version}?`,
		moveDescription:
			"Moved organisations keep their billing interval and paid seats and aren't asked to accept. Paying ones get a scheduled change; trials switch now, as nothing is billed during a trial.",
		moveAll: (count: number) => `Move all ${count}`,
		moveOrg: (org: string, version: number) => `Move ${org} (on v${version})`,
		keepsVersion: (interval: string, newVersion: number, version: number) =>
			`bills ${interval.toLowerCase()}, which v${newVersion} doesn't offer, so it keeps v${version}`,
		trialMovesNow: "trial: moves now, and upgrading pays the new price",
		movePendingUpgrade:
			"its upgrade from the trial waits for payment and would start at its old price, so it can't be moved until the upgrade invoice is paid or expires",
		movePreview: (before: string, after: string, date: string) =>
			`next invoice ${before} → ${after} (Stripe, ${date})`,
		moveLostCode: (code: string) =>
			`promotion code ${code} stops discounting once the move applies`,
		atEachRenewal: "At each organisation's next renewal",
		moveDate: "Effective date",
		moveDateDescription:
			"Must be before each organisation's renewal; one that renews earlier shows an error below, and Move stays disabled until you untick it.",
		prorate: "Prorate the rest of the period",
		moveError: (org: string, error: string) => `${org}: ${error}`,
		skipMove: "Don't move",
		moveOrgs: "Move organisations",
		moveConsent:
			"I confirm the amounts Stripe invoices each moved paying organisation, as shown for each",
		moveScheduled: (version: number, date: string) =>
			`already scheduled to move to v${version} on ${date}`,
		moveReplacesScheduled: (date: string) =>
			`moving replaces the change scheduled for ${date}`,
		move: "Move",
		moveLoadFailed: "Failed to load the organisations.",
		movePreviewFailed: "Failed to preview a move.",
		moveFailed: "Failed to move the organisations.",
		unfinishedTitle: (plan: string) => `A change to ${plan} didn't finish`,
		operationPreparing:
			"It's still being prepared. Reload in a minute; nothing was sent to Stripe yet.",
		operationAbandoned:
			"It stopped while being prepared and nothing was sent to Stripe. Discard it to change the plan again.",
		operationUnconfirmed:
			"Stripe didn't confirm one of its steps, so the plan can't change until it's resumed. Resuming sends the same steps again, and none is applied twice: each sets a product, price or promotion code to a target state, or creates a price Stripe allows only once. After a day, each step is first checked in Stripe and skipped if its effect is there.",
		operationRejected: (error: string) =>
			`Stripe rejected one of its steps (${error}), so it stopped part-way. The steps before it stay applied. Retry it if the cause was temporary, or discard it, check the plan and save again.`,
		operationCompensating:
			"A subscription or scheduled change started on this plan while it was being deleted, so the plan stays disabled, but some of its prices are still inactive. Resume it to reactivate them; nothing is deactivated again.",
		hiddenPlan: (planId: string) => `plan ${planId} (not in the plan list)`,
		resume: "Resume",
		retryChange: "Retry",
		discard: "Discard",
		deletionRetryOnly:
			"A deletion started before plans could only be disabled stopped part-way. It can't be discarded, as it would leave the plan disabled with inactive prices. Retry it to finish the deletion.",
		discardBlocked: (count: number) =>
			`${count === 1 ? "A Stripe request" : `${count} Stripe requests`} of this change never got a final response and may still apply, so it can't be discarded. Resume or Retry gets Stripe's answer, or after a day checks the request's effect in Stripe and sends it again if it's missing; then it can be discarded.`,
		operationFailed: "Failed to finish the plan change.",
		featuresNotice: (count: number) =>
			`Feature changes apply immediately to ${count === 1 ? "the 1 organisation" : `all ${count} organisations`}, whatever price ${count === 1 ? "it's" : "they're"} on.`,
		loading: "Loading plans…",
		loadFailed: "Failed to load plans.",
		saveFailed: "Failed to save the plan.",
		saving: "Saving…",
		retry: "Retry",
		noPlans: "No plans yet.",
	},
	seatOffers: {
		conflictingCodes: (codes: string) =>
			`${codes} doesn't combine with other discounts but applies together with this seat discount, probably added in the Stripe Dashboard. It isn't removed automatically; check it in Stripe.`,
		codeCombines: (code: string) =>
			`Discount ${code} combines with other discounts, so it also applies to these seats.`,
		codeEnds: (code: string) =>
			`Discount ${code} can't be combined with a seat discount, so accepting ends it in the same change: the amounts above are without it. If the invoice isn't paid, it stays.`,
		endCodesConsent: (codes: string) =>
			`End discount ${codes} to accept this seat discount`,
		changeEnds: (discount: string, tier: string) =>
			`This ends the seat discount (${discount}): no extra ${tier} seats are left for it.`,
		changeDrops: (before: number, after: number, tier: string) =>
			`The seat discount drops from ${before} to ${after} ${tier} seats, as only ${after} extra are left, and doesn't come back. `,
		changeEndsEarly: (
			endsAt: string,
			interval: string,
			firstFullPriceAt: string,
		) =>
			`This ends the seat discount early: it was promised until ${endsAt}, but the ${interval} invoice on ${firstFullPriceAt} runs past that date, so it's at the normal seat price.`,
		changeKeepsEnd: (endsAt: string, firstFullPriceAt: string) =>
			`The seat discount keeps its end date, ${endsAt}; the first invoice at the normal seat price is on ${firstFullPriceAt}.`,
		monthlyInvoice: "monthly",
		yearlyInvoice: "yearly",
		discountEndsEarly: (
			interval: string,
			endsAt: string,
			firstFullPriceAt: string,
		) =>
			`This ends the discount early: ${interval} invoices don't line up with ${endsAt}, so the invoice on ${firstFullPriceAt}, which runs past it, is at the normal price.`,
		seatsFree: (seats: number, tier: string) =>
			`${seats} ${tier} ${seats === 1 ? "seat" : "seats"} free`,
		seatsOff: (seats: number, tier: string, percent: number) =>
			`${seats} ${tier} ${seats === 1 ? "seat" : "seats"} ${percent}% off`,
		seatsNormal: (seats: number, tier: string) =>
			`${seats} ${tier} ${seats === 1 ? "seat" : "seats"} at the normal price`,
		until: (offer: string, date: string) => `${offer} until ${date}`,
		seats: "Seats",
		addSeats: "Add seats",
		addSeatsTitle: (org: string) => `Add seats for ${org}`,
		addSeatsDescription:
			"The amounts are quoted by Stripe, with any discounts that apply.",
		mode: "How to add them",
		modeOffer: "Create seat offer",
		modeApply: "Apply now",
		modeOfferHelp: (org: string) =>
			`Nothing is added or charged until ${org} accepts on Billing. They aren't notified: share the billing link with them.`,
		modeApplyHelp:
			"Adds the seats now, without the organisation accepting in the app. Only use it once they've agreed to the charge.",
		modeOfferUnavailable: "Seat offers need an active, paid subscription.",
		tier: "Tier",
		extraSeats: "Extra seats to add",
		extraSeatsHelp: (tier: string, from: number, to: number) =>
			`Once accepted, extra ${tier} seats go from ${from} to ${to}.`,
		discountPercent: "Discount (%)",
		percentInvalid: "Enter a whole number from 0 to 100.",
		seatsInvalid: "Enter a whole number of seats from 1 to 999.",
		duration: "Duration",
		months: (months: number) =>
			`${months} ${months === 1 ? "month" : "months"}`,
		endsOnRenewal: (date: string) => `Ends on the renewal on ${date}.`,
		preview: "Preview",
		customerSees: "The customer sees",
		renewsNote: (date: string) =>
			`Once accepted, the seats renew automatically at the normal seat price from ${date} while the subscription is active, unless the customer removes them before.`,
		send: "Create offer",
		previewing: "Previewing…",
		applySeatsHelp: (tier: string, from: number, to: number) =>
			`Extra ${tier} seats go from ${from} to ${to} in total.`,
		applySummary: (seats: number, tier: string, from: number, to: number) =>
			`Adds ${seats} extra ${tier} ${seats === 1 ? "seat" : "seats"}: ${from} → ${to} in total.`,
		applyDueNow: (amount: string) =>
			`Charged now: ${amount}, prorated to the end of the current period.`,
		applyPaymentNote:
			"Stripe charges the organisation's default payment method. If that fails, the seats wait until the invoice is paid.",
		applyTrialSubscribed: (date: string, amount: string) =>
			`Nothing is charged now: the organisation is in a trial. It ends on ${date}, and the first invoice then is ${amount}, the subscription total with these seats.`,
		applyTrialUnsubscribed: (date: string) =>
			`Nothing is charged now: the organisation is in a trial. It ends on ${date}, and nothing is charged unless the organisation subscribes.`,
		applyNextInvoice: (date: string, amount: string) =>
			`Next invoice, ${date}: ${amount}, the subscription total with these seats.`,
		applyEnds: (date: string) =>
			`The subscription ends on ${date}, so there's no next invoice.`,
		applyDiscountEnds: (date: string) =>
			`That total includes a discount ending on ${date}; invoices from then on cost more and aren't shown here.`,
		applyPastDue: "The subscription is past due: an earlier invoice is unpaid.",
		consentConfirm: (org: string) =>
			`${org} agreed to these seats and the amounts shown`,
		consentReference: "Where their consent is recorded",
		consentReferenceHelp:
			"For example a ticket number, or an email's subject and date. Don't enter payment details.",
		consentReferenceInvalid: (min: number, max: number) =>
			`Enter ${min} to ${max} characters.`,
		apply: "Apply now",
		applying: "Applying…",
		paymentPending:
			"Added seats wait for their invoice to be paid. The organisation isn't notified: share the invoice or the billing link with them.",
		copyInvoiceLink: "Copy invoice link",
		copyBillingLink: "Copy organisation billing link",
		shareNote: (org: string) =>
			`${org} isn't notified: share the billing link with them.`,
		cancel: "Cancel",
		dueOnAccepting: (date: string) =>
			`Due on accepting · these seats, prorated to ${date}`,
		perInterval: (amount: string, interval: string) =>
			`${amount} / ${interval}`,
		month: "month",
		year: "year",
		discountedUntilThen: (amount: string, interval: string) =>
			` Until then they cost ${amount} / ${interval}.`,
		promotionCodeEnds: (code: string, date: string) =>
			`Promotion code ${code} only discounts invoices before ${date}, so invoices from then on cost more than the amounts shown for earlier dates.`,
		nextRenewal: (date: string, endsOn: string) =>
			`Next renewal, ${date} · subscription total, these seats discounted until ${endsOn}`,
		firstFullPrice: (date: string) =>
			`First invoice at the normal seat price, ${date} · subscription total`,
		withdrawNote:
			"A plan, price or interval change withdraws this offer and any unpaid invoice for it.",
		offerSent: (offer: string) => `Seat offer created: ${offer}.`,
		waiting: (org: string) =>
			`Waiting for ${org} to accept or decline it. Nothing is added or charged until then.`,
		acceptedWaiting:
			"Accepted; the seats apply once the prorated invoice is paid.",
		withdraw: "Withdraw offer",
		withdrawTitle: (org: string) => `Withdraw the seat offer to ${org}?`,
		withdrawAccepted:
			"The unpaid invoice is voided, and no seats are added or charged.",
		withdrawOpen:
			"The customer can no longer accept it. No seats were added or charged.",
		discountNotice: (discount: string) => `Seat discount: ${discount}.`,
		discountInDialog: (discount: string) =>
			`${discount}, then billed at the normal price unless removed before.`,
		discountDrops: (seats: number, tier: string) =>
			`The seat discount drops to ${seats} ${tier} seats and doesn't come back if you add seats later.`,
		discountRenews: (date: string, amount: string) =>
			`From ${date} these seats stay on the plan at the normal ${amount}, charged automatically if the subscription is active. To avoid that, remove them in Manage seats before ${date}. Seats used by members or pending invitations can't be removed.`,
		endMismatch:
			"Stripe ends this discount on a different renewal than the offer agreed. Check the subscription item's discount in Stripe.",
		endDiscount: "End seat discount",
		endDiscountTitle: (org: string) => `End ${org}'s seat discount?`,
		endDiscountDescription:
			"The discounted seats stay on the plan and are billed at the normal price from the next invoice.",
		endDiscountConfirm: "End discount",
		unavailableScheduled: (date: string) =>
			`Unavailable while a plan change is scheduled for ${date}. Wait until it applies or cancel it.`,
		offerFrom: (offer: string, amount: string, date: string) =>
			`Seat offer from gen.os.build: ${offer}. Nothing is added or charged unless you accept. Once accepted, the seats stay on the plan and renew automatically while the subscription is active, at ${amount} from ${date}, unless you remove them in Manage seats before.`,
		acceptPay: (amount: string) => `Accept and pay ${amount}`,
		acceptFree: "Accept · nothing due now",
		accept: "Accept",
		acceptPaused: {
			seatDiscountUnavailable: (offer: string) =>
				`gen.os.build offered you ${offer}. It can't be accepted until gen.os.build support resolves an issue with your seat discount, but you can decline it.`,
			reactivationCheck: (offer: string) =>
				`gen.os.build offered you ${offer}. It can't be accepted until the issue with your reactivation shown above is resolved, but you can decline it.`,
			externalSchedule: (offer: string) =>
				`gen.os.build offered you ${offer}. It can't be accepted while a change made outside the app is scheduled for your subscription. Ask gen.os.build support to change or cancel it, or decline the offer.`,
			seatChangePending: (offer: string) =>
				`gen.os.build offered you ${offer}. It can't be accepted while the seat change shown above waits for payment. Pay its invoice or let it expire, then accept the offer, or decline it.`,
		},
		decline: "Decline",
		updateFailed: "The seat offer couldn't be updated.",
		quoteFailed: "The seat offer couldn't be previewed.",
		applyFailed: "The seats couldn't be added.",
		applyQuoteFailed: "Adding the seats couldn't be previewed.",
	},
	adminUsers: {
		users: "Users",
		user: "User",
		email: "Email",
		status: "Status",
		created: "Created",
		lastSignIn: "Last sign-in",
		never: "Never",
		active: "Active",
		appAdmin: "App admin",
		actions: "Actions",
		noUsers: "No users yet",
		loading: "Loading users…",
		loadFailed: "Failed to load users.",
		disable: "Disable",
		disableTitle: (name: string) => `Disable ${name}?`,
		disableDescription:
			"They're signed out everywhere and can't sign in again. Their organisation memberships and seats stay, so organisations keep paying for their seats until an admin removes them. You can enable them again. Disabling them is a Clerk ban, which applies in every environment sharing this Clerk instance (next, every PR preview and local development share one).",
		enable: "Enable",
		enableTitle: (name: string) => `Enable ${name}?`,
		enableDescription: "They can sign in again.",
		changeFailed: "Failed to change whether the user is disabled.",
	},
	adminOrgs: {
		disableOrg: "Disable organisation",
		disablingOrg: "Disabling organisation…",
		disableOrgTitle: (name: string) => `Disable ${name}?`,
		disableOrgDescription:
			"Its members can't use the app or start a trial, checkout or reactivation, while Billing stays visible. Nothing is cancelled or charged. You can enable it again. The flag is stored in Clerk, so it applies in every environment sharing this Clerk instance (next, every PR preview and local development share one).",
		disableOrgHint:
			"End its trial on this page, or ask its admin to cancel the plan on Billing and wait until the subscription has ended, then try again.",
		enableOrg: "Enable organisation",
		enablingOrg: "Enabling organisation…",
		orgAccess: "Organisation access",
		orgEnabled: "Enabled",
		refreshing: "Refreshing…",
		refreshFailed: "Couldn't refresh, so some details may be out of date.",
		done: "Done",
		howSeatsWork: "How seats work",
		searchMembers: "Search members",
		searchMembersPlaceholder: "Name or email",
		noMatchingMembers: "No members match your search.",
		prepareSetupOpen: "Prepare setup…",
		prepareSetupTitle: (name: string) => `Prepare setup for ${name}`,
		reactivationLinkOpen: "Create reactivation link…",
		reactivationLinkTitle: (name: string) => `Reactivation link for ${name}`,
		enableOrgTitle: (name: string) => `Enable ${name}?`,
		enableOrgDescription:
			"Its members can use the app again if its plan allows it. The flag is stored in Clerk, so it applies in every environment sharing this Clerk instance (next, every PR preview and local development share one).",
		disabledNotice:
			"This organisation is disabled: its members can't use the app or start a trial, checkout or reactivation.",
		disabledChangeFailed:
			"Failed to change whether the organisation is disabled.",
		reactivationCheckPending: (id: string) =>
			`Reactivation ${id} hasn't been checked against its confirmed terms yet; the organisation can run the check on Billing.`,
		reactivationCheckFailedUnpaid: (problem: string, id: string) =>
			`Reactivation ${id} ${getAdminTermsProblem(problem)}, and Stripe refused to void its unpaid first invoice. Void it in the Stripe Dashboard, which ends the reactivation; until then the organisation's plan and seat changes are refused and its invoice can't be paid in the app.`,
		reactivationCheckFailed: (problem: string, id: string) =>
			`Reactivation ${id} ${getAdminTermsProblem(problem)}, and its first invoice was paid or had nothing due. Correct it in the Stripe Dashboard, then set its termsCheck metadata to passed; until then the organisation's plan and seat changes are refused.`,

		seatDiscountUnavailable: (tier: string, ids: string) =>
			`The Stripe coupon of the seat discount on ${tier} seats was deleted, so its terms can't be read: ${ids}. Stripe keeps applying the discount and the organisation keeps its access. Seat offers, plan changes and syncing a scheduled change that has to carry the discount are paused until the discount is ended. Ending it bills the discounted seats at the normal price from the next invoice; cancel a scheduled plan change first. A waiting offer can still be withdrawn.`,
		conflictingIds: (ids: string) =>
			`The organisation's billing is inconsistent, so it has no access: ${ids}. Resolve Stripe IDs in Stripe; for a membership or invitation ID, fix its seat tier in its Clerk private metadata. Nothing was cancelled automatically.`,
		retry: "Retry",
		scheduleSyncQueued: (date: string) =>
			`Since ${date}, the seats of the scheduled change wait to be updated in Stripe: a seat change waits for payment, it couldn't run yet, Stripe refused it, or the schedule has phases the app can't carry over. Until then the change would apply with its earlier seats. Updating them says why if it still fails, including when the change already started; then check the seats in Stripe, correct them with a seat change if needed, and mark it resolved.`,
		retryScheduleSync: "Update the scheduled seats",
		resolveScheduleSync: "Mark resolved",
		resolveScheduleSyncFailed: "The queued seat update couldn't be removed.",
		scheduleSyncFailed: "Stripe couldn't update the scheduled seats.",
		organisations: "Organisations",
		previousPage: "Previous",
		nextPage: "Next",
		pageOf: (page: number, pageCount: number) => `Page ${page} of ${pageCount}`,
		organisation: "Organisation",
		plan: "Plan",
		status: "Status",
		seatsUsed: "Seats used",
		seatsUsedOf: (used: number, paid: number) => `${used} of ${paid}`,
		planNamesFailed:
			"Plan names couldn't be loaded, so plans show their Stripe ID. Reload to try again.",
		invalidSeatTiers: "Invalid seat tiers",
		seatsUsedCount: (used: number) => `${used} used`,
		billingUnavailable: "Billing unavailable",
		retryBilling: "Retry",
		manage: "Manage",
		noPlan: "No plan",
		seatsColumn: "Seats",
		seatsAssignedCovered: (assigned: number, covered: number) =>
			`${assigned}/${covered}`,
		seatsCellLabel: (assigned: number, covered: number, isMismatch: boolean) =>
			`${assigned} seats assigned, ${covered} covered${isMismatch ? ". A seat type has more seats assigned than the subscription pays for" : ""}. Show seats per seat type`,
		seatsTierAssignedCovered: (
			tier: string,
			assigned: number,
			covered: number,
		) => `${tier}: ${assigned} assigned, ${covered} covered`,
		seatsNotCovered: "No active subscription, so no seat is covered.",
		seatsMismatch:
			"A seat type has more seats assigned than the subscription pays for.",
		reviewIssue: "Review issue",
		issues: "Issues",
		issuesNotAllFixable:
			"Not every issue can be repaired here: billing conflicts and problems in Stripe need the Stripe or Clerk dashboard.",
		issueSeatsExceeded: (tier: string, used: number, paid: number) =>
			`${tier}: ${used} seats assigned, ${paid} paid for. Move members to a seat type with a free seat, change their role to Finance (Finance holds no seat), or send a seat offer the organisation accepts and pays.`,
		issueBillingConflict: (ids: string) =>
			`Billing is inconsistent. Resolve ${ids} in Stripe or Clerk; it can't be fixed here.`,
		issueReactivationCheck: "A reactivation hasn't passed its terms check.",
		issueScheduleSync:
			"Seats of a scheduled change still wait to be synced to Stripe.",
		issueSeatOperationStuck:
			"A member or seat change couldn't be settled automatically.",
		issueNoCoverage:
			"Seats are assigned, but no active, trialing or past due subscription covers them.",
		noCoverageStatus: (status: string) => `Billing status: ${status}.`,
		issueNoPlan: "It has no plan in this environment, so no seat is covered.",
		issuePlanNotActive:
			"A plan was chosen for it, but isn't active yet, so no seat is covered.",
		setupTracked: ({
			plan,
			isTrial,
			isSupport,
			isCompleted,
			date,
		}: {
			plan: string;
			isTrial: boolean;
			isSupport: boolean;
			isCompleted: boolean;
			date: string;
		}) =>
			`${isSupport ? "Support prepared" : "Its creator chose"} ${isTrial ? `a trial of ${plan}` : `${plan}, paid`}; ${isCompleted ? "that setup completed" : "not started or paid yet"} (last changed ${date}).`,
		setupUntracked:
			"No plan choice is recorded for it in this environment, for example because it was created without choosing a plan.",
		setupMissing: (id: string) =>
			`It names setup ${id}, which isn't recorded in this environment. The record needs repair.`,
		setupOtherOrg: (id: string, orgId: string) =>
			`It names setup ${id}, which belongs to ${orgId || "no organisation yet"}. Review both organisations; nothing is deleted automatically.`,
		goToTrial: "Go to trial",
		goToPrepareSetup: "Prepare a setup",
		prepareSetup: "Prepare setup",
		prepareSetupDescription:
			"Save a plan choice that the organisation's admins and finance members find in Billing. They start the trial or pay themselves; nothing is sent to Stripe now and nobody is charged.",
		setupStartWith: "Start with",
		setupModeTrial: "Trial",
		setupModePaid: "Payment",
		prepareSetupSave: "Save setup",
		prepareSetupFailed: "The setup couldn't be prepared.",
		goToMembers: "Go to members",
		goToSeats: "Go to seats",
		goToSubscription: "Go to subscription",
		goToSeatOperation: "Go to the change",
		members: "Members",
		membersLoadFailed: "The members couldn't be loaded. Reload to try again.",
		membersConflict: (ids: string) =>
			`Members can't be listed while these seat tiers are invalid: ${ids}.`,
		memberColumn: "Member",
		roleColumn: "Role",
		seatColumn: "Seat",
		invited: "Invited",
		tierUnsaved: "not saved yet",
		noSeat: "No seat",
		roleAccessFinance:
			"Finance is a role, not a seat: it holds no seat and only has access to Billing.",
		noBillingChange:
			"No billing change: nothing is charged, and no subscription or paid seat quantity changes. When a seat is taken, the organisation's open checkouts are closed, as when its own admins change members.",
		seatOnlyWherePaidFree:
			"A seat can only be taken where a paid seat is free.",
		memberChangeFailed: "The member couldn't be changed.",
		cannotRemoveSelf: "You can't remove yourself.",
		inviteMember: "Invite",
		inviteMemberTitle: (org: string) => `Invite to ${org}`,
		inviteMemberDescription:
			"Sends the same invitation email as the organisation's own admins. An admin or member invitation reserves a seat of the chosen type until it's accepted or cancelled.",
		addExistingUser: "Add existing user",
		addExistingUserTitle: (org: string) => `Add an existing user to ${org}`,
		addExistingUserDescription:
			"Adds the user directly, without an invitation. An admin or member takes a seat of the chosen type.",
		searchUsers: "Search users",
		searchUsersPlaceholder: "Name, email or user ID",
		search: "Search",
		select: "Select",
		selected: "Selected",
		selectUser: (user: string) => `Select ${user}`,
		selectedUser: (user: string) => `Selected: ${user}`,
		noMatchingUsers: "No users match your search.",
		alreadyMember: "Member",
		disabledUser: "Disabled",
		addMember: "Add",
		seatTypeFull: (tier: string) => `${tier} (full)`,
		orgDisabledNoAdd:
			"The organisation is disabled, so no one can be invited or added. Members and invitations can still be removed.",
		removeMemberTitle: (name: string, org: string) =>
			`Remove ${name} from ${org}?`,
		removeMemberDescription: (name: string, org: string) =>
			`Only ${name}'s membership of ${org} is removed. The user and their other organisations stay as they are.`,
		removeMemberSeatFreed:
			"Paid quantities and billing stay unchanged, so their paid seat becomes free.",
		removeMemberNoSeat: "Paid quantities and billing stay unchanged.",
		removePerson: (name: string) => `Remove ${name}`,
		revokeInvitation: "Cancel invitation",
		revokeInvitationOf: (email: string) => `Cancel invitation for ${email}`,
		revokeInvitationTitle: (email: string, org: string) =>
			`Cancel the invitation of ${email} to ${org}?`,
		revokeInvitationDescription:
			"The invitation link stops working and the seat it reserved becomes free. Paid quantities and billing stay unchanged.",
		reactivationLink: "Reactivation link",
		reactivationLinkDescription:
			"Prepare the plan and seats for the organisation's admin or finance member. They open the link, review the price and pay on Billing; nothing is charged until they confirm, and it can't be paid for them here.",
		reactivationLinkPlanNote:
			"Only plans the organisation can choose itself are offered: public plans open to new organisations and its previous plan.",
		reactivationExtraSeats: (tier: string) => `Extra ${tier} seats`,
		copyLink: "Copy link",
		linkCopied: "Link copied",
		linkCopyFailed: "The link couldn't be copied. Select and copy it by hand.",
		loading: "Loading organisations…",
		loadingOrg: "Loading organisation…",
		loadFailed: "Failed to load the organisations.",
		orgLoadFailed:
			"The organisation's billing couldn't be loaded, so its plan, seats and trial aren't shown. Reload to try again.",
		planCatalogFailed:
			"The plan catalog couldn't be loaded, so plan names, plan changes and new trials aren't available. Reload to try again.",
		seatOperationLoadFailed:
			"The member and seat change state couldn't be loaded, so a stuck change can't be checked or released here. Reload to try again.",
		subscription: "Subscription",
		trialEnds: "Trial ends",
		trial: "Trial",
		trialCancelsAtEnd:
			"Until the organisation subscribes from Billing, the trial is set to cancel when it ends, so a card on file isn't charged.",
		alreadySubscribed: "Already subscribed—use a discount instead.",
		alreadyHadTrial:
			"Already had a trial. Expiring or cancelling doesn't make it eligible again.",
		subscribedTrial:
			"The organisation subscribed during the trial, so it can't be extended or ended here. Its first invoice is charged when the trial ends; it can be cancelled in the billing portal before then.",
		unmanagedTrial:
			"This trial wasn't started in the app, so it can't be extended or ended here. Manage it in Stripe.",
		alreadyPaid:
			"Paid for a plan before, so it can't get a trial. It can reactivate on Stripe, with a coupon if needed.",
		extendTrial: "Extend trial",
		extendTitle: (org: string) => `Extend ${org}'s trial`,
		days: "Days",
		newTrialEnd: (date: string) => `The trial then ends on ${date}.`,
		daysInvalid: (maxDays: number) =>
			`Enter a whole number of days from 1 to ${maxDays}.`,
		extendTooLong: (limit: number, maxDays: number) =>
			`Stripe ends a trial at most ${limit} days from today, so this trial can be extended by at most ${maxDays} ${maxDays === 1 ? "day" : "days"}.`,
		endTrialNow: "End trial now",
		endTrialTitle: (org: string) => `End ${org}'s trial now?`,
		endTrialDescription:
			"The plan becomes Cancelled today and projects lock. A card on file isn't charged, as no paid plan was accepted. An admin or finance member can choose a plan and pay. Members, invitations and seats are kept.",
		endTrial: "End trial",
		cancelSubscription: "Cancel subscription",
		cancelSubscriptionTitle: (org: string) => `Cancel ${org}'s subscription`,
		cancelSubscriptionDescription:
			"Only on the organisation's request, recorded outside the app. Members, invitations and seats are kept, and the organisation isn't disabled. This can't be undone here.",
		cancellationMode: "When and refund",
		cancellationModes: {
			atRenewal: "At renewal",
			now: "Now, no refund",
			nowProrated: "Now, refund unused time",
			nowFullRefund: "Now, refund latest invoice",
		},
		cancellationModeHelp: {
			atRenewal:
				"Access continues until the current period ends. Nothing is refunded.",
			now: "Access ends now. Nothing is refunded.",
			nowProrated:
				"Access ends now. The unused part of each paid invoice of the current period, seat change invoices included, is refunded in cash.",
			nowFullRefund:
				"Access ends now. What's left to refund on the latest paid invoice, which may be a seat change invoice, is refunded in cash.",
		},
		cancellationPreview: "Preview",
		cancellationPreviewing: "Previewing…",
		cancellationQuoteFailed: "Couldn't preview the cancellation.",
		cancellationAccessEnds: (date: string) => `Access ends: ${date}.`,
		cancellationNoRefund: "Nothing is refunded.",
		cancellationInvoiceKinds: {
			period: "Subscription invoice",
			seatChange: "Seat change invoice",
		},
		cancellationRefund: (
			kind: string,
			invoice: string,
			paidAt: string,
			amount: string,
		) => `${kind} ${invoice}, paid ${paidAt}: ${amount} refunded.`,
		cancellationRefundTotal: (amount: string) => `Total refunded: ${amount}.`,
		cancellationRefundNote:
			"Refunds go to the card or bank account each invoice was paid with, never as account credit. They can take a few days to arrive.",
		cancellationConsent: (org: string) =>
			`${org} asked for this cancellation and agreed to the refund shown`,
		keepSubscription: "Keep subscription",
		cancelling: "Cancelling…",
		cancellationFailed:
			"The cancellation didn't go through as shown. Check the subscription above before previewing again: if it was already cancelled, don't cancel it again. If Stripe didn't confirm a step, member and seat changes stay paused while it's retried, without refunding twice; contact support with this organisation's ID if they stay paused.",
		cancellationScheduled:
			"The subscription is set to end at renewal. Nothing was refunded.",
		cancellationCancelled: "The subscription was cancelled.",
		cancellationRefunded: (count: number) =>
			`${count} ${count === 1 ? "refund was" : "refunds were"} made.`,
		cancellationRefundsPending: (count: number) =>
			`${count} ${count === 1 ? "refund is" : "refunds are"} still being processed by the payment provider. The app doesn't follow them up: if one isn't received, contact support with this organisation's ID.`,
		cancellationRefundsFailed: (count: number, orgId: string) =>
			`${count} ${count === 1 ? "refund" : "refunds"} failed at the payment provider. Contact support with organisation ${orgId} to have ${count === 1 ? "it" : "them"} refunded.`,
		addTrial: "Add trial",
		startTrialOnPlan: "Start trial on a plan",
		startTrialTitle: (org: string) => `Start trial on a plan for ${org}`,
		startTrialDescription: "Any open plan, public or private.",
		trialPlan: "Trial plan",
		billing: "Billing",
		monthly: "Monthly",
		yearly: "Yearly",
		public: "Public",
		assigned: "Private",
		startTrialNotice: (org: string, plan: string, date: string) =>
			`${org} gets ${plan} as a trial until ${date}, without a card. Seats cover its current members and invitations, which are kept. It's then not eligible for another trial.`,
		startTrial: "Start trial",
		cancel: "Cancel",
		back: "Organisations",
		trialFailed: "Failed to change the trial.",
		price: "Price",
		priceVersion: (version: number, interval: string) =>
			`v${version} · ${interval}`,
		planChangeFailed: "Failed to change the plan.",
		releaseFailed: "Failed to resolve the seat change.",
		previewFailed: "Failed to preview the change.",
		changePlan: "Change plan or billing",
		changePlanTitle: (org: string) => `Change ${org}'s plan or billing`,
		paidSeatsStay: (parts: SeatPart[]) =>
			`Paid seats stay at ${formatSeatParts({ parts, joiner: " and " })}.`,
		seatMappingLegend: "Seat types",
		trialSeatNote: (seatType: string) =>
			`The organisation's only member gets the plan's trial seat, ${seatType}, until the trial ends.`,
		seatMappingDescription: (plan: string) =>
			`${plan} doesn't offer every seat type in use. Choose the seat type each one moves to; several can move to the same one. Members and invitations move with their seats.`,
		seatMappingFor: (name: string, paid: number, used: number) =>
			`${name} · ${paid} paid, ${used} used`,
		seatMappingTarget: (name: string) => `Seat type ${name} moves to`,
		chooseSeatType: "Choose a seat type",
		seatMappingToday:
			"Seats that move to another seat type move today. Set the effective date to today.",
		seatsAfterChange: (parts: SeatPart[]) =>
			`After the change: ${formatSeatParts({ parts, joiner: " and " })}.`,
		newPlan: "New plan",
		currentPlanNotOffered:
			"The organisation's current plan is disabled, so it isn't offered here. Choose the plan to move it to.",
		planOption: (plan: string, version: number, visibility: string) =>
			`${plan} · v${version} · ${visibility}`,
		billingInterval: "Billing interval",
		effectiveDate: "Effective date",
		defaultsToRenewal: (date: string) =>
			`Defaults to the next renewal, ${date}.`,
		prorate: "Prorate the rest of the period",
		atRenewalNote: "Starting with the renewal invoice.",
		proratedNote:
			"With the difference for the rest of the period prorated on the next invoice.",
		notProratedNote:
			"Without proration: no charge or credit for the rest of the period.",
		intervalChangeNote: (interval: string, date: string, isProrated: boolean) =>
			`Changing the interval starts a new billing period on ${date}: Stripe invoices the full ${interval.toLowerCase()} price then, ${isProrated ? "crediting the unused time of the current period" : "without credit for the unused time of the current period"}.`,
		amountsEstimated:
			"For a later date these are today's amounts; Stripe bills the prices, discounts and tax that apply on that date.",
		chargeConsent: (total: string, date: string) =>
			`I confirm Stripe invoices ${total} on ${date} with this change.`,
		trialAppliesNow: (date: string) =>
			`Applies now: nothing is billed during a trial, which continues on the new plan until ${date}.`,
		alreadyOnPrice: (org: string) =>
			`${org} is already on this price and interval.`,
		notAsked: (org: string, date: string, total: string) =>
			`${org} isn't asked to accept. Stripe's next invoice with the change, on ${date}: ${total}.`,
		insteadOf: (total: string) => `Without it: ${total}.`,
		replacesChange: (date: string) =>
			`This replaces the change already scheduled for ${date}.`,
		voidsInvoice:
			"A seat change waits for payment. Changing the plan today makes Stripe void its unpaid invoice, and seats can then be added again at the new price; a later date can only be scheduled once the invoice is paid or discarded.",
		voidsInvoiceAndInvite: (email: string) =>
			`A seat change waits for payment. Changing the plan today makes Stripe void its unpaid invoice and cancels the invitation for ${email} waiting on it, and seats can then be added again at the new price; a later date can only be scheduled once the invoice is paid or discarded.`,
		lostCode: (code: string) =>
			`Promotion code ${code} is restricted to the current plan, so it stops discounting invoices once the change applies.`,
		changePlanConfirm: "Change plan",
		scheduledChange: (
			date: string,
			org: string,
			plan: string,
			version: number,
			interval: string,
			isProrated: boolean,
		) =>
			`Scheduled for ${date}: ${org} moves to ${plan} at price v${version}, ${interval.toLowerCase()}, ${isProrated ? "with the rest of the period prorated" : "without proration"}. Until then nothing changes.`,
		externalChange: (date: string) =>
			`A change made outside the app, in Stripe, is scheduled for ${date}.`,
		externalScheduleAttached:
			"A subscription schedule made in the Stripe Dashboard is attached, so the plan can't be changed, nor the scheduled change cancelled, here, and the organisation can't change its seats. Change or cancel it in the Stripe Dashboard.",
		cancelScheduledChange: "Cancel scheduled change",
		cancelScheduledChangeTitle: "Cancel the scheduled change?",
		cancelScheduledChangeDescription: (org: string) =>
			`${org} stays on its current plan, price and interval.`,
		cancelChange: "Cancel change",
		seatOperation: "Member or seat change",
		operationInProgress: "In progress",
		unknownAction: "unreadable",
		operationSummary: (action: string, stage: string, updatedAt: string) =>
			`${action} · ${stage} · last step ${updatedAt}`,
		waitingInvitation: "Waiting invitation",
		reservationSummary: (
			email: string,
			tier: string,
			invoiceId: string,
			target: string,
		) =>
			`${email} (${tier}) waits for invoice ${invoiceId}, which raises ${target}`,
		stripeRequests: "Stripe requests sent",
		stripeSubscription: "Stripe subscription",
		subscriptionEvidence: (
			status: string,
			items: string,
			hasPendingUpdate: boolean,
		) =>
			`${status} · ${items}${hasPendingUpdate ? " · seat change waiting for payment" : ""}`,
		stripeInvoice: "Stripe invoice",
		clerkPeople: "Clerk members and invitations",
		personEvidence: (kind: string, email: string, role: string, tier: string) =>
			`${kind}: ${email} · ${role} · ${tier}`,
		releaseWarning:
			"Releasing undoes nothing in Stripe or Clerk and can't stop a request still running. Only release once Stripe and Clerk above show whether the change applied; if you can't tell, don't release.",
		releaseOutcome: "What Stripe and Clerk show",
		chooseOutcome: "Choose the outcome",
		outcomeApplied: "The change applied",
		outcomeNotApplied: "The change didn't apply",
		outcomeInvite: "The seat is paid or was added: send the invitation",
		releaseNote: "Note (what you checked, at least 10 characters)",
		release: "Release",
	},
	subscriptionOrder: {
		progress: "Subscription progress",
		planStep: "Plan",
		seatsStep: "Seats",
		detailsStep: "Payment details",
		detailsShortStep: "Payment",
		reviewStep: "Review",
		confirmedStep: "Confirmed",
		completed: "(completed)",
		chooseSeatTitle: "Choose your seat",
		subscribeTo: (plan: string) => `Subscribe to ${plan}`,
		reviewTitle: "Review and subscribe",
		completeTitle: "You're subscribed",
		summary: "Subscription summary",
		includes: (seats: string) => `Includes ${seats}.`,
		seatLine: (count: number, tier: string, price: string) =>
			`${count} ${tier} ${count === 1 ? "seat" : "seats"} × ${price}`,
		paidSeatLegend: (date: string) => `Your paid seat from ${date}`,
		trialSeatSwitch: (trialTier: string, paidTier: string, date: string) =>
			`Your ${trialTier} seat stays until your trial ends on ${date}, then becomes ${paidTier}.`,
		totalExcludingTax: "Total excluding tax",
		chargedWhenTrialEnds: "You’ll be charged when your trial ends on",
		notChargedYet: "You won't be charged yet",
		cardSavedNote:
			"Your payment details are saved. Review the exact amount, then subscribe.",
		continue: "Continue",
		changeCode: "Change the code",
		paymentDetails: "Payment details",
		detailsSaved: "Billing address and card saved",
		codePending: "Apply or clear the promotion code to subscribe.",
		codeApplied: (code: string) => `${code} applied.`,
		discount: "Discount",
		tax: "Tax",
		totalIncludingTax: "Total including tax",
		dueAfterCredit: "Due after your credit",
		thenAfterCode: (amount: string) => `Then ${amount} once the code ends.`,
		checking: "Checking the amount due…",
		subscribing: "Subscribing…",
		confirmedTitle: "Subscription confirmed",
		confirmedNote: "Billing address and card saved for future invoices.",
		goToBilling: "Go to billing",
	},
	orgBilling: {
		orgDisabled:
			"gen.os.build disabled this organisation, so its members can't use the app or start a plan or trial. Billing stays visible. Contact gen.os.build support to enable it again.",
		billingConflict:
			"This organisation's billing is inconsistent, so nobody in it can open projects until gen.os.build support resolves it. Nothing was cancelled or charged because of this. Please contact support.",
		conflictingIds: (ids: string) => `IDs to quote to support: ${ids}`,
		externalSchedulePausesSeats:
			"A change made outside the app is scheduled for this subscription, so seats can't be changed here until gen.os.build support changes or cancels it.",
		seatDiscountUnavailable: (tier: string) =>
			`The details of your seat discount on ${tier} seats can't be read. It still applies to your invoices. Seat changes and seat offers are paused until gen.os.build support resolves it; paying or discarding a pending change and the billing portal still work.`,
		scheduledSeatDiscountUnknown:
			" How your seat discount carries into this change can't be shown.",
		choosePlan: "Choose a plan",
		firstSeatAsAdmin: "You take the first seat as admin.",
		yourSeat: "Your seat",
		trialSeatNote: (seatType: string) =>
			`Trial: your seat is ${seatType}. Additional seats are chosen when subscribing.`,
		paidSeats: "Seats when subscribing",
		yourSeatNote:
			"You're the only admin or member, so you choose your own seat type.",
		seatDiscountKept: (seatDiscount: string) =>
			`Your seat discount (${seatDiscount}) continues until its end date.`,
		promotionCodeOptional: "Promotion code (optional)",
		promotionCodeCombines:
			"Only codes that can be combined with other discounts work together with the seat discount.",
		continueToPayment: "Continue to payment",
		reactivationConfirmTitle: "Reactivate keeping the seat discount?",
		reactivationFirstInvoiceProrated: (amount: string, date: string) =>
			`First invoice: ${amount} including tax, until ${date}. Next charge: ${date}.`,
		reactivationFirstInvoicePeriod: (amount: string, date: string) =>
			`First invoice: ${amount} including tax, until ${date}. Next charge: ${date}.`,
		reactivationDueAfterBalance: (amount: string) =>
			`Due now after your account balance: ${amount}.`,
		reactivationNothingDue:
			"Nothing is due now, so the plan starts right away.",
		reactivationBelowMinimum: (amount: string, minimum: string) =>
			`That's below the minimum charge of ${minimum}, so nothing is charged now: ${amount} is added to your next invoice, and the plan starts right away.`,
		reactivationTermsRenewal: (date: string) =>
			`The first invoice covers the time until ${date}, your next charge date.`,
		reactivationTermsAmountLater: (minimum: string) =>
			`You review its amount, including tax, before paying; nothing is charged until you pay. If less than ${minimum} is due, the plan starts right away and the amount is added to your next invoice.`,
		reactivationCreateInvoice: "Create first invoice",
		reactivationInvoiceAmount: (total: string, tax: string, date: string) =>
			`The first invoice is ${total}, including ${tax} tax, for the time until ${date}, when your subscription renews.`,
		reactivationDiscountUntil: (date: string) =>
			`Your seat discount lasts until ${date}; from then these seats are at the normal price.`,
		checkoutEndingDiscount: "Continue without seat discount",
		endSeatDiscountTitle: "End the seat discount?",
		endedSeatDiscountUnreadable: (tier: string) =>
			`Your previous seat discount on ${tier} seats can't be restored here. To keep it, contact gen.os.build support before reactivating. Continuing without it bills these seats at the normal price.`,
		unreadableSeatDiscount: (tier: string) =>
			`on ${tier} seats, whose terms can't be read`,
		endSeatDiscountDescription: (seatDiscount: string) =>
			`Your seat discount (${seatDiscount}) ends now, and these seats are billed at the normal price. You can enter a promotion code at checkout.`,
		upgradePending: (date: string) =>
			`The paid plan is waiting for its first invoice to be paid; the trial continues until then. Unpaid, the paid plan is cancelled on ${date}.`,
		upgradePendingAfterTrial: (date: string) =>
			`The trial ended while the paid plan's first invoice waits for payment, so projects stay locked until it's paid. Unpaid, the paid plan is cancelled on ${date}.`,
		reactivationPending: (date: string) =>
			`Reactivation is waiting for its first invoice to be paid. Until it's paid there's no access; it's cancelled if it's still unpaid on ${date}.`,
		cancelReactivation: "Cancel reactivation",
		reactivationCheckPending:
			"The reactivation's renewal dates and seat discount haven't been checked against those you confirmed yet. Until they are, its invoice can't be paid here and plan and seat changes wait.",
		reactivationCheckPendingActive:
			"Your plan is active, but the reactivation's renewal dates and seat discount haven't been checked against those you confirmed yet. Until they are, plan and seat changes wait.",
		reactivationCheckFailed: (failure: "mismatch" | "unknown", id: string) =>
			`${failure === "unknown" ? "The reactivation's seat discount can't be checked against the terms you confirmed" : "The reactivation was set up with renewal dates or a seat discount different from those you confirmed"}, and its first invoice was already paid or had nothing due. Your plan stays active and nothing was cancelled, refunded or credited, but plan and seat changes wait until gen.os.build support corrects it. Contact support with ID ${id}.`,
		reactivationCheckFailedUnpaid: (
			failure: "mismatch" | "unknown",
			id: string,
		) =>
			`${failure === "unknown" ? "The reactivation's seat discount can't be checked against the terms you confirmed" : "The reactivation was set up with renewal dates or a seat discount different from those you confirmed"}, and its unpaid first invoice couldn't be voided. Don't pay it: nothing was charged. Check reactivation tries voiding it again, which ends the reactivation; until then plan and seat changes wait. Contact gen.os.build support with ID ${id} if it keeps failing.`,
		checkReactivation: "Check reactivation",
		checkReactivationFailed: "The reactivation couldn't be checked.",
		cancelReactivationFailed: "The reactivation couldn't be cancelled.",
		reactivateTitle: (org: string) => `Reactivate ${org}`,
		minSeatsNote:
			"Seats can't go below the seats your members and invitations use.",
		plan: "Plan",
		billing: "Billing",
		billingInterval: "Billing interval",
		intervalOnly: (interval: string) => `${interval} only for this plan.`,
		yearlySaving: (amount: string) => `Yearly saves ${amount} on the base fee.`,
		monthly: "Monthly",
		yearly: "Yearly",
		month: "month",
		year: "year",
		additionalSeats: (tier: string) => `Additional ${tier} seats`,
		perSeat: (price: string, interval: string) =>
			`${price} per seat / ${interval}`,
		planIncludes: (plan: string, seats: string) => `${plan} includes ${seats}.`,
		includedSeats: (parts: SeatPart[]) =>
			formatSeatParts({ parts, joiner: " and " }),
		seatCounts: (parts: SeatPart[]) =>
			formatSeatParts({ parts, joiner: " + " }),
		baseFee: (seats: string) => `Base fee · includes ${seats}`,
		extraLine: (count: number, tier: string, price: string) =>
			`${count} extra ${tier} × ${price}`,
		total: "Total",
		perInterval: (amount: string, interval: string) =>
			`${amount} / ${interval}`,
		startTrial: (days: number) => `Start ${days}-day trial`,
		startingTrial: "Starting trial…",
		continueToCheckout: "Continue to checkout",
		finishSetupTitle: (org: string) => `Finish setting up ${org}`,
		finishSetupDescription:
			"Choose a plan to start using gen.os.build with this organisation.",
		setupChoiceTitle: (plan: string) => `Continue with ${plan}`,
		setupPreparedBySupport:
			"gen.os.build support prepared this plan choice for your organisation.",
		setupTrialDescription: (days: number) =>
			`Your ${days}-day trial starts when you click below. No card is needed and nothing is charged.`,
		setupPaidDescription:
			"Review your choice, then continue to Stripe to pay. Nothing is charged until you confirm there.",
		setupStartTrial: (days: number, plan: string) =>
			`Start ${days}-day trial of ${plan}`,
		setupContinueToPayment: (plan: string) => `Continue to payment for ${plan}`,
		setupChooseOther: "Choose a different plan",
		setupPlanUnavailable:
			"The plan you chose is no longer available to new organisations. Choose another plan.",
		setupTrialUnavailable:
			"A trial of this plan isn't available any more. You can buy it or choose another plan.",
		setupSeatsChanged:
			"Your seat choice no longer fits this plan's current price. Check it and choose again.",
		setupYourSeat: (tier: string) => `Your seat: ${tier}`,
		linkOrgNotMember:
			"This billing link is for an organisation you aren't a member of, so it isn't applied here.",
		linkOrgSwitching: "Opening the organisation of this billing link…",
		linkOrgSwitchFailed:
			"The organisation of this billing link couldn't be opened.",
		linkOrgRetry: "Try again",
		linkOrgShowCurrent: "Show my current organisation's billing",
		noPlans:
			"No plan is open for sign-up right now. Contact gen.os.build to get access.",
		privatePlan: (plan: string, org: string) =>
			`${plan} is private: gen.os.build assigned it to ${org}, and other organisations can't choose it.`,
		archivedPlan: (plan: string, org: string) =>
			`${plan} is disabled, so no organisation can start it any more. ${org}'s trial began before that, so it's honoured until the trial ends.`,
		closedPlan: (plan: string, org: string) =>
			`${plan} is closed to new organisations, but ${org} keeps access to it.`,
		reactivateKeepsPrice:
			"Reactivating starts a new subscription at its current price.",
		previousPlanUnavailable: (org: string) =>
			`${org}'s previous plan is no longer offered, so reactivating needs one of the plans above.`,
		subscribeNow: "Subscribe now",
		trialExpiresIn: (days: number) =>
			`Your trial will expire in ${days} ${days === 1 ? "day" : "days"}.`,
		subscribeToAvoidInterruption:
			"Subscribe now to avoid service interruption.",
		subscribedTrialNote: (date: string) =>
			`You're subscribed. The trial continues until ${date}, when the first payment is charged.`,
		subscribedTrialEnds: (date: string) => `Your subscription ends on ${date}.`,
		unmanagedTrialNote:
			"This trial was set up outside the app, so it can't be subscribed to here. Contact support to change it.",
		subscribeKeepsPlan: (plan: string) =>
			`Subscribing keeps ${plan} at the price you trialled.`,
		subscribeDetailsNeeded:
			"First add a billing address and payment method on a secure page. Nothing is charged.",
		addPaymentDetails: "Add payment details",
		applyCode: "Apply",
		promotionCodeAcceptedBeforeDetails:
			"Code accepted. We’ll confirm the discount after you add payment details.",
		confirmSubscribe: "Subscribe",
		reactivationNotCheckedTitle: "Reactivation not checked yet",
		reactivationNeedsSupportTitle: "Reactivation needs support",
		reactivationDontPayTitle: "Don't pay the reactivation invoice",
		reactivationWaitingTitle: "Reactivation waiting for payment",
		pendingCancelledOn: (date: string) =>
			`If still unpaid, this pending subscription will be cancelled on ${date}.`,
		upgradeWaitingTitle: "Paid plan waiting for payment",
		upgradeWaitingAfterTrialTitle: "Trial ended, paid plan waiting for payment",
		planExpiredTitle: "Your organisation’s plan has expired.",
		planCancelledTitle: "Your organisation’s plan is cancelled.",
		reactivateNote:
			"Choose a plan to reactivate. Members and invitations are kept.",
		askToReactivate:
			"Ask an admin or finance member to reactivate. Members and invitations are kept.",
		nextInvoice: "Next invoice",
		projectedTotal: "Projected total",
		earlierPrice: "Earlier price",
		earlierPriceNote:
			"You keep the price and included seats you signed up with. New customers pay the current price.",
		seatSubscription: "Seat subscription",
		seatColumn: "Seat",
		includedColumn: "Included",
		paidExtraColumn: "Paid extra",
		totalColumn: "Total",
		usedColumn: "Used",
		manageExtraSeats: "Manage paid extra seats",
		baseIncludes: (seats: string) => `The base fee includes ${seats}.`,
		extraTierSeats: (tier: string) => `Extra ${tier} seats`,
		extraSeatsInUse: (count: number, tier: string) =>
			`At least ${count} are needed for the ${tier} seats in use. Free some on Members first.`,
		eachPerInterval: (price: string, interval: string) =>
			`${price} each / ${interval}.`,
		activeSeatsNote:
			"Added seats apply once paid; fewer seats apply right away, without a refund, also when other seats are added in the same change.",
		dueNow: (amount: string, date: string) =>
			`${amount} is due now, prorated to ${date}.`,
		previewing: "Checking the amount due…",
		trialSeatsNote: "During the trial, seat changes apply straight away.",
		paymentPendingAmount: (
			pending: string,
			amount: string,
			date: string,
			current: string,
		) =>
			`Payment needed: paid extra seats go to ${pending} once the ${amount} invoice, prorated to ${date}, is paid. Until then you keep ${current}.`,
		payAmount: (amount: string) => `Pay ${amount}`,
		discardChange: "Discard change",
		discardFailed: "Failed to discard the change.",
		discountRedeemed: (
			code: string,
			discount: string,
			duration: string,
			date: string,
		) =>
			`Promotion code ${code} (${discount} ${duration}), redeemed on ${date}`,
		discountPercent: (percent: number) => `${percent}% off`,
		discountAmount: (amount: string) => `${amount} off`,
		discountForever: "forever",
		discountOnce: "on the first invoice",
		discountMonths: (months: number) =>
			`for ${months} ${months === 1 ? "month" : "months"}`,
		discountOtherPlan:
			", is restricted to another plan, so it no longer discounts invoices.",
		deletedCouponDiscount: (code: string, redeemedAt: string) =>
			`Promotion code ${code}, redeemed on ${redeemedAt}, still applies, but its details can't be shown.`,
		deletedCouponDiscountEnds: (date: string) => ` It ends on ${date}.`,
		discountEveryInvoice: ", applies to every invoice.",
		discountNextInvoice: ", applies to the next invoice only.",
		discountRunOut: (date: string) =>
			`, has run its course: the invoice on ${date} is at the full price.`,
		discountUntil: (date: string) =>
			`, applies to invoices before ${date}, then the full price.`,
		scheduledChange: (
			date: string,
			org: string,
			plan: string,
			version: number,
			interval: string,
			isProrated: boolean,
		) =>
			`Scheduled for ${date}: ${org} moves to ${plan} at price v${version}, ${interval}, ${isProrated ? "with the difference for the rest of the period prorated on the next invoice" : "without proration"}. Until ${date} nothing changes.`,
		scheduledExternal: (date: string) =>
			`A change to this subscription is scheduled for ${date}.`,
		scheduledWithdrawal: ({
			isOfferOpen,
			isInvoicePending,
			inviteEmail,
		}: Withdrawal) => {
			const outcomes = [
				...(isOfferOpen ? ["withdraws the open seat offer"] : []),
				...(isInvoicePending ? ["voids the unpaid seat invoice"] : []),
				...(inviteEmail
					? [`cancels the invitation for ${inviteEmail} waiting on it`]
					: []),
			];
			if (outcomes.length === 0) return undefined;
			const list = new Intl.ListFormat("en-GB").format(outcomes);
			return isInvoicePending
				? `${list}; seats can then be added again at the new price`
				: list;
		},
		scheduledOnDate: (note: string) => ` On that date it ${note}.`,
		pendingBeforeChange: (date: string, note: string) =>
			` If it's still unpaid on ${date}, the scheduled plan change ${note}.`,
		pendingEndsCodes: (codes: string) =>
			` Paying also ends promotion code ${codes}, which can't be combined with the seat discount.`,
		acceptedPending: (offer: string) =>
			` You accepted the seat offer (${offer}). Discarding ends the offer; gen.os.build can send you a new one.`,
		scheduledRenewal: (date: string, amount: string) =>
			`The next invoice with the change, on ${date}, is ${amount} for the same seats.`,
		discountEnded: (date: string) =>
			`, has run its course: it last discounted the invoice on ${date}.`,
		monthlyLower: "monthly",
		yearlyLower: "yearly",
		plansFailed: "Failed to load the plans.",
		trialFailed: "Failed to start the trial.",
		subscribeFailed: "Failed to subscribe.",
		previewFailed: "Failed to check the amount due.",
		yourPlan: "Your plan",
		status: "Status",
		noPlan: "No plan",
		reactivate: "Reactivate",
		keptWhileInactive: "Members and invitations are kept.",
		seats: "Seats",
		seatsAvailable: (seats: number, available: number) =>
			`${seats} (${available} available)`,
		manageSeats: "Manage seats",
		seatsDialogDescription: (used: number, seats: number) =>
			`${used} of ${seats} ${seats === 1 ? "seat is" : "seats are"} assigned or reserved by invitations.`,
		seatsInUse: (used: number) =>
			`${used} ${used === 1 ? "seat is" : "seats are"} in use. Remove members or invitations first.`,
		seatsChangeHint:
			"Extra seats are charged pro rata now. Fewer seats apply without a refund.",
		cancel: "Cancel",
		save: "Save",
		renewalDate: "Renewal date",
		trialEnds: "Trial ends",
		cancelAt: "Ends on",
		pendingInvite: (email: string, tier: string) =>
			`Includes the invitation for ${email} with a ${tier} seat; it's only sent once the invoice is paid.`,
		paymentPending: (
			pending: number,
			seats: number,
			amount: string,
			date: string,
		) =>
			`Payment needed: ${pending} ${pending === 1 ? "seat is" : "seats are"} waiting for the ${amount} invoice, prorated to ${date}, to be paid. Until then you keep ${seats}.`,
		payInvoice: "Pay invoice",
		overUsed: (excess: number) =>
			`${excess} more ${excess === 1 ? "seat is" : "seats are"} used than paid for, so nobody in the organisation can open projects. Buy more seats, or remove or demote members or cancel invitations in Members.`,
		overUsedMember: (excess: number) =>
			`${excess} more ${excess === 1 ? "seat is" : "seats are"} used than paid for, so nobody in the organisation can open projects. Ask an organisation admin to buy more seats or remove members or invitations.`,
		overUsedFinance: (excess: number) =>
			`${excess} more ${excess === 1 ? "seat is" : "seats are"} used than paid for, so nobody in the organisation can open projects. Buy more seats, or ask an organisation admin to remove members or invitations.`,
		paymentTitle: "Payment & invoices",
		paymentDescription: "Payment methods, billing details and invoices.",
		openPortal: "Manage billing",
		loading: "Loading billing…",
		loadFailed: "Failed to load the organisation plan.",
		checkoutFailed: "Failed to start the organisation checkout.",
		updateFailed: "Failed to change the number of seats.",
		portalFailed: "Failed to open organisation billing.",
		hasCompanySeat:
			"Your organisation gives you a seat, so you don't need your own subscription.",
		seatsExceeded:
			"Your organisation uses more seats than it pays for, so nobody in it can open projects. An organisation admin can resolve this in Organisation billing.",
		personalPlanNoAccess:
			"A personal subscription no longer gives access to the app; only an organisation plan does.",
		noOrgPlan:
			"Your organisation has no active plan. An organisation admin can buy one in Organisation billing.",
		goToOrgBilling: "Organisation billing",
		accessFailed: "Couldn't check your access to the app.",
		retry: "Retry",
	},
	onboarding: {
		title: "Create an organisation",
		choosePlan: "Choose a plan",
		choosePlanDescription:
			"Pick the plan for your new organisation. You start the trial or pay after naming it.",
		noPlans: "No plans are open to new organisations right now.",
		plansFailed: "The plans couldn't be loaded. Reload to try again.",
		continueWithTrial: (days: number) => `Continue with ${days}-day trial`,
		continueWithPayment: "Continue with payment",
		nameOrg: "Name your organisation",
		nameOrgTrial: (plan: string, days: number) =>
			`You chose a ${days}-day trial of ${plan}. It starts as soon as the organisation is created, without a card.`,
		nameOrgPaid: (plan: string) =>
			`You chose ${plan}. After creating the organisation you continue to payment. Until it's paid, the organisation waits for payment with this choice saved.`,
		orgName: "Organisation name",
		createAndStartTrial: (days: number) =>
			`Create organisation and start ${days}-day trial`,
		createAndPay: "Create organisation and continue to payment",
		retryContinue: "Try again",
		setupUnconfirmed:
			"We couldn't confirm yet whether the organisation was created. Trying again looks it up instead of creating another one.",
		unfinishedSetups: "Unfinished setups",
		resumeSetup: "Continue setup",
		setupWaitsForPayment: "Waits for payment",
		setupWaitsForTrial: "Trial not started",
		setupNotCreated: "Not created yet",
		creating: "Creating…",
		createFailed: "The organisation couldn't be created.",
		back: "Back",
		cancel: "Cancel",
		chooseOrg: "Choose an organisation",
		createOrg: "Create organisation",
		yourOrgs: "Your organisations",
		noOrgs: "You aren't a member of any organisation yet.",
		invitations: "Invitations",
		accept: "Accept",
		acceptFailed: "The invitation couldn't be accepted.",
		suggestions: "Organisations you can join",
		requestToJoin: "Request to join",
		requested: "Requested",
		joinFailed: "The request to join couldn't be sent.",
		showMore: "Show more",
	},
	invitation: {
		unavailable: "Unable to accept this invitation.",
		backToProjects: "Back to projects",
	},
	prospectInvite: {
		invites: "Prospect invitations",
		invite: "Invite prospect",
		plansLoading: "Loading plans…",
		plansLoadFailed: "Plans couldn't be loaded, so no prospect can be invited.",
		inviteDescription:
			"They get a link to create their organisation with a trial on this plan, without a card. Only someone signed in with this verified email address can accept it, until it expires.",
		orgName: "Organisation name",
		email: "Email",
		plan: "Plan",
		trialDays: "Trial days",
		trialDaysInvalid: (max: number) => `Enter 1 to ${max} days.`,
		expiresOn: "Expires on",
		expiresOnDescription: (timeZone: string) =>
			`The link works until the end of this day (${timeZone}).`,
		expiresOnInvalid: (last: string) => `Choose a day from today to ${last}.`,
		noPlans: "No plan open to new organisations offers a trial.",
		create: "Create invitation",
		createFailed: "The invitation couldn't be created.",
		invitesLoadFailed: "Failed to load prospect invitations.",
		noInvites: "No invitations yet",
		emailInvalid: "Enter a valid email address.",
		invitee: "Invitee",
		delivery: "Email",
		status: "Status",
		expires: "Expires",
		trialOf: (plan: string, days: number) => `${plan} · ${days}-day trial`,
		copyLink: "Copy link",
		linkCopied: "Link copied",
		copyFailed: "The link couldn't be copied.",
		retryEmail: "Retry email",
		retryFailed: "The email couldn't be requested.",
		revoke: "Revoke",
		revokeTitle: (email: string) => `Revoke the invitation for ${email}?`,
		revokeDescription:
			"Its link stops working. Anyone can still sign up for an account; they just can't use this invitation. An invitation its recipient already accepted can't be revoked.",
		revokeFailed: "The invitation couldn't be revoked.",
		deliveries: {
			requested: "Email requested",
			accountExists: "Has an account",
			alreadyInvited: "Already invited",
			refused: "Not emailed",
			unconfirmed: "Not confirmed",
		},
		deliveryNotes: {
			requested:
				"Clerk was asked to email the link, which doesn't confirm it arrived. You can also copy it.",
			accountExists:
				"Not emailed, because this address already has an account. Copy the link and send it yourself.",
			alreadyInvited:
				"Not emailed, because this address already has a pending invitation. Copy the link and send it yourself.",
			refused:
				"Clerk didn't email it, usually because this address already has an account or a pending invitation. Copy the link and send it yourself.",
			unconfirmed:
				"Clerk didn't confirm the email. Retry it, or copy the link and send it yourself.",
		},
		statuses: {
			pending: "Pending",
			expired: "Expired",
			revoked: "Revoked",
			claimed: "Accepted",
			completed: "Trial started",
		},
		title: "You're invited",
		acceptTitle: "Start your trial",
		acceptDescription: (org: string, plan: string, days: number) =>
			`Create ${org} with a ${days}-day trial of ${plan}. No card is needed.`,
		startTrial: "Create organisation and start trial",
		starting: "Starting…",
		startFailed: "The trial couldn't be started. Try again.",
		wrongAccount: "This invitation is for a different account",
		wrongAccountDescription:
			"Sign out, then sign in or sign up with the email address the invitation was sent to.",
		unavailable: "This invitation isn't available",
		unavailableDescription:
			"It may have expired or been revoked. Ask whoever invited you for a new one.",
		loadFailed: "The invitation couldn't be loaded.",
		retry: "Try again",
	},
	errorMessages: {
		whoops: "Whoops!",
		goToProjects: "Go to Projects",
		errors: {
			generic: "Something went wrong.",
			401: "You are not authorized to view this page.",
			403: "You do not have permission to access this page.",
			404: "The page you are looking for does not exist.",
			410: "The page you are looking for has been permanently removed.",
			429: "You have made too many requests. Please try again later.",
			500: "Something went wrong.",
			503: "Something went wrong.",
		},
	},
} satisfies Record<string, unknown> & {
	bimTypes: Record<BimType, string>;
	billingStatuses: Record<BillingStatus, string>;
	cubeFaces: Record<CubeSide, string>;
	errorMessages: {
		whoops: string;
		goToProjects: string;
		errors: Record<string | number, string>;
	};
};

export const translations: Record<Lang, Translations> = {
	[Lang.en]: en,
	[Lang.de]: {
		bimTypes: {
			[BimType.world]: "Welt",
			[BimType.site]: "Standort",
			[BimType.plot]: "Grundstück",
			[BimType.building]: "Gebäude",
			[BimType.wing]: "Flügel",
			[BimType.fragment]: "Fragment",
			[BimType.load]: "Last",
			[BimType.level]: "Ebene",
			[BimType.corridor]: "Flur",
			[BimType.roof]: "Dach",
			[BimType.commercialSpace]: "Gewerbebereich",
			[BimType.apartment]: "Wohnung",
			[BimType.core]: "Kern",
			[BimType.wall]: "Fassadenwände",
			[BimType.door]: "Tür",
			[BimType.window]: "Fenster",
			[BimType.opening]: "Eröffnung",
			[BimType.floor]: "Boden",
			[BimType.ceiling]: "Decke",
			[BimType.room]: "Raum",
			[BimType.box]: "Box",
			[BimType.massingConfig]: "Massenplanung",
			[BimType.wingConfig]: "Flügelkonfiguration",
			[BimType.extrusion2D]: "2D-Extrusion",
			[BimType.genericTechnicalSpace]: "Generischer Technikbereich",
			[BimType.railing]: "Geländer",
			[BimType.parkingSpot]: "Stellplatz",
			[BimType.meta]: "Meta",
			[BimType.stairs]: "Treppen",
			[BimType.region]: "Bereich",
			[BimType.path]: "Weg",
			[BimType.lineParking]: "Längsparkplatz",
		},
		regionTypes: {
			[DefaultRegionType.Parking]: "Parkplatz",
			[DefaultRegionType.GreenSpace]: "Grünfläche",
			[DefaultRegionType.PavedArea]: "Befestigte Fläche",
			[DefaultRegionType.Recreation]: "Freizeit",
			[DefaultRegionType.ServicesUtilities]: "Versorgung/Technik",
		},
		cubeFaces: {
			[CubeSide.top]: "OBEN",
			[CubeSide.bottom]: "UNTEN",
			[CubeSide.left]: "LINKS",
			[CubeSide.right]: "RECHTS",
			[CubeSide.front]: "VORNE",
			[CubeSide.back]: "HINTEN",
		},
		displaySettings: {
			mapViewMode: "Kartenansichtsmodus",
			noMap: "Keine Karte",
			map2D: "2D-Karte",
			map2DOsmBuildings: "2D-Karte + OSM-Gebäude",
			map2DBasemapDe: "2D-Karte + basemap.de LoD2",
			map3D: "3D-Karte",
			camera: "Kamera",
			orthographic: "Parallel",
			perspective: "Perspektivisch",
			display: "Anzeige",
			loadParcels: "Flurstücke laden",
			loadParcelsHint: "ALKIS-Shapefile .zip – klicken oder hierher ziehen",
			opacity: "Deckkraft",
			outline: "Umriss",
			ambientOcclusion: "Umgebungsverdeckung",
		},
		underlays: {
			title: "Planunterlagen",
			upload: "PDF oder Bild hochladen",
			uploadHint: (limit: number) =>
				`PDF, PNG, JPG oder WebP bis ${limit} MB – oder auf die Karte ziehen`,
			uploading: (name: string) => `${name} wird vorbereitet…`,
			empty:
				"Laden Sie einen Bebauungsplan oder Lageplan hoch, um ihn unter Ihr Grundstück zu legen.",
			show: "Einblenden",
			hide: "Ausblenden",
			lock: "Sperren",
			unlock: "Entsperren",
			moreActions: "Weitere Aktionen",
			delete: "Löschen",
			uploadFailed: (name: string, reason: string) =>
				`${name} konnte nicht hochgeladen werden: ${reason}`,
			fileTooLarge: (limit: number) =>
				`Die Datei darf höchstens ${limit} MB groß sein`,
			unsupportedFile:
				"Nur PDF-, PNG-, JPG- und WebP-Dateien werden unterstützt",
		},
		navigation: {
			infoMode: "Info-Modus - Gebäudedaten anzeigen",
			settingsTooltip: "Einstellungen",
			generationTooltip: "Generierung",
			visibilityTooltip: "Sichtbarkeit",
			sunTooltip: "Sonnenposition",
			elementsTooltip: "Elemente",
			valuationTooltip: "Bewertung",
			enhanceImageTooltip: "Bild verbessern",
			exportTooltip: "Exportieren",
			variantsTooltip: "Varianten",
			variantsPanelTitle: "Varianten-Panel",
			variantsPanelDescription:
				"Designoptionen nach Effizienz, Abdeckung und GFA vergleichen und verwalten.",
			metricsTooltip: "Metriken",
			metricsPanelTitle: "Metriken",
			metricsPanelDescription:
				"Projektmetriken einschließlich Flächen, Verhältnisse und Einheitenverteilung anzeigen.",
			chatTooltip: "Chat",
			chatPanelTitle: "Chat",
			chatPanelDescription: "Stellen Sie Fragen zu Ihrem Projekt",
			paragraph34Tooltip: "§34 BauGB",
			paragraph34PanelTitle: "§34 BauGB",
			paragraph34PanelDescription:
				"Nachbarschaftliche Richtwerte (nur erstes Grundstück)",
			underlaysTooltip: "Planunterlagen",
			reportTooltip: "Bericht",
			warningsTooltip: "Warnungen",
			warningsPanelTitle: "Warnungen",
			warningsPanelDescription: "Validierungswarnungen des aktuellen Layouts",
			elements: "Elemente",
			variants: (layout: string) => `Von ${layout} abgeleitet`,
			layouts: "Layouts",
			sortBy: "Sortieren nach",
			compareFields: "Vergleichsfelder",
			duplicateLayout: "Layout duplizieren",
			renameLayout: "Layout umbenennen",
			pinVariant: "Als Layout speichern",
			removeLayout: "Layout entfernen",
			removeVariant: "Variante entfernen",
			variantFieldShortLabels: {
				gfa: "BGF",
				layoutEfficiency: "Effizienz",
				gsi: "GRZ",
				buildingFootprint: "Grundfläche",
				far: "GFZ",
				avr: "A/V",
				constructionCost: "Kosten",
			},
		},
		tools: {
			moreTools: "Weitere Werkzeuge",
			viewOnlyMode: "Ansichtsmodus",
			editMode: "Bearbeitungsmodus",
			commentMode: "Kommentarmodus",
			selectParcelAsSite: "Parzelle als neues Grundstück auswählen",
			drawNewSite: "Neues Grundstück zeichnen",
			cutSites: "Grundstücke schneiden",
			unionSites: "Grundstücke vereinen",
			deleteSite: "Grundstück löschen",
			editSite: "Grundstück bearbeiten",
			editSiteOffset: "Geländeversatz bearbeiten",
			editSiteSetback: "Standortabstände bearbeiten",
			addNonBuildableArea: "Bauverbotsbereich hinzufügen",
			deleteNonBuildableArea: "Bauverbotsbereich löschen",
			drawBuildableArea: "Baugrenze zeichnen",
			deleteBuildableArea: "Baugrenze löschen",
			addPath: "Weg hinzufügen",
			deletePath: "Weg löschen",
			pathWidth: "Breite",
			pathTurningRadius: "Kurvenradius",
			pathAffectsBuildingSetback: "Beeinflusst Gebäudeabstand",
			addLineParking: "Längsparkplatz hinzufügen",
			deleteLineParking: "Längsparkplatz löschen",
			lineParkingStallWidth: "Stellplatzbreite",
			lineParkingStallDepth: "Stellplatztiefe",
			lineParkingAccessDepth: "Fahrgassentiefe",
			lineParkingType: "Typ",
			lineParkingTilt: "Neigung",
			lineParkingFlipSide: "Seite wechseln",
			regionFill: "Füllung",
			regionType: "Bereichstyp",
			regionAffectsBuildingSetback: "Bereich beeinflusst Gebäudeabstand",
			unknownRegionType: "Unbekannt",
			addCustomRegionType: "Benutzerdefinierten Bereichstyp hinzufügen",
			customRegionTypePlaceholder: "Benutzerdefinierter Typname",
			addNewBuilding: "Neues Gebäude hinzufügen",
			undo: "Rückgängig",
			enhancementStarted: "Bildverbesserung gestartet",
			enhancementComplete: "Bildverbesserung abgeschlossen",
			redo: "Wiederholen",
			deleteBuilding: {
				enabled: "Gebäude löschen",
				disabled: "Nur gespeicherte Gebäude können gelöscht werden",
			},
			moveRotateBuilding: "Gebäude verschieben/drehen",
			changeWingsLength: "Flügellänge ändern",
			addNewWings: {
				enabled: "Neue Flügel hinzufügen",
				disabled:
					"Flügel können zu dieser Gebäudeform nicht hinzugefügt werden",
			},
			editLevels: "Geschosse bearbeiten",
			editFragmentLevels: "Geschosse pro Fragment bearbeiten",
			editFragmentBoundaries: {
				enabled: "Fragmentgrenzen bearbeiten",
				disabled: "Nur Flügel mit mehreren Fragmenten haben Grenzen",
			},
			flipLoad: {
				enabled: "Erschließung wenden",
				disabled: "Nur Laubengang kann die Richtung flippen",
			},
			coreSide: {
				enabled: "Kernseite ändern",
				disabled: "Nur Gebäude mit Flur haben zwei Seiten für Kerne",
			},
			cornerCoreSide: {
				enabled: "Eckkernseite ändern",
				disabled: "Nur versetzte Ecken haben eine Eckkernseite zum Ändern",
			},
			editCore: "Kern bearbeiten",
			floorplanEditor: "Grundriss-Editor",
			autoRotate: "Kamera automatisch drehen",
			focusCamera: "Kamera zentrieren",
			orientateCamera: "Kamera ausrichten",
			captureCurrentView: "Aktuelle Ansicht aufnehmen",
			captureAndEnhance: "Aufnehmen und Verbessern",
			enhanceImage: "Bild verbessern",
			imageHistory: "Bildverlauf",
			clear: "Löschen",
			openInNewTab: "In neuem Tab öffnen",
			downloadImage: "Bild herunterladen",
			enhancing: "Wird verbessert",
			enhanced: "Verbessert",
			original: "Original",
			failed: "Fehlgeschlagen",
			loadMore: "Mehr laden",
			deleteImage: "Bild löschen",
			generateVideo: "Video generieren",
			viewVideo: "Video ansehen",
			enhancementOptions: "Verbesserungsoptionen",
			solarPanels: "Solarpaneele",
			landscaping: "Landschaftsgestaltung",
			whiteModel: "Weißmodell",
			customEnhancement: "Benutzerdefinierte Verbesserung",
			customPromptPlaceholder: "Beschreiben Sie, wie es aussehen soll...",
			generationSteps: {
				enhance: "Wird verbessert",
				customPrompt: "Verbesserungen werden angewendet",
			},
			stepMayTakeMinutes: "Dieser Schritt kann einige Minuten dauern",
			cancelOperation: "Abbrechen",
			finishOperation: "Beenden",
			measureDistance: "Messen",
		},
		errors: {
			canvasNotFound:
				"Canvas nicht gefunden. Bitte stellen Sie sicher, dass die 3D-Ansicht geladen ist.",
			imageEnhancementFailed: "Bildverbesserung fehlgeschlagen.",
			imageDeleteFailed:
				"Bild konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.",
			liveSessionFailed: "Verbindung zur Live-Sitzung fehlgeschlagen",
			liveSessionDisconnected:
				"Die Live-Sitzung wurde beendet und konnte sich nicht wieder verbinden, daher werden Änderungen ab jetzt nicht gespeichert. Laden Sie die Seite neu; wenn es weiterhin fehlschlägt, prüfen Sie Ihren Zugriff auf dieses Projekt und den Plan seiner Organisation.",
		},
		feedback: {
			tooltip: "Feedback",
			title: "Gib uns dein Feedback",
			description:
				"Gab es Fehlermeldungen? Hast du Feature-Requests? Sende uns eine Nachricht und wir melden uns bei dir.",
			message: "Nachricht",
			send: "Abschicken",
			sending: "Wird gesendet",
			cancel: "Abbrechen",
			error:
				"Es gab Probleme beim senden deiner Nachricht, bitte versuche es später noch einmal.",
			success: "Deine Nachricht wurde erfolgreich gesendet.",
			ok: "Ok",
		},
		contact: {
			title: "Kontakt",
			description:
				"Wenn Sie Fragen oder Feedback haben oder Unterstützung benötigen, kontaktieren Sie uns bitte.",
			message: "Nachricht",
			send: "Abschicken",
			sending: "Wird gesendet",
			cancel: "Abbrechen",
			error:
				"Es gab Probleme beim senden deiner Nachricht, bitte versuche es später noch einmal.",
			success: "Deine Nachricht wurde erfolgreich gesendet.",
			ok: "Ok",
		},
		chatPanel: {
			newChat: "Neuer Chat",
			deleteChat: "Chat löschen",
		},
		buildingSettings: {
			title: "Gebäudeeinstellungen",
			levels: "Geschosse",
			global: "Global",
			building: "Gebäude",
		},
		generationPanel: {
			generation: "Generierung",
			target: "Ziel",
			wingLengthRange: "Längenbereich",
			wingLengthDescription: "Minimale und maximale Länge für Gebäudeflügel",
			wingMinLength: "Min. Länge",
			wingMinLengthDescription: "Minimale Länge für Gebäudeflügel",
			wingMaxLength: "Max. Länge",
			wingMaxLengthDescription: "Maximale Länge für Gebäudeflügel",
			maxFragmentLength: "Max. Regelabschnitt",
			maxFragmentLengthDescription:
				"Maximale Länge jedes Abschnitts, auch der Ecken, sofern sie keine eigene Grenze haben",
			maxCornerFragmentLength: "Max. Eckabschnitt",
			maxCornerFragmentLengthDescription:
				"Eigene maximale Länge für Eckabschnitte, gemessen ab der Außenfassade des querenden Flügels",
			corridorEndApartments: "Wohnungen am Flurende",
			corridorEndApartmentsDescription:
				"Eine Wohnung darf den Flur an den Gebäudeenden abschließen. Aus lässt den Flur bis zur Stirnfassade offen, z. B. für ein Flurfenster.",
			shiftedCorners: "Versetzte Ecken",
			shiftedCornersDescription:
				"L-Ecken von Flügeln mit Flur werden mit den Eckwohnungen des Sets um einen aus der Ecke versetzten Kern gefüllt. Aus plant Ecken wie den Rest des Flügels.",
			wingMinSetback: "Min. Abstand",
			wingMinSetbackDescription:
				"Mindestens reservierter Abstand auf jeder Gebäudeseite",
			wingSetbackFactor: "Abstandsfaktor",
			wingSetbackFactorDescription:
				"Wird mit der Gebäudehöhe multipliziert, um den Abstand zu berechnen",
			balconiesAffectMassing: "Balkone beeinflussen die Baumasse",
			balconiesAffectMassingDescription:
				"Bei der Generierung den durch Balkone vergrößerten Abstand reservieren statt des Basisabstands",
			avoidNorthFactor: "Nordausrichtung vermeiden",
			avoidNorthFactorDescription:
				"Wie stark die Generierung Flügel mit nach Norden (±30°) ausgerichteten Wohnungen vermeidet. 0 = aus, 1 = immer vermeiden",
			corridorWidth: "Flurbreite",
			corridorWidthDescription: "Standard-Flurbreite für alle Gebäude",
			globalBuildingHeight: "Max. Geschosse",
			levels: "Geschosse",
			globalBuildingHeightDescription:
				"Maximale Anzahl der Geschosse für alle generierten Gebäude",
			unitHeight: "Höhe",
			unitHeightDescription: "Einheitshöhe für Wohneinheiten",
			unitDepth: "Tiefe",
			unitDepthDescription: "Einheitentiefe für Wohneinheiten",
			unitMix: "Wohnungsmix",
			unitMixEnabled: "Aktiviert",
			lockRatio: "Anteil sperren",
			unlockRatio: "Anteil entsperren",
			resetToEqualDistribution: "Gleichmäßig verteilen",
			commercialSection: "Gewerbebereich",
			commercialSectionDescription:
				"Fügt einen Gewerbebereich im Erdgeschoss mit der angegebenen Höhe hinzu",
			commercialHeight: "Gewerbehöhe",
			site: "Grundstück",
			maxGsi: "Max. GRZ",
			maxGsiDescription:
				"Maximaler Anteil der Grundstücksfläche, der bebaut werden darf.",
			maxAncillaryGsi: "Max. GRZ II",
			maxAncillaryGsiDescription:
				"Maximaler Anteil der Grundstücksfläche, der durch Gebäude und versiegelte Nebenanlagen — Stellplätze, befestigte Flächen, Versorgungsflächen und Wege — bedeckt werden darf. Ausgeschaltet gilt die gesetzliche Regel: Max. GRZ × 1,5, höchstens 0,8. Erfordert Max. GRZ — ohne Hauptlimit gibt es nichts zu überschreiten.",
			farValue: "Max. GFZ",
			farValueDescription:
				"Ziel-Geschossflächenzahl. Bei aktiviertem Limit werden Geschosse hinzugefügt, bis der Zielwert erreicht ist – begrenzt durch die maximale Geschosszahl. Ausgeschaltet gilt kein GFZ-Limit.",
			noLimit: "Kein Limit",
			loadType: "Erschließungstyp",
			loadTypeDouble: "Flur",
			loadTypeSingle: "Laubengang",
			loadTypeCorner: "Ecke",
			buildingGroup: "Gebäude",
			wingsGroup: "Flügel",
			unitsGroup: "Einheiten",
			bimObjects: "BIM-Objekte",
			bimObjectThicknessDescription:
				"Dicke in Metern, die bei der Generierung dieses Elements verwendet wird. Wird mit der Größe der zugeordneten Revit-Familie synchronisiert.",
			gapFillingGroup: "Lückenfüllung",
			gapFillingDoubleLoadOnly: "Nur für Flurgebäude",
			fragmentGapFillMethod: "Methode",
			fragmentGapFillMethodDescription:
				"Wie Lücken geschlossen werden, die nach dem Platzieren der Wohnungen in einem Abschnitt bleiben:\n• Größte Einheit erweitern – verbreitert die größte Wohnung, die in die Lücke wachsen kann.\n• Kleinste Einheit erweitern – verbreitert die kleinste Wohnung, die in die Lücke wachsen kann.\n• Gleichmäßig verteilen – verteilt die Lücke gleichmäßig auf alle Wohnungen, die in sie wachsen können.\n• Technikbereiche – füllt die Lücke mit Technikbereichen.\n• Keine – überlässt die Lücke den folgenden Schritten.\nVerbleibende Lücken werden durch Erweitern des Eckkerns (falls aktiviert) und danach mit generischen Technikbereichen gefüllt.",
			fragmentGapFillMethods: {
				[FragmentGapFillMethod.expandLargestUnit]: "Größte Einheit erweitern",
				[FragmentGapFillMethod.expandSmallestUnit]:
					"Kleinste Einheit erweitern",
				[FragmentGapFillMethod.distributeEqually]: "Gleichmäßig verteilen",
				[FragmentGapFillMethod.technicalSpaces]: "Technikbereiche",
				[FragmentGapFillMethod.none]: "Keine",
			},
			resizeCornerCores: "Eckkerne erweitern",
			resizeCornerCoresDescription:
				"Bleiben nach der Lückenfüllung noch Lücken, wird versucht, sie durch Erweitern des Eckkerns zu füllen. Der Kern kann sich nur in eine Richtung erweitern – in Richtung der größeren Lücke. Danach noch verbleibende Lücken werden mit generischen Technikbereichen gefüllt. Bei der Methode Technikbereiche immer deaktiviert.",
			parkingGroup: "Parken",
			parkingFactors: "Stellplatzschlüssel",
			parkingFactorsInfo:
				"Stellplatzschlüssel werden in dieser Reihenfolge angewendet: Wohnungstyp, dann Wohnflächenbereich, dann die verbleibende Gesamtfläche. Jede Wohnung wird von der ersten passenden Zeile gezählt — n ist die Anzahl der Wohnungen, die eine Zeile zählt. Flächenbereiche beziehen sich auf die Wohnfläche und schließen ihre Untergrenze aus, ihre Obergrenze aber ein, etwa kleine/mittlere/große Wohnungen einer kommunalen Stellplatzsatzung. Die Gesamtfläche gilt je einstellbarer Bezugsfläche für die Wohnfläche der Wohnungen, die nicht von vorherigen Schlüsseln gezählt wurden. Bitte prüfen Sie die Vorschriften Ihrer Gemeinde, um die korrekten Werte für Ihr Projekt zu ermitteln.",
			parkingFactorsInfoLabel: "Über Stellplatzschlüssel",
			parkingFactorApartmentType: "Wohnungstyp",
			parkingFactorApartmentArea: "Wohnflächenbereich",
			parkingFactorTotalArea: "Gesamtfläche",
			parkingAddFactor: "Schlüssel hinzufügen",
			parkingRemoveFactor: "Schlüssel entfernen",
			parkingRemoveRow: "Zeile entfernen",
			parkingAddType: "Typ hinzufügen",
			parkingAddRange: "Bereich hinzufügen",
			parkingType: "Typ",
			parkingSelectType: "Typ wählen",
			parkingUnknownApartmentType:
				"Der Wohnungstyp einer Zeile ist nicht im aktuellen Wohnungsset. Sie zählt keine Wohnungen, bis Sie einen Typ wählen.",
			parkingMinArea: "(Min",
			parkingMaxArea: "Max]",
			parkingAreaBasis: "Je",
			parkingRemainingArea: "Fläche",
			parkingCount: "n",
			parkingSpaces: "Stpl.",
			parkingSpacesPerApartmentTypeDescription:
				"Anzahl der erforderlichen Stellplätze je Wohnung dieses Typs.",
			parkingSpacesPerAreaRangeDescription:
				"Anzahl der erforderlichen Stellplätze je Wohnung innerhalb dieses Wohnflächenbereichs.",
			parkingSpacesPerFloorAreaDescription:
				"Anzahl der erforderlichen Stellplätze je Bezugsfläche der verbleibenden Wohnfläche.",
			parkingAllApartmentTypesAdded:
				"Alle Wohnungstypen sind bereits enthalten.",
			parkingAreaRangeCovered:
				"Die Bereiche erreichen bereits die maximale Wohnungsfläche. Senken Sie die höchste Obergrenze, um einen weiteren Bereich hinzuzufügen.",
			parkingInvalidRange:
				"Das Max eines Bereichs muss größer als sein Min sein.",
			parkingOverlappingRanges:
				"Bereiche überschneiden sich. Wohnungen in der Überschneidung werden nur vom ersten passenden Bereich gezählt.",
			parkingIncompleteRanges: (maxArea: number) =>
				`Die Bereiche decken nicht alle Wohnungsflächen von 0 bis ${maxArea} m² ab. Wohnungen außerhalb davon fallen in den Gesamtflächen-Schlüssel oder werden nicht gezählt.`,
			parkingMultiplier: "Multiplikator",
			parkingMultiplierDescription:
				"Multiplikator, der den Stellplatzbedarf an den Standort anpasst, etwa an ÖPNV-Anbindung, Zentralität, Parkdruck, Carsharing oder ein Mobilitätskonzept.",
			requiredParkingSpots: "Benötigte Stellplätze",
			requiredParkingSpotsDescription:
				"Summe der Stellplätze aller Schlüssel, angepasst mit dem Multiplikator.",
			totalParkingSpots: "Gesamtzahl der Stellplätze",
			totalParkingSpotsDescription: "Gesamtzahl der verfügbaren Stellplätze.",
			apartmentTypes: "Wohnungstypen",
			newType: "Neuer Typ",
			editType: "Typ bearbeiten",
			removeType: "Typ entfernen",
			name: "Name",
			rooms: "Zimmer",
			inhabitants: "Einwohner",
			width: "Breite",
			depth: "Tiefe",
			cancel: "Abbrechen",
			save: "Speichern",
		},
		targets: {
			suggested: "§34 BauGB",
			useSuggested: "Vorschlag übernehmen",
		},
		hierarchyPanel: {
			site: "Grundstück",
			building: "Gebäude",
		},
		visibilityPanel: {
			elementVisibility: "Element-Sichtbarkeit",
			site: "Grundstück",
			outerBuilding: "Gebäude",
			units: "Einheiten",
			unitElements: "Einheitselemente",
			resetToDefaults: "Auf Standardwerte zurücksetzen",
			buildingSetbacks: "Gebäudeabstände",
			plotSetbacks: "Grundstücksabstände",
			warnings: "Warnungen",
			dimensions: "Abmessungen",
			logo: "Logo der Organisation",
			streets: "Straßen",
			general: "Allgemein",
			facade: "Fassade",
			structuralFloors: "Strukturelle Böden",
			finishFloors: "Fertigböden",
		},
		sunPanel: {
			resetToDefaults: "Auf Standardwerte zurücksetzen",
			date: "Datum",
			time: "Uhrzeit",
			shadows: "Schatten",
			sunHoursAnalysis: "Sonnenstunden-Analyse",
			daylightPotential: "Tageslichtpotenzial",
			solarRadiation: "Solarstrahlung",
			sunHoursAnalysisCaption: "Stunden direkter, unverschatteter Sonne",
			sunHoursAnalysisDescription:
				"Stunden direkter Sonne am gewählten Datum, einschließlich der Verschattung durch das Projekt selbst und durch erfasste Gebäude im Umkreis von 500 m. Die Skala endet bei 9 Stunden; alles darüber erscheint in der obersten Farbe.",
			daylightPotentialCaption: "Sichtbarer Himmelsanteil (%)",
			daylightPotentialDescription:
				"Wie viel Himmel jede Fläche sieht, gewichtet für einen bedeckten Himmel und bezogen auf eine unverschattete horizontale Fläche — der Vertical Sky Component aus den BRE-Tageslichtrichtlinien. Ein Flachdach erreicht 100 %, eine Fassade ohne Hindernis davor etwa 40 %, dort endet die Skala. Ab 27 % gilt das Tageslicht für ein Fenster nach BRE als ausreichend.",
			solarRadiationCaption: "Energieeintrag pro Jahr (kWh/m²)",
			solarRadiationDescription:
				"Solarenergie, die im Lauf eines Jahres auf jede Fläche trifft, einschließlich der Verschattung durch das Projekt selbst und durch erfasste Gebäude im Umkreis von 500 m. Das Skalenmaximum entspricht dem, was ein optimal geneigtes Modul an diesem Standort empfangen könnte.\n\nWetterdaten: PVGIS © European Communities, 2001-2026.",
			hours: "Stunden",
			percentOfSky: "% Himmel",
			kWhPerSqm: "kWh/m²",
			analysisFailed: "Analyse fehlgeschlagen. Bitte erneut versuchen.",
			total: "Gesamt",
			facade: "Fassade",
			roof: "Dach",
			ground: "Boden",
		},
		valuationPanel: {
			valuation: "Bewertung",
			total: "Gesamt",
			view: "Ansicht",
			project: "Projekt",
			statistics: "Statistiken",
			livingArea: "Wohnfläche",
			facadeArea: "Fassadenfläche",
			windowArea: "Fensterfläche",
			buildingYear: "Baujahr",
			buildingsYear: "Baujahr",
			defaultBuildingYear: "Standardbaujahr",
			buildingYearHelperDefault: "Standardbaujahr für neue Gebäude",
			buildingYearPastIndicator: "Vergangenes Jahr",
			buildingYearPresentIndicator: "Aktuelles Jahr",
			residentialUnits: "Wohneinheiten",
			buildings: "Gebäude",
			constructionCost: "Baukosten",
			totalCost: "Gesamtkosten",
			marketValue: "Marktwert",
			price: "Preis",
			salePrice: "Verkaufspreis",
			priceRange: "Preisspanne",
			priceRangeMin: "Preisspanne Min",
			priceRangeMax: "Preisspanne Max",
			rentPrice: "Mietpreis",
			netRent: "Nettomiete",
			rentRange: "Mietpreisspanne",
			rentRangeMin: "Mietpreisspanne Min",
			rentRangeMax: "Mietpreisspanne Max",
			valuationUpdated: "Bewertung aktualisiert:",
			confidence: "Vertrauen:",
			// New Valuation Section translations
			projectGeometry: "Projektgeometrie",
			residentialArea: "Wohnfläche (Wfl)",
			residentialAreaHelper: "Wohnfläche - Wohnbereich",
			grossFloorArea: "Brutto-Grundfläche (BGF)",
			grossFloorAreaHelper: "Brutto-Grundfläche - Gesamtfläche",
			footprint: "Grundfläche",
			footprintHelper: "Gebäudegrundfläche",
			apartments: "Wohnungen",
			levels: "Ebenen",
			constructionCosts: "Baukosten",
			basebuild: "Rohbau",
			electrical: "Elektrik",
			hvac: "HLS",
			hvacFullName: "Heizung, Lüftung, Sanitär",
			shellConstruction: "Rohbau",
			siteSetup: "Baustelleneinrichtung",
			drainage: "Entwässerung",
			facade: "Fassade",
			costsPerSqmResidential: "Kosten pro m² (Wohnfläche)",
			ancillaryCosts: "Baunebenkosten (BNK)",
			costsPerItem: "Kosten pro Einheit",
			lift: "Aufzug",
			doors: "Türen",
			bathrooms: "Bäder",
			balcony: "Balkon",
			parking: "Stellplätze",
			costsPerSqmFootprint: "Kosten pro m² (Grundfläche)",
			roof: "Dach",
			cellar: "Keller",
			totalConstructionCost: "Gesamte Baukosten",
			totalProjectCosts: "Gesamte Projektkosten",
			landCostPerSqm: "Grundstückskosten pro m²",
			landCostTotal: "Gesamte Grundstückskosten",
			landCostTotalHelper:
				"Gesamtbetrag bearbeiten, um Preis pro m² zu berechnen",
			sideCosts: "Nebenkosten",
			sideCostsHelper: "Genehmigungen, Gebühren, etc. (% der Baukosten)",
			totalCostsPreFinance: "Gesamtkosten (vor Finanzierung)",
			financing: "Finanzierung",
			buildTime: "Bauzeit",
			equityShare: "Eigenkapitalanteil",
			loanInterestRate: "Darlehenszins",
			loanShare: "Darlehensanteil",
			equity: "Eigenkapital",
			loan: "Darlehen",
			totalInterestCost: "Gesamte Zinskosten",
			totalInterestCostHelper:
				"Progressive Finanzierung - Mittel werden gleichmäßig über die Bauzeit abgerufen. Zinsen auf durchschnittlichen Kreditsaldo berechnet (50% des Gesamtkredits)",
			totalProjectCostAllIn: "Gesamte Projektkosten (All-in)",
			revenueAssumptions: "Erlösannahmen",
			salesPricePerSqm: "Verkaufspreis pro m²",
			salesPriceOverride: "Preis überschreiben",
			totalSalesValue: "Gesamter Verkaufswert",
			summaryResults: "Zusammenfassung & Ergebnisse",
			netProfit: "Nettogewinn",
			equityInvested: "Investiertes Eigenkapital",
			returnOnEquity: "Eigenkapitalrendite (ROE)",
			returnOnEquityHelper: "Nettogewinn / Investiertes Eigenkapital",
			returnOnCost: "Kostenrendite (ROC)",
			returnOnCostHelper: "Nettogewinn / Gesamtprojektkosten",
		},
		metricsPanel: {
			projectOverview: "Standortmetriken",
			projectOverviewDescription:
				"Grundlegende projektweite Werte für Grundstück und Wohnungsanzahl.",
			buildingArea: "Gebäudefläche",
			buildingAreaDescription:
				"Gesamte Bebauung, Grundfläche und nutzbare Innenfläche.",
			developmentRatios: "Entwicklungsverhältnisse",
			developmentRatiosDescription:
				"Leistungskennzahlen abgeleitet aus dem Gebäudelayout.",
			regionAreasDescription:
				"Gesamtfläche der Bauverbotsbereiche und Aufschlüsselung nach Typ. Jeder Typ zeigt seinen Anteil am Grundstück und an der gesamten ausgewiesenen Fläche. Überlappende Typen können zusammen mehr als 100 % ergeben.",
			noBuildRegions: "Bauverbotsbereiche",
			area: "Fläche",
			totalRegionArea: "Gesamte ausgewiesene Fläche",
			siteAreaFraction: "Grundstücksflächenanteil",
			shareOfTotalDesignatedArea: "Anteil an der gesamten ausgewiesenen Fläche",
			noWorldLoaded: "Keine Welt geladen.",
			unitMixDistribution: "Wohnungsmix-Verteilung",
			unitMixDistributionDescription:
				"Aufschlüsselung der Wohnungstypen nach Prozentsatz und Anzahl.",
			siteArea: "Grundstücksfläche",
			siteAreaDescription: "Gesamtfläche des Grundstücks",
			apartments: "Wohnungen",
			apartmentsDescription: "Gesamtanzahl der Wohnungen",
			buildingsDescription: "Gesamtanzahl der Gebäude",
			buildingFootprint: "Gebäudegrundfläche",
			buildingFootprintDescription: "Gesamte Gebäudegrundfläche",
			layoutEfficiency: "Layout-Effizienz",
			layoutEfficiencyDescription:
				"Verhältnis der Netto-Nutzfläche zur Brutto-Grundfläche (inkl. Flure, Wände, Schächte)",
			gsi: "Grundflächenzahl (GRZ)",
			gsiDescription: "Verhältnis der Gebäudegrundfläche zur Grundstücksfläche",
			ancillaryGsi: "Grundflächenzahl II (GRZ II)",
			ancillaryGsiDescription:
				"Verhältnis der Gebäudegrundfläche plus versiegelter Nebenanlagen zur Grundstücksfläche",
			grossFloorArea: "Brutto-Grundfläche (BGF)",
			grossFloorAreaDescription:
				"Gesamte Geschossfläche aller Stockwerke (inkl. Wände, Flure, Schächte)",
			netInternalArea: "Netto-Nutzfläche (NNF)",
			netInternalAreaDescription:
				"Gesamte nutzbare Fläche innerhalb der Räume (ohne Wände, Flure, Schächte)",
			livingArea: "Wohnfläche (WoFlV)",
			livingAreaDescription:
				"Wohnfläche nach Wohnflächenverordnung (§ 4 WoFlV): Wohnungsgrundfläche zu 100 %, Balkone zu 25 %. Keine Abzüge für Raumhöhen.",
			floorAreaRatio: "Geschossflächenzahl (GFZ)",
			floorAreaRatioDescription:
				"Verhältnis der Brutto-Grundfläche zur Grundstücksfläche",
			areaToVolumeRatio: "Flächen-Volumen-Verhältnis (A/V)",
			areaToVolumeRatioDescription:
				"Verhältnis der Gebäudehüllfläche zum Gebäudevolumen",
			unitMixDescription: "Wohnungsanzahl nach Schlafzimmern und Bewohnern",
			layoutEfficiencyPercent: "Layout-Effizienz (%)",
			layoutEfficiencyTooltip:
				"Layout-Effizienz: Verhältnis der Netto-Nutzfläche zur Brutto-Grundfläche.\\n\\nNetto-Nutzfläche (NNF): Gesamte nutzbare Fläche innerhalb der Räume (ohne Wände, Flure, Schächte)\\n\\nBrutto-Grundfläche (BGF): Gesamte Geschossfläche aller Stockwerke (inkl. Wände, Flure, Schächte)",
			buildingFootprintSqm: "Gebäudegrundfläche (m²)",
			buildingFootprintTooltip:
				"Gesamtfläche, die von Gebäuden auf Bodenhöhe bedeckt ist",
			grossFloorAreaSqm: "Brutto-Grundfläche (m²)",
			grossFloorAreaTooltip:
				"Gesamte Geschossfläche aller Stockwerke (inkl. Wände, Flure, Schächte)",
			areaToVolumeRatioTooltip:
				"Verhältnis der Gebäudehüllfläche zum Gebäudevolumen",
			variantCount: "Variantenanzahl",
			providedRequiredParking: "Stellplätze (bereitgestellte / benötigte)",
			providedRequiredParkingDescription:
				"Gesamtzahl der bereitgestellten Stellplätze im Vergleich zur erforderlichen Anzahl gemäß Stellplatzschlüsseln und Multiplikator.",
			parking: "Stellplätze",
			parkingDescription:
				"Stellplätze und die von ihnen bedeckte Fläche, aufgeteilt in Straßenparken und Parkplätze.",
			streetParking: "Straßenparken",
			carParks: "Parkplätze",
			spotsAvailable: "Verfügbare Stellplätze",
			total: "Gesamt",
			streetParkingAreaDescription: "Vom Straßenparken bedeckte Grundfläche",
			streetParkingSpotsDescription: "Anzahl der Straßenstellplätze",
			carParkAreaDescription: "Von Parkplatzbereichen bedeckte Grundfläche",
			carParkSpotsDescription:
				"Anzahl der Stellplätze innerhalb von Parkplatzbereichen",
			totalParkingAreaDescription:
				"Von Straßenparken und Parkplätzen bedeckte Grundfläche",
			pavedSurfaces: "Befestigte Flächen",
			pavedSurfacesDescription:
				"Durch Wege, Stellplätze und befestigte Bereiche versiegelte Grundfläche.",
			paths: "Wege",
			pathsAreaDescription: "Von Wegen bedeckte Grundfläche",
			pavedRegions: "Befestigte Bereiche",
			pavedRegionAreaDescription:
				"Von befestigten Bereichen bedeckte Grundfläche",
			totalPavedAreaDescription:
				"Gesamtfläche von Wegen, Stellplätzen und befestigten Bereichen",
		},
		paragraph34Panel: {
			far: "GFZ",
			gsi: "GRZ",
			height: "Höhe",
			noData: "Keine Daten. Bitte Grundstück und Parzellen laden.",
			uploadedParcelsOnly:
				"§34 benötigt die umliegenden Flurstücke. Hochgeladene Flurstücke decken nur das Grundstück ab und werden daher nicht verwendet.",
			loadingBuildings: "Gebäude werden geladen…",
			withinLimits: "im Rahmen",
			parcels: "Parzellen",
			parcel: "Parzelle",
			referenceLabel: "Referenz",
			selectParcelHint: "Parzelle anklicken, um sie als Referenz zu verwenden.",
			pickOnMap: "Auf Karte wählen",
			pickOnMapCancel: "Abbrechen",
			pickOnMapHint:
				"Beliebige Parzelle in der Szene anklicken, um sie als Referenz zu verwenden. Zum Vorschauen darüberfahren.",
			manualReferenceInfo:
				"ist die Referenz — ihre GFZ, GRZ und Höhe bestimmen die §34-Grenzwerte und Zielvorschläge.",
			resetReference: "Auf Auto zurücksetzen",
			legendTitle: "Farblegende",
			legendInRange: "Auf oder unter Ref — im Rahmen von §34",
			legendAboveMax: "Über Ref — überschreitet Referenzparzelle",
			infoTitle: "So werden §34-Zielwerte berechnet",
			infoNeighbours:
				"Nachbarn: Kataster-Parzellen, deren Umriss höchstens 200 m vom Umriss der Grundstücksparzelle entfernt ist, bilden den Kontext.",
			infoFilter:
				"Filter: leere Parzellen (ohne Gebäude) und statistische Ausreißer bei GFZ und Höhe werden entfernt.",
			infoReference:
				"Referenzparzelle: unter den verbleibenden Nachbarn diejenige mit der höchsten GFZ. Ihre GFZ, GRZ und Höhe bilden das Ref (Obergrenze).",
			infoCompliance:
				"Das Projekt liegt im Rahmen von §34, wenn jede Kennzahl auf oder unter Ref bleibt.",
		},
		report: {
			title: "Projektbericht",
			noBuildings: "Keine Gebäude in diesem Layout",
			noBuildingsDescription:
				"Ohne Einheiten kann kein Bericht erstellt werden. Erstellen Sie zuerst ein Gebäude.",
			generating: "Bericht wird erstellt...",
			reportDate: "Berichtsdatum",
			developmentSummary: "Entwicklungsübersicht",
			basis: "Grundlage",
			basisNote:
				"Die Zahlen basieren auf dem aktiven Layout und den im Projekt festgelegten Bewertungsannahmen.",
			siteAndDesignMetrics: "Standort- und Entwurfskennzahlen",
			siteAndDesignMetricsSubtitle:
				"Was der Standort ermöglicht, was der Entwurf leistet und wie effizient die bebaute Fläche genutzt wird.",
			costAndRevenueModel: "Kosten- und Erlösmodell",
			costAndRevenueModelSubtitle:
				"Aufschlüsselung der Baukosten, Projektkosten, Finanzierung und die daraus resultierende Rendite.",
			feasibilityReport: "Machbarkeitsbericht",
			feasibilityStudy: "Machbarkeitsstudie",
			coverSubtitle:
				"Potenzialanalyse für die Entwicklung von hochwertigem Wohnraum in urbaner Lage.",
			massingTopView: "Baumassen — Draufsicht",
			estimate: "Schätzwert",
			range: "Spanne",
			perSqmLivingArea: "Je m² Wohnfläche",
			monthly: "monatlich",
			subtotalPreFinance: "Zwischensumme vor Finanzierung",
			priceSourceMarket: "Marktschätzung",
			priceSourceManual: "manuell",
			page: "Seite",
			printReport: "Bericht drucken",
			units: "Einheiten",
			share: "Anteil",
			region: "Bereich",
			area: "Fläche",
			ofSite: "Am Grundstück",
			shareOfDesignatedArea: "Anteil der ausgewiesenen Fläche",
			item: "Kostengruppe / Position",
			rate: "Ansatz / Einheit",
			amount: "Gesamt",
			unitApartment: "WE",
			unitLevel: "Ebene",
			unitYears: "J.",
			unitFootprint: "m² GF",
			disclaimer: "Haftungsausschluss & Reliance",
			disclaimerBasisTitle: "Daten- und Methodengrundlage",
			disclaimerBasis1:
				"Die vorliegende Machbarkeitsstudie wurde nach bestem Wissen und Gewissen sowie mit der üblichen Sorgfalt erstellt. Sämtliche Analysen, Bewertungen, Kennzahlen, Prognosen und Schlussfolgerungen beruhen auf den zum Zeitpunkt der Erstellung verfügbaren Informationen, insbesondere auf Unterlagen und Angaben des Auftraggebers, öffentlich zugänglichen Quellen sowie – soweit eingesetzt – auf datenbasierten Auswertungen unter Nutzung von Softwarelösungen und Methoden der Künstlichen Intelligenz (KI).",
			disclaimerBasis2:
				"Eine eigenständige, vollumfängliche Prüfung sämtlicher Grundlagen, Datenquellen und Annahmen im Sinne einer technischen, wirtschaftlichen, steuerlichen oder rechtlichen Due Diligence wurde nicht geschuldet und nicht durchgeführt. Es erfolgte ausschließlich eine sorgfältige Plausibilisierung im Rahmen einer überschlägigen, indikativ ausgerichteten Betrachtung.",
			disclaimerWarrantyTitle: "Gewährleistungsausschluss",
			disclaimerWarranty1:
				"Trotz sorgfältiger Vorgehensweise kann keine Gewähr für die Vollständigkeit, Richtigkeit und Aktualität der verwendeten Daten, der zugrunde gelegten Annahmen sowie der daraus abgeleiteten Ergebnisse übernommen werden. Dies gilt insbesondere vor dem Hintergrund, dass (i) Teile der Inhalte auf probabilistischen und algorithmischen Verfahren, einschließlich KI-gestützter Modelle, beruhen, die systemimmanenten Unsicherheiten und Fehlerquellen unterliegen, und (ii) sich Rahmenbedingungen (z. B. Markt-, Kosten-, Zins-, Rechts- oder Förderkulisse) jederzeit ändern können.",
			disclaimerWarranty2:
				"Die in dieser Machbarkeitsstudie enthaltenen Aussagen - insbesondere zu Kosten, Terminen, Erlösen, Wirtschaftlichkeit, Risiken oder Realisierbarkeit - stellen daher unverbindliche Einschätzungen zum Zeitpunkt der Erstellung dar und sind ausdrücklich nicht als Zusage, Garantie oder Beschaffenheitsvereinbarung zu verstehen.",
			disclaimerUseTitle: "Verwendungszweck & Haftung",
			disclaimerUse1:
				"Die Machbarkeitsstudie dient ausschließlich der Orientierung und Vorbereitung weiterer Überlegungen des Auftraggebers. Sie ersetzt weder eine vertiefte technische, rechtliche, steuerliche oder wirtschaftliche Fachberatung noch eine projektspezifische Detailplanung. Der Auftraggeber bleibt verpflichtet, auf Grundlage eigener Prüfung sowie gegebenenfalls ergänzender Gutachten und Beraterleistungen eigenverantwortliche Entscheidungen zu treffen. Eine unmittelbare oder mittelbare Verwendung der Ergebnisse für Investitions-, Finanzierungs-, Vergabe- oder Erwerbsentscheidungen erfolgt auf eigenes Risiko des Auftraggebers.",
			disclaimerUse2:
				"Jegliche Haftung des Auftragnehmers für die inhaltliche Richtigkeit, Vollständigkeit und Aktualität der Machbarkeitsstudie, ihrer Grundlagen und Annahmen sowie für daraus abgeleitete Entscheidungen oder unterlassene Entscheidungen des Auftraggebers oder Dritter wird - soweit gesetzlich zulässig - ausgeschlossen. Insbesondere werden Ansprüche aus sogenannter „Reliance“ (Vertrauen auf die in dieser Studie enthaltenen Inhalte), einschließlich etwaiger mittelbarer oder Folgeschäden, ausgeschlossen. Die vorstehenden Haftungsbeschränkungen gelten für sämtliche vertraglichen, vorvertraglichen und außervertraglichen Ansprüche gleichermaßen und auch zugunsten der gesetzlichen Vertreter, Mitarbeiter und sonstigen Erfüllungsgehilfen des Auftragnehmers.",
		},
		statusMessages: {
			siteSaved: "Grundstück gespeichert",
			siteNotSaved: "Grundstück wird nicht gespeichert",
			buildingSaved: "Gebäude gespeichert",
			buildingNotSaved: "Gebäude wird nicht gespeichert",
			saving: "Speichern...",
			savedToCloud: "In der Cloud gespeichert",
		},
		buildingInfo: {
			residential: "Wohngebäude",
			units: "Einheiten",
		},
		validation: {
			buildingExceedsPlot: "Gebäude überschreitet das Grundstück",
			buildingSetbacksOverlap: "Gebäudeabstände überschneiden sich",
			buildingSetbackExceedsSiteLimit:
				"Gebäudeabstand überschreitet die Abstandsgrenze des Grundstücks",
			apartmentFacesPureNorth: "Wohnungen sind rein nach Norden ausgerichtet",
			siteExceedsMaxGsi:
				"Bebauung überschreitet die maximale GRZ (Grundflächenzahl)",
			siteExceedsMaxAncillaryGsi:
				"Bebauung inkl. versiegelter Flächen überschreitet die zulässige GRZ II (Nebenanlagen)",
			coreExceedsMaxApartments: "Treppenhaus erschließt zu viele Wohnungen",
			apartmentModifiedToFillGap: "Wohnung zum Füllen einer Lücke angepasst",
			apartmentModifiedToFillGapDetails: (rooms: number, livingArea: string) =>
				`${rooms} Zimmer, ${livingArea} m² Wohnfläche`,
		},
		warningsPanel: {
			noWarnings: "Keine Warnungen",
			archived: "Archiviert",
			archive: "Archivieren",
			restore: "Wiederherstellen",
			showDetails: "Details anzeigen",
			hideDetails: "Details ausblenden",
			moreDetails: (count: number) => `+${count} weitere`,
			collapse: "Einklappen",
			expand: "Ausklappen",
			archiveAll: "Alle archivieren",
			groups: {
				building: "Gebäude",
				gsi: "GRZ",
				unitOrientation: "Ausrichtung der Einheit",
				unitModified: "Einheit angepasst",
				core: "Kern",
			},
			warningTypes: "Warnungstypen",
			warningsWhileEditing: "Warnungen während der Bearbeitung",
		},
		userMenu: {
			name: "Name",
			cursorColor: "Cursorfarbe",
			cursorColorInfo:
				"Wählen Sie die Farbe, in der andere Ihren Cursor sehen.",
			language: "Sprache",
			theme: "Design",
			themeSystem: "System",
			themeLight: "Hell",
			themeDark: "Dunkel",
			organisation: "Organisation",
			manageAccount: "Konto verwalten",
			createOrganisation: "Organisation erstellen",
			organisationSettings: "Organisationseinstellungen",
			appAdmin: "App-Admin",
			contactUs: "Kontaktieren Sie uns",
			signOut: "Abmelden",
			signIn: "Anmelden",
			legal: "Rechtliches",
			privacyPolicy: "Datenschutzrichtlinie",
			cookiePolicy: "Cookie-Richtlinie",
			impressum: "Impressum",
			terms: "Geschäftsbedingungen",
		},
		locationPicker: {
			selectLand: "Wähle einen Standort an Land",
			cannotVerifyLocation:
				"Standort konnte nicht geprüft werden. Zum Wiederholen erneut auswählen.",
			useThisLocation: "Diesen Standort verwenden",
		},
		exportPanel: {
			generate: "Generieren",
			exportType: "Format",
			generateRevit: "Revit-Datei generieren",
			generateIfc: "IFC-Datei generieren",
			revitJobLabel: "Revit-Export",
			ifcJobLabel: "IFC-Export",
			generating: "Wird generiert...",
			[ExportStatus.pending]: "Ausstehend",
			[ExportStatus.inprogress]: "In Bearbeitung",
			[ExportStatus.success]: "Abgeschlossen",
			failed: "Fehlgeschlagen",
			download: "Datei herunterladen",
			delete: "Löschen",
			startNewJob: "Neuen Job starten",
			rawData: "Rohdaten",
			export: "Export",
			revitFiles: "Revit-Dateien",
			uploadRevitFile: "Revit-Datei hochladen",
			uploading: "Wird hochgeladen...",
			revitFileTooLarge: "Datei überschreitet die Größenbeschränkung",
			revitFileExists:
				"Eine Datei mit diesem Namen existiert bereits — löschen Sie sie zuerst oder benennen Sie die Datei um",
			revitFileHint: "Revit 2025 Referenzprojekt",
			referenceFile: "Referenzdatei",
			defaultReferenceFile: "Standard (integriert)",
			notMapped: "(nicht zugeordnet)",
			noBuildings: "Fügen Sie mindestens ein Gebäude hinzu, um zu exportieren",
			extracting: "Wird extrahiert...",
			extractionFailed: "Extraktion fehlgeschlagen",
			mapFamilies: "Familien zuordnen",
			downloadIfc: "IFC herunterladen",
			mappingTitle: "Familien zuordnen",
			mappingDescription:
				"Wählen Sie, welche Familie beim Export für jeden Elementtyp verwendet wird",
			mappingNone: "—",
			mappingRequired: "Ordnen Sie die Familien dieser Datei vor dem Export zu",
			mappingOutOfSync:
				"BIM-Objekt-Eigenschaften geändert — aktualisieren Sie zuerst die Familienzuordnung",
			mappingSearchPlaceholder: "Familien suchen...",
			mappingNoResults: "Keine Familien gefunden",
			mappingFixedSize: "(feste Größe)",
			mappingCategoryLabels: mappingCategoryLabelsDe,
			mappingSlotLabels: mappingSlotLabelsDe,
			mappingLoading: "Familien werden geladen...",
			mappingLoadFailed:
				"Die extrahierten Familien konnten nicht geladen werden",
			mappingSaveFailed: "Die Zuordnung konnte nicht gespeichert werden",
			mappingSave: "Speichern",
			mappingSaving: "Wird gespeichert...",
			mappingCancel: "Abbrechen",
			applySizesTitle: "BIM-Objekt-Eigenschaften aktualisieren?",
			applySizesDescription:
				"Die zugeordneten Familien haben andere Größen als die aktuellen Generierungseinstellungen. Auf dieses Layout anwenden und neu generieren?",
			applySizesApply: "Anwenden & neu generieren",
			applySizesKeep: "Aktuelle Werte behalten",
			revitFileUploadFailed:
				"Das Hochladen der Datei ist fehlgeschlagen — bitte erneut versuchen",
		},
		org: {
			apartmentSets: "Wohnungssets",
			globalApartmentSet: "Vorgegeben",
			globalApartmentSetDescription:
				"Von gen.os.build bereitgestellt und in jedes neue Projekt kopiert. Fügen Sie eigene Wohnungssets hinzu, um sie anzupassen.",
			defaultApartmentSet: "Standard für neue Projekte",
			defaultApartmentSetDescription:
				"Neue Projekte starten mit diesem Wohnungsset.",
			setDefaultApartmentSet: "Als Standard für neue Projekte festlegen",
			emptyDefaultApartmentSet:
				"Lade mindestens eine Wohnungsvorlage hoch, bevor du dieses Set als Standard festlegst.",
			createApartmentSet: "Neues Wohnungsset",
			defaultCorridorWidth: "Standard-Flurbreite",
			createApartmentGroup: "Neue Wohnungsgruppe",
			createApartmentTemplate: "Neue Wohnungsvorlage",
			bulkUploadApartmentTemplates: "Massen-Upload",
			bulkUploadApartmentTemplatesDescription:
				"Laden Sie mehrere Wohnungsvorlagen-JSON-Dateien gleichzeitig hoch. Jede wird der in der Datei genannten Gruppe hinzugefügt; fehlt die Gruppe, wird sie angelegt.",
			bulkUploadReadyCount: (count: number) =>
				`${count} Wohnungsvorlage${count === 1 ? "" : "n"} zum Hochladen bereit`,
			bulkUploadSkippedCount: (count: number) =>
				`${count} Datei${count === 1 ? "" : "en"} übersprungen — keine gültige Wohnungsvorlage`,
			bulkUploadProgress: (uploaded: number, total: number) =>
				`${uploaded} von ${total} hochgeladen`,
			bulkUploadFailedCount: (count: number) =>
				`${count} Upload${count === 1 ? "" : "s"} fehlgeschlagen`,
			upload: "Hochladen",
			close: "Schließen",
			name: "Name",
			color: "Farbe",
			shuffleColor: "Farbe zufällig wählen",
			create: "Erstellen",
			cancel: "Abbrechen",
			remove: "Entfernen",
			update: "Aktualisieren",
			apartmentGroup: "Wohnungsgruppe",
			newApartmentGroup: "Neue Gruppe…",
			addApartmentTemplate: "Wohnungsvorlage hinzufügen",
			chooseApartmentGroup:
				"Wähle die Gruppe, zu der diese Wohnung gehören soll.",
			add: "Hinzufügen",
			removeApartmentSet: "Wohnungsset entfernen",
			removeApartmentSetDescription:
				"Dieses Wohnungsset und alle zugehörigen Gruppen und Vorlagen werden endgültig entfernt.",
			removeApartmentTemplate: "Wohnungsvorlage entfernen",
			removeApartmentTemplateDescription:
				"Diese Wohnungsvorlage wird endgültig entfernt.",
		},
		appAdmin: {
			defaultApartmentSets: "Standard-Wohnungssets",
			autodeskExports: "Autodesk-Exporte",
			disabled: "Deaktiviert",
			cancel: "Abbrechen",
			typeToConfirm: { before: "Geben Sie ", after: " zur Bestätigung ein" },
			blockersChecking: "Es wird geprüft, was noch enden muss…",
			blockersFailed: "Es konnte nicht geprüft werden, was noch enden muss.",
			blockersIntro: "Noch nicht deaktivierbar:",
			openInStripe: "In Stripe öffnen",
			disableBlockers: {
				subscription: (id: string, status: string, appEnv?: string) =>
					`Abonnement ${id} (${status}${appEnv ? `, ${appEnv}` : ""}) ist nicht beendet. Auch eines mit späterer Kündigung zählt, bis es endet.`,
				schedule: (id: string, appEnv?: string) =>
					`Abonnementplan ${id}${appEnv ? ` (${appEnv})` : ""} ist nicht abgeschlossen.`,
				checkout: (id: string, appEnv?: string) =>
					`Checkout ${id}${appEnv ? ` (${appEnv})` : ""} ist noch offen.`,
				unknownCustomer: (id: string) =>
					`Stripe-Kunde ${id} ist nicht in diesem Stripe-Konto, daher kann seine Abrechnung nicht geprüft werden.`,
				otherOwnerCustomer: (id: string) =>
					`Stripe-Kunde ${id} nennt in seinen Metadaten jemand anderen.`,
				seatOperation: (id: string) =>
					`Eine Mitglieder- oder Platzänderung (${id}) läuft oder ist nicht abgeschlossen.`,
				seatReservation: "Ein Platz wartet auf die Zahlung seiner Rechnung.",
				scheduleSync:
					"Eine geplante Änderung wartet auf den Abgleich mit Stripe.",
				self: "Sie können sich nicht selbst deaktivieren.",
				appAdmin: "App-Admins können nicht deaktiviert werden.",
				lastOrgAdmin: (org: string) =>
					`Sie sind die einzige nicht deaktivierte Person mit Admin- oder Mitgliedsrolle in ${org}.`,
			},
			apsNoTransactions: "Noch keine Design-Automation-Läufe erfasst",
			apsStarted: "Gestartet",
			apsOrganisation: "Organisation",
			apsUser: "Benutzer",
			apsProject: "Projekt",
			apsAction: "Aktion",
			apsReferenceFile: "Referenzdatei",
			apsStatus: "Status",
			apsDuration: "Dauer",
			apsProcessing: "Engine-Zeit",
			apsCredits: "Cloud-Credits",
			apsCost: "Gesch. Kosten",
			apsFailureReason: "Grund",
			apsReport: "Bericht & World-JSON",
			apsFiles: "Dateien",
			apsResultFile: "Exportierte Datei",
			apsNotAvailable: "nicht verfügbar",
			apsOwnFile: "Eigener Upload",
			apsDefaultFile: "Standarddatei",
			apsKinds: {
				exportRevit: "Revit-Export",
				exportIfc: "IFC-Export",
				familyExtraction: "Familien-Extraktion",
			} as Record<string, string>,
			apsStatuses: {
				pending: "Ausstehend",
				inprogress: "Läuft",
				success: "Erfolgreich",
				failed: "Fehlgeschlagen",
				failedToStart: "Start fehlgeschlagen",
			} as Record<string, string>,
			apsStatusFilters: {
				__all__: "Alle Läufe",
				pending: "Ausstehend",
				inprogress: "Läuft",
				success: "Erfolgreich",
				failed: "Fehlgeschlagen",
				failedToStart: "Start fehlgeschlagen (Kontingent / abgelehnt)",
			} as Record<string, string>,
		},
		orgMembers: {
			title: "Mitglieder",
			inProgress:
				"Eine Mitglieder- oder Platzänderung wird noch bestätigt. Andere Änderungen warten, bis sie abgeschlossen ist; laden Sie gleich neu.",
			needsSupport:
				"Eine frühere Mitglieder- oder Platzänderung konnte nicht bestätigt werden, daher sind Mitglieder- und Platzänderungen pausiert. Wenden Sie sich an den Support.",
			seatUsage: (tier: string, used: number, paid: number) =>
				`${tier}: ${used} von ${paid} belegt`,
			standard: "Standard",
			premium: "Premium",
			admin: "Admin",
			member: "Bearbeiter",
			finance: "Finanzen",
			all: "Alle",
			active: "Aktiv",
			pending: "Ausstehend",
			filter: "Mitglieder filtern",
			memberColumn: "Mitglied",
			roleColumn: "Rolle",
			seatColumn: "Platz",
			noSeat: "Kein Platz",
			noPending: "Keine ausstehenden Einladungen.",
			cancelInvite: "Einladung zurückziehen",
			roleFor: (name: string) => `Rolle von ${name}`,
			seatFor: (name: string) => `Platz von ${name}`,
			chooseSeat: "Platz wählen",
			chooseSeatForRole:
				"Ihr üblicher Platztyp ist voll. Wählen Sie einen Platz, um die Rolle zu ändern.",
			trialSeatUntil: (trialTier: string, date: string) =>
				`${trialTier} bis zum Ende der Testphase am ${date}`,
			remove: "Entfernen",
			lastAdmin:
				"Eine Organisation braucht mindestens einen Admin. Machen Sie zuerst jemand anderen zum Admin.",
			removeTitle: (name: string) => `${name} entfernen?`,
			removeDescription: (name: string, org: string) =>
				`${name} verliert den Zugriff auf ${org}. Andere Organisationen sind nicht betroffen.`,
			cancel: "Abbrechen",
			invite: "Einladen",
			inviteTitle: (org: string) => `Zu ${org} einladen`,
			inviteDescription:
				"Admins und Bearbeiter belegen einen Platz eines Platztyps des Plans, Finanzmitglieder nicht. Einladungen reservieren den Platz, das Zurückziehen gibt ihn frei.",
			email: "E-Mail",
			emailInvalid: "Geben Sie eine gültige E-Mail-Adresse ein.",
			emailTaken:
				"Diese E-Mail gehört bereits zu einem Mitglied, einer Einladung oder einer Einladung, die auf Zahlung wartet.",
			emailPlaceholder: "name@acme.example",
			adminsOnly: "Nur Admins verwalten Mitglieder.",
			role: "Rolle",
			seat: "Platz",
			invitationRole: "Rolle der Einladung",
			invitationSeat: "Platz der Einladung",
			sendInvite: "Einladung senden",
			addSeatInvoiced: (tier: string, dueNow: string) =>
				`Alle ${tier}-Plätze sind belegt oder reserviert. Dies fügt 1 zusätzlichen ${tier}-Platz hinzu (${dueNow} jetzt fällig, anteilig) und sendet die Einladung, sobald sie bezahlt ist.`,
			addSeatInvoicedPrice: (tier: string, price: string, dueNow: string) =>
				`Alle ${tier}-Plätze sind belegt oder reserviert. Dies fügt 1 zusätzlichen ${tier}-Platz hinzu (${price}; ${dueNow} jetzt fällig, anteilig) und sendet die Einladung, sobald sie bezahlt ist.`,
			tierIncludesFeature: (tier: string, plan: string, feature: string) =>
				`${tier}-Plätze in ${plan} enthalten ${feature}.`,
			tierExcludesFeature: (tier: string, plan: string, feature: string) =>
				`${tier}-Plätze in ${plan} enthalten ${feature} nicht.`,
			addSeatTrial: (tier: string) =>
				`Alle ${tier}-Plätze sind belegt oder reserviert. Dies fügt der Testphase sofort 1 Platz hinzu und sendet die Einladung.`,
			seatChangeWaiting: (tier: string) =>
				`Alle ${tier}-Plätze sind belegt oder reserviert, und eine Platzänderung oder ein Platzangebot wartet bereits. Klären Sie dies zuerst unter Abrechnung.`,
			seatAddPaused: (tier: string) =>
				`Alle ${tier}-Plätze sind belegt oder reserviert, und das Hinzufügen von Plätzen ist pausiert, bis das unter Abrechnung angezeigte Problem gelöst ist. Lösen Sie es dort, wenden Sie sich an den gen.os.build-Support oder laden Sie in eine Stufe mit freiem Platz ein.`,
			addSeatAndInvite: "1 Platz hinzufügen & einladen",
			reviewOnBilling: "Unter Abrechnung prüfen",
			choosePlan: "Plan wählen",
			inactive:
				"Die Organisation hat keinen aktiven Plan. Wählen Sie unter Abrechnung einen, bevor Sie Plätze hinzufügen; Finanzmitglieder können weiterhin eingeladen werden.",
			loadFailed: "Mitglieder konnten nicht geladen werden.",
			saveFailed: "Mitglieder konnten nicht aktualisiert werden.",
			retry: "Erneut versuchen",
			loading: "Mitglieder werden geladen…",
		},
		orgSettings: {
			organisation: "Organisation",
			general: "Allgemein",
			billing: "Abrechnung",
			people: "Personen",
			members: "Mitglieder",
			name: "Name",
			save: "Speichern",
			nameRequired: "Geben Sie einen Namen für die Organisation ein.",
			renameFailed: "Die Organisation konnte nicht umbenannt werden.",
		},
		billingStatuses: {
			trialing: "Testphase",
			active: "Aktiv",
			incomplete: "Unvollständig",
			incomplete_expired: "Abgelaufen",
			past_due: "Zahlung überfällig",
			canceled: "Gekündigt",
			unpaid: "Unbezahlt",
			paused: "Pausiert",
		},
		adminCoupons: {
			combines: "Kombinierbar",
			combinesLabel: (code: string) =>
				`${code} ist mit anderen Rabatten kombinierbar`,
			combinesWithDiscounts: "Mit anderen Rabatten kombinierbar",
			combinesDescription:
				"Standardmäßig aus. Wenn aus, kann der Code nicht zusammen mit einem Platzrabatt genutzt werden: Beim Annehmen eines Platzrabatts wird vor dem Beenden des Codes gefragt. Eine spätere Änderung gilt nur für künftige Kombinationen.",
			combinesFailed: "Der Gutschein konnte nicht aktualisiert werden.",
			dashboard: "Stripe-Dashboard",
			dashboardNotCombinable: "Nicht gesetzt – gilt als nicht kombinierbar",
			yes: "Ja",
			coupons: "Gutscheine",
			back: "App-Admin",
			loading: "Gutscheine werden geladen…",
			loadFailed: "Gutscheine konnten nicht geladen werden.",
			retry: "Erneut versuchen",
			plansLoadFailed:
				"Die Pläne konnten nicht geladen werden, daher zeigen Gutscheine die Stripe-ID ihres Plans, und neue Gutscheine können erst erstellt werden, wenn sie geladen sind.",
			none: "Noch keine Gutscheine.",
			code: "Code",
			discount: "Rabatt",
			plan: "Plan",
			expires: "Läuft ab",
			redemptions: "Einlösungen",
			status: "Status",
			never: "Nie",
			statuses: {
				active: "Aktiv",
				inactive: "Deaktiviert",
				usedUp: "Aufgebraucht",
				expired: "Abgelaufen",
			},
			allPlans: "Alle Pläne",
			deletedPlan: "Gelöschter Plan",
			redemptionsUnlimited: (redemptions: number) =>
				`${redemptions} von unbegrenzt`,
			redemptionsLimited: (redemptions: number, max: number, left: number) =>
				`${redemptions} von ${max} · ${left} übrig`,
			deactivate: "Deaktivieren",
			deactivateTitle: (code: string) => `${code} deaktivieren?`,
			deactivateDescription:
				"Organisationen, die ihn bereits nutzen, behalten ihren Rabatt. Für neue Checkouts kann er erst wieder verwendet werden, wenn er aktiviert wird.",
			deactivateFailed: "Der Gutschein konnte nicht deaktiviert werden.",
			enable: "Aktivieren",
			enableTitle: (code: string) => `${code} aktivieren?`,
			enableDescription:
				"Er kann wieder für neue Checkouts verwendet werden. Stripe lehnt das ab, wenn sein Gutschein abgelaufen oder aufgebraucht ist oder ein anderer aktiver Code denselben Namen hat.",
			enableFailed: "Der Gutschein konnte nicht aktiviert werden.",
			cancel: "Abbrechen",
			newCoupon: "Neuer Gutschein",
			newCouponDescription:
				"Erstellt einen Aktionscode in Stripe. Organisationen geben ihn beim Checkout ein.",
			codeInvalid:
				"Verwenden Sie Buchstaben und Ziffern, optional durch Bindestriche getrennt.",
			codeTakenInStripe:
				"Dieser Code ist in Stripe bereits ein aktiver Gutscheincode, eventuell aus dem Stripe-Dashboard. Wählen Sie einen anderen Code.",
			prefix: "Präfix für generierten Code",
			prefixPlaceholder: "Präfix",
			generate: "Einmal-Code generieren",
			discountType: "Rabattart",
			percent: "Prozent",
			fixedAmount: "Fester Betrag",
			percentOff: "Prozent Rabatt",
			euroOff: "Euro Rabatt pro Rechnung",
			percentInvalid: "Geben Sie einen ganzen Prozentsatz von 1 bis 100 ein.",
			amountInvalid:
				"Geben Sie einen Betrag von mindestens 0,01 € ein, höchstens auf den Cent genau.",
			expiresOptional: "Läuft ab (optional)",
			expiresDescription: "Letzter Tag, an dem er eingelöst werden kann.",
			maxRedemptions: "Maximale Einlösungen (optional)",
			unlimited: "Unbegrenzt",
			maxRedemptionsDescription: "Gesamtzahl über alle Organisationen.",
			maxRedemptionsInvalid:
				"Geben Sie eine ganze Zahl ab 1 ein, oder lassen Sie das Feld für unbegrenzt leer.",
			duration: "Dauer",
			durations: {
				"1": "Einmalig",
				"3": "3 Monate",
				"12": "12 Monate",
				forever: "Dauerhaft",
			},
			durationDescription:
				"Stripe rabattiert die Rechnungen, die in dieser Zeit ab der Einlösung erstellt werden; bei einem Jahresplan ist das meist nur die erste Rechnung.",
			create: "Gutschein erstellen",
			createFailed: "Der Gutschein konnte nicht erstellt werden.",
		},
		serverNotices: {
			seatChangesPaused:
				"Stripe hat eine Änderung noch nicht bestätigt. Mitglieder- und Platzänderungen bleiben pausiert, bis sie bestätigt ist; prüfen Sie die Abrechnung in ein paar Minuten.",
			seatChangeChecking:
				"Die Platzänderung konnte nicht bestätigt werden und wird noch geprüft. Sehen Sie in der Abrechnung nach, bevor Sie es erneut versuchen.",
			seatChangeNotStarted:
				"Die Platzänderung konnte nicht gestartet werden, es wurde nichts berechnet. Versuchen Sie es erneut.",
			seatRevertFailed:
				"Der Platz wurde geändert, die Rolle aber nicht, und der Platz konnte nicht zurückgesetzt werden. Mitgliederänderungen bleiben pausiert, bis der Support das klärt.",
			planChangeUnconfirmed:
				"Diese Planänderung konnte nicht mit Stripe bestätigt werden. Setzen Sie sie auf der Seite Pläne fort; bis dahin kann der Plan nicht anders geändert werden.",
		},
		adminPlans: {
			plans: "Pläne",
			outsideCatalog: "Außerhalb des Katalogs",
			outsideCatalogHelp:
				"Stripe-Preise mit nicht beendeten Abonnements, deren Produkte keine App-Pläne sind, etwa das frühere persönliche Abonnement. Die App kann sie weder ändern noch kündigen; verwalten Sie sie im Stripe-Dashboard. Umgebungen, die dieses Stripe-Konto teilen, listen dieselben auf.",
			outsideCatalogLoading: "Preise außerhalb des Katalogs werden geladen…",
			outsideCatalogLoadFailed:
				"Die Preise außerhalb des Katalogs konnten nicht geladen werden.",
			outsideCatalogNone:
				"Jedes nicht beendete Abonnement läuft auf einem App-Plan.",
			amount: "Betrag",
			subscriptions: "Abonnements",
			statuses: "Status",
			issues: "Probleme",
			customAmount: "Gestaffelt oder individuell",
			amountPerInterval: (amount: string, interval: string, count: number) =>
				count === 1
					? `${amount} / ${interval}`
					: `${amount} / ${count} × ${interval}`,
			notAppPlan: "Kein App-Plan",
			archivedInStripe: "In Stripe archiviert · Abonnements bleiben",
			personal: (count: number) => `Persönlich (${count})`,
			unknownEnvironment: "Umgebung unbekannt",
			unknownEnvironmentCount: (count: number) =>
				`Umgebung unbekannt (${count})`,
			showSubscriptions: "Abonnements anzeigen",
			hideSubscriptions: "Abonnements ausblenden",
			stripePrice: "Preis in Stripe",
			stripeProduct: "Produkt in Stripe",
			customer: "Kunde",
			invalidPlan: (planId: string, reason: string) =>
				`Plan ${planId} wird nicht aufgeführt und Kunden nicht angeboten, da er in Stripe ungültig ist: ${reason}. Beheben Sie das im Stripe-Dashboard.`,
			newPlan: "Neuer Plan",
			showArchived: "Deaktivierte Pläne anzeigen",
			plan: "Plan",
			visibility: "Sichtbarkeit",
			enrollment: "Anmeldung",
			currentPrice: "Aktueller Preis",
			organisations: "Organisationen",
			edit: "Bearbeiten",
			archived: "Deaktiviert",
			public: "Öffentlich",
			private: "Privat",
			open: "Offen",
			closed: "Geschlossen",
			aboutVisibility: "Über die Sichtbarkeit",
			visibilityHelp:
				"Öffentlich: Kunden können ihn wählen. Privat: Nur App-Admins können ihn zuweisen.",
			aboutEnrollment: "Über die Anmeldung",
			enrollmentHelp:
				"Neue Abonnements zulassen. Bestehende Abonnements verlängern sich wie gewohnt.",
			name: "Name",
			nameRequired: "Geben Sie einen Plannamen ein.",
			allowTrials: "Testphasen erlauben",
			trialDays: "Testtage",
			trialDaysHelp:
				"Selbst gestartete Testphasen und der Standard für Testphasen von App-Admins.",
			trialSeat: "Testplatz",
			chooseTrialSeat: "Platztyp wählen",
			trialSeatHelp:
				"Startet das einzige Mitglied einer Organisation eine Testphase, wechselt sein Platz bis zum Ende der Testphase zu diesem Platztyp.",
			trialSeatInvalid: "Wählen Sie einen der Platztypen dieses Plans.",
			priceInvalid:
				"Geben Sie jeden Preis in Euro mit höchstens 2 Nachkommastellen, jede Anzahl enthaltener Plätze als ganze Zahl und für jeden Platztyp einen anderen Namen mit höchstens 40 Zeichen ein. Leere Felder sind nicht erlaubt.",
			seatTypes: "Platztypen",
			seatTypesNote:
				"Namen, enthaltene Plätze und Preise für zusätzliche Plätze gehören zum Preis.",
			seatTypeName: "Name des Platztyps",
			removeSeatType: (name: string) => `${name}-Plätze entfernen`,
			extraSeatPrice: "Preis pro zusätzlichem Platz",
			addSeatType: "Platztyp hinzufügen",
			couponsFailed:
				"Die auf diesen Plan beschränkten Gutscheine konnten nicht geladen werden.",
			newSeatTypeCoupons: (plan: string, codes: string) =>
				`Auf ${plan} beschränkte Aktionscodes (${codes}) rabattieren die Plätze der neuen Platztypen nicht, da Stripe die Produkte eines Gutscheins nicht ändern kann. Erstellen Sie bei Bedarf neue Codes.`,
			features: "Funktionen",
			featureA: "Platzhalter-Funktion A",
			featureChangeNote: (count: number, name: string) =>
				`Funktionsänderungen gelten sofort für ${count === 1 ? "die 1 Organisation" : `alle ${count} Organisationen`} mit ${name}, unabhängig von ihrem Preis.`,
			price: "Preis",
			versionNote: (version: number) =>
				`Eine Preisänderung wird als Version ${version} für neue Abonnements gespeichert.`,
			billing: "Abrechnung",
			billingIntervals: "Abrechnungsintervalle",
			monthly: "Monatlich",
			yearly: "Jährlich",
			month: "Monat",
			year: "Jahr",
			intervalsHelp:
				"Organisationen, die bereits in einem entfernten Intervall abgerechnet werden, behalten ihren Preis und ihr Intervall.",
			includedInBase: "In der Grundgebühr enthalten",
			baseFee: "Grundgebühr",
			baseFeeFor: (interval: string) => `Grundgebühr ${interval}`,
			includedFor: (tier: string) => `Enthaltene ${tier}-Plätze`,
			extraSeatFor: (tier: string, interval: string) =>
				`Zusätzlicher ${tier}-Platz ${interval}`,
			example: (seats: string, interval: string) =>
				`Beispiel: ${seats} über die enthaltenen hinaus, ${interval}`,
			perInterval: (amount: string, interval: string) =>
				`${amount} / ${interval}`,
			or: " oder ",
			priceSummary: (amounts: string, seats: string) =>
				`${amounts} · enthält ${seats}`,
			seatCounts: (parts: SeatPart[]) =>
				formatSeatPartsDe({ parts, joiner: " und " }),
			priceHistory: "Preisverlauf",
			sharedCatalogNote:
				"Alle Umgebungen mit diesem Stripe-Konto teilen diese Pläne, die Abonnements ihrer Organisationen und die Gutscheine: Änderungen gelten in allen, und Speichern oder Deaktivieren eines Plans lässt seine offenen Checkouts in allen ablaufen.",
			current: "Aktuell",
			orgCount: (count: number) =>
				`${count} ${count === 1 ? "Organisation" : "Organisationen"}`,
			archivePlan: "Plan deaktivieren",
			archiveTitle: (name: string) => `${name} deaktivieren?`,
			archiveDescription: (count: number) =>
				`Die Anmeldung wird geschlossen, der Plan verlässt den Katalog und seine offenen Checkouts laufen ab. ${count === 1 ? "Die 1 Organisation darauf behält" : `Die ${count} Organisationen darauf behalten`} Plan, Preis und Funktionen, und ihre Abonnements verlängern sich und werden weiter abgerechnet. Sie können ihn wieder aktivieren.`,
			sharedArchiveNote:
				" Alle Umgebungen mit diesem Stripe-Konto teilen diesen Plan: Er verlässt auch deren Kataloge, und ihre offenen Checkouts dafür laufen ab.",
			archive: "Deaktivieren",
			restorePlan: "Plan aktivieren",
			archivedMoveNote:
				"Organisationen können nicht auf eine andere Version eines deaktivierten Plans verschoben werden. Aktivieren Sie ihn zuerst, um sie zu verschieben.",
			restoreTitle: (name: string) => `${name} aktivieren?`,
			restoreDescription:
				"Er kehrt mit geschlossener Anmeldung in den Katalog zurück.",
			restore: "Aktivieren",
			cancel: "Abbrechen",
			save: "Speichern",
			create: "Plan erstellen",
			confirmTitle: (name: string) => `Änderungen an ${name} speichern?`,
			confirmDescription: (count: number) =>
				`${count} ${count === 1 ? "Organisation hat" : "Organisationen haben"} diesen Plan.`,
			newVersion: (version: number) => `Neue Preisversion ${version}`,
			newVersionNote:
				"Bestehende Abonnements behalten ihren Preis, sofern sie nicht verschoben werden. Nach dem Speichern wählen Sie, welche Organisationen verschoben werden: zahlende erhalten eine geplante Änderung, Testphasen wechseln sofort, und keine muss zustimmen.",
			newVersionArchivedNote:
				"Bestehende Abonnements behalten ihren Preis. Solange der Plan deaktiviert ist, können keine Organisationen dorthin verschoben werden; aktivieren Sie den Plan zuerst, um sie zu verschieben.",
			moveTitle: (plan: string, version: number) =>
				`Organisationen zu ${plan} v${version} verschieben?`,
			moveDescription:
				"Verschobene Organisationen behalten ihr Abrechnungsintervall und ihre bezahlten Plätze und müssen nicht zustimmen. Zahlende erhalten eine geplante Änderung; Testphasen wechseln sofort, da währenddessen nichts berechnet wird.",
			moveAll: (count: number) => `Alle ${count} verschieben`,
			moveOrg: (org: string, version: number) =>
				`${org} verschieben (auf v${version})`,
			keepsVersion: (interval: string, newVersion: number, version: number) =>
				`rechnet ${interval.toLowerCase()} ab, was v${newVersion} nicht anbietet, und behält daher v${version}`,
			trialMovesNow:
				"Testphase: wechselt sofort, und ein Upgrade zahlt den neuen Preis",
			movePendingUpgrade:
				"ihr Upgrade aus der Testphase wartet auf Zahlung und würde zum alten Preis beginnen, daher kann sie erst verschoben werden, wenn die Upgrade-Rechnung bezahlt oder abgelaufen ist",
			movePreview: (before: string, after: string, date: string) =>
				`nächste Rechnung ${before} → ${after} (Stripe, ${date})`,
			moveLostCode: (code: string) =>
				`Gutscheincode ${code} rabattiert nicht mehr, sobald die Verschiebung gilt`,
			atEachRenewal: "Zur nächsten Verlängerung jeder Organisation",
			moveDate: "Gültig ab",
			moveDateDescription:
				"Muss vor der Verlängerung jeder Organisation liegen; bei einer, die früher verlängert, erscheint unten ein Fehler, und Verschieben bleibt deaktiviert, bis Sie sie abwählen.",
			prorate: "Den Rest des Zeitraums anteilig berechnen",
			moveError: (org: string, error: string) => `${org}: ${error}`,
			skipMove: "Nicht verschieben",
			moveOrgs: "Organisationen verschieben",
			moveConsent:
				"Ich bestätige die Beträge, die Stripe jeder verschobenen zahlenden Organisation in Rechnung stellt, wie jeweils angezeigt",
			moveScheduled: (version: number, date: string) =>
				`Wechsel zu v${version} bereits für ${date} geplant`,
			moveReplacesScheduled: (date: string) =>
				`das Verschieben ersetzt den für ${date} geplanten Wechsel`,
			move: "Verschieben",
			moveLoadFailed: "Die Organisationen konnten nicht geladen werden.",
			movePreviewFailed: "Die Vorschau einer Verschiebung ist fehlgeschlagen.",
			moveFailed: "Die Organisationen konnten nicht verschoben werden.",
			unfinishedTitle: (plan: string) =>
				`Eine Änderung an ${plan} wurde nicht abgeschlossen`,
			operationPreparing:
				"Sie wird noch vorbereitet. Laden Sie in einer Minute neu; an Stripe wurde noch nichts gesendet.",
			operationAbandoned:
				"Sie wurde während der Vorbereitung abgebrochen, an Stripe wurde nichts gesendet. Verwerfen Sie sie, um den Plan wieder zu ändern.",
			operationUnconfirmed:
				"Stripe hat einen ihrer Schritte nicht bestätigt, daher kann sich der Plan erst ändern, wenn sie fortgesetzt wird. Beim Fortsetzen werden dieselben Schritte erneut gesendet, keiner wird doppelt angewendet: Jeder setzt ein Produkt, einen Preis oder einen Gutscheincode auf einen Zielzustand oder erstellt einen Preis, den Stripe nur einmal zulässt. Nach einem Tag wird jeder Schritt zuerst in Stripe geprüft und übersprungen, wenn seine Wirkung schon da ist.",
			operationRejected: (error: string) =>
				`Stripe hat einen ihrer Schritte abgelehnt (${error}), daher wurde sie mittendrin beendet. Die Schritte davor bleiben angewendet. Versuchen Sie es erneut, wenn die Ursache vorübergehend war, oder verwerfen Sie sie, prüfen Sie den Plan und speichern Sie erneut.`,
			operationCompensating:
				"Während der Plan gelöscht wurde, hat ein Abonnement oder eine geplante Änderung mit ihm begonnen. Der Plan bleibt daher deaktiviert, aber einige seiner Preise sind noch inaktiv. Setzen Sie sie fort, um sie wieder zu aktivieren; nichts wird erneut deaktiviert.",
			hiddenPlan: (planId: string) => `Plan ${planId} (nicht in der Planliste)`,
			resume: "Fortsetzen",
			retryChange: "Erneut versuchen",
			discard: "Verwerfen",
			deletionRetryOnly:
				"Eine Löschung, die begann, bevor Pläne nur noch deaktiviert werden konnten, wurde unterbrochen. Sie kann nicht verworfen werden, da der Plan sonst deaktiviert mit inaktiven Preisen bliebe. Wiederholen Sie sie, um die Löschung abzuschließen.",
			discardBlocked: (count: number) =>
				`${count === 1 ? "Eine Stripe-Anfrage" : `${count} Stripe-Anfragen`} dieser Änderung ${count === 1 ? "hat" : "haben"} nie eine endgültige Antwort erhalten und ${count === 1 ? "kann" : "können"} noch wirken, daher lässt sie sich nicht verwerfen. Fortsetzen oder erneut versuchen holt Stripes Antwort oder prüft nach einem Tag die Wirkung der Anfrage in Stripe und sendet sie erneut, wenn sie fehlt; danach lässt sie sich verwerfen.`,
			operationFailed: "Die Planänderung konnte nicht abgeschlossen werden.",
			featuresNotice: (count: number) =>
				`Funktionsänderungen gelten sofort für ${count === 1 ? "die 1 Organisation" : `alle ${count} Organisationen`}, unabhängig von ihrem Preis.`,
			loading: "Pläne werden geladen…",
			loadFailed: "Pläne konnten nicht geladen werden.",
			saveFailed: "Der Plan konnte nicht gespeichert werden.",
			saving: "Wird gespeichert…",
			retry: "Erneut versuchen",
			noPlans: "Noch keine Pläne.",
		},
		seatOffers: {
			conflictingCodes: (codes: string) =>
				`${codes} ist nicht mit anderen Rabatten kombinierbar, gilt aber zusammen mit diesem Platzrabatt, vermutlich im Stripe-Dashboard hinzugefügt. Er wird nicht automatisch entfernt; prüfen Sie ihn in Stripe.`,
			codeCombines: (code: string) =>
				`Rabatt ${code} ist mit anderen Rabatten kombinierbar und gilt daher auch für diese Plätze.`,
			codeEnds: (code: string) =>
				`Rabatt ${code} ist nicht mit einem Platzrabatt kombinierbar, daher endet er mit der Annahme: Die Beträge oben sind ohne ihn. Wird die Rechnung nicht bezahlt, bleibt er.`,
			endCodesConsent: (codes: string) =>
				`Rabatt ${codes} beenden, um diesen Platzrabatt anzunehmen`,
			changeEnds: (discount: string, tier: string) =>
				`Damit endet der Platzrabatt (${discount}): Es bleiben keine zusätzlichen ${tier}-Plätze dafür.`,
			changeDrops: (before: number, after: number, tier: string) =>
				`Der Platzrabatt sinkt von ${before} auf ${after} ${tier}-Plätze, da nur ${after} zusätzliche bleiben, und kommt nicht zurück. `,
			changeEndsEarly: (
				endsAt: string,
				interval: string,
				firstFullPriceAt: string,
			) =>
				`Damit endet der Platzrabatt früher: Er war bis ${endsAt} zugesagt, aber die ${interval} Rechnung am ${firstFullPriceAt} reicht über dieses Datum hinaus und hat daher den normalen Platzpreis.`,
			changeKeepsEnd: (endsAt: string, firstFullPriceAt: string) =>
				`Der Platzrabatt behält sein Enddatum, ${endsAt}; die erste Rechnung zum normalen Platzpreis ist am ${firstFullPriceAt}.`,
			monthlyInvoice: "monatliche",
			yearlyInvoice: "jährliche",
			discountEndsEarly: (
				interval: string,
				endsAt: string,
				firstFullPriceAt: string,
			) =>
				`Damit endet der Rabatt früher: ${interval} Rechnungen fallen nicht auf den ${endsAt}, daher hat die Rechnung am ${firstFullPriceAt}, die darüber hinausreicht, den normalen Preis.`,
			seatsFree: (seats: number, tier: string) =>
				`${seats} ${tier}-${seats === 1 ? "Platz" : "Plätze"} kostenlos`,
			seatsOff: (seats: number, tier: string, percent: number) =>
				`${seats} ${tier}-${seats === 1 ? "Platz" : "Plätze"} ${percent} % günstiger`,
			seatsNormal: (seats: number, tier: string) =>
				`${seats} ${tier}-${seats === 1 ? "Platz" : "Plätze"} zum normalen Preis`,
			until: (offer: string, date: string) => `${offer} bis ${date}`,
			seats: "Plätze",
			addSeats: "Plätze hinzufügen",
			addSeatsTitle: (org: string) => `Plätze für ${org} hinzufügen`,
			addSeatsDescription:
				"Die Beträge sind von Stripe berechnet, mit allen geltenden Rabatten.",
			mode: "Wie sie hinzugefügt werden",
			modeOffer: "Platzangebot erstellen",
			modeApply: "Jetzt anwenden",
			modeOfferHelp: (org: string) =>
				`Nichts wird hinzugefügt oder berechnet, bis ${org} unter Abrechnung annimmt. Die Organisation wird nicht benachrichtigt: Teilen Sie ihr den Abrechnungslink.`,
			modeApplyHelp:
				"Fügt die Plätze jetzt hinzu, ohne dass die Organisation in der App annimmt. Nur verwenden, wenn sie der Belastung zugestimmt hat.",
			modeOfferUnavailable:
				"Platzangebote brauchen ein aktives, bezahltes Abonnement.",
			tier: "Stufe",
			extraSeats: "Zusätzliche Plätze hinzufügen",
			extraSeatsHelp: (tier: string, from: number, to: number) =>
				`Nach der Annahme steigen die zusätzlichen ${tier}-Plätze von ${from} auf ${to}.`,
			discountPercent: "Rabatt (%)",
			percentInvalid: "Geben Sie eine ganze Zahl von 0 bis 100 ein.",
			seatsInvalid: "Geben Sie eine ganze Zahl von Plätzen von 1 bis 999 ein.",
			duration: "Dauer",
			months: (months: number) =>
				`${months} ${months === 1 ? "Monat" : "Monate"}`,
			endsOnRenewal: (date: string) => `Endet mit der Verlängerung am ${date}.`,
			preview: "Vorschau",
			customerSees: "Der Kunde sieht",
			renewsNote: (date: string) =>
				`Nach der Annahme verlängern sich die Plätze ab ${date} automatisch zum normalen Platzpreis, solange das Abonnement aktiv ist, sofern der Kunde sie nicht vorher entfernt.`,
			send: "Angebot erstellen",
			previewing: "Vorschau wird erstellt…",
			applySeatsHelp: (tier: string, from: number, to: number) =>
				`Die zusätzlichen ${tier}-Plätze steigen insgesamt von ${from} auf ${to}.`,
			applySummary: (seats: number, tier: string, from: number, to: number) =>
				`Fügt ${seats} zusätzliche${seats === 1 ? "n" : ""} ${tier}-${seats === 1 ? "Platz" : "Plätze"} hinzu: insgesamt ${from} → ${to}.`,
			applyDueNow: (amount: string) =>
				`Jetzt berechnet: ${amount}, anteilig bis zum Ende des aktuellen Zeitraums.`,
			applyPaymentNote:
				"Stripe belastet die Standardzahlungsmethode der Organisation. Schlägt das fehl, warten die Plätze, bis die Rechnung bezahlt ist.",
			applyTrialSubscribed: (date: string, amount: string) =>
				`Jetzt wird nichts berechnet: Die Organisation ist in einer Testphase. Sie endet am ${date}, und die erste Rechnung beträgt dann ${amount}, die Abonnementsumme mit diesen Plätzen.`,
			applyTrialUnsubscribed: (date: string) =>
				`Jetzt wird nichts berechnet: Die Organisation ist in einer Testphase. Sie endet am ${date}, und es wird nichts berechnet, sofern die Organisation kein Abonnement abschließt.`,
			applyNextInvoice: (date: string, amount: string) =>
				`Nächste Rechnung, ${date}: ${amount}, die Abonnementsumme mit diesen Plätzen.`,
			applyEnds: (date: string) =>
				`Das Abonnement endet am ${date}, daher gibt es keine nächste Rechnung.`,
			applyDiscountEnds: (date: string) =>
				`Diese Summe enthält einen Rabatt, der am ${date} endet; spätere Rechnungen kosten mehr und werden hier nicht angezeigt.`,
			applyPastDue:
				"Das Abonnement ist überfällig: Eine frühere Rechnung ist unbezahlt.",
			consentConfirm: (org: string) =>
				`${org} hat diesen Plätzen und den angezeigten Beträgen zugestimmt`,
			consentReference: "Wo die Zustimmung festgehalten ist",
			consentReferenceHelp:
				"Zum Beispiel eine Ticketnummer oder Betreff und Datum einer E-Mail. Keine Zahlungsdaten eingeben.",
			consentReferenceInvalid: (min: number, max: number) =>
				`Geben Sie ${min} bis ${max} Zeichen ein.`,
			apply: "Jetzt anwenden",
			applying: "Wird angewendet…",
			paymentPending:
				"Hinzugefügte Plätze warten auf die Zahlung ihrer Rechnung. Die Organisation wird nicht benachrichtigt: Teilen Sie ihr die Rechnung oder den Abrechnungslink.",
			copyInvoiceLink: "Rechnungslink kopieren",
			copyBillingLink: "Abrechnungslink der Organisation kopieren",
			shareNote: (org: string) =>
				`${org} wird nicht benachrichtigt: Teilen Sie den Abrechnungslink.`,
			cancel: "Abbrechen",
			dueOnAccepting: (date: string) =>
				`Fällig bei Annahme · diese Plätze, anteilig bis ${date}`,
			perInterval: (amount: string, interval: string) =>
				`${amount} / ${interval}`,
			month: "Monat",
			year: "Jahr",
			discountedUntilThen: (amount: string, interval: string) =>
				` Bis dahin kosten sie ${amount} / ${interval}.`,
			promotionCodeEnds: (code: string, date: string) =>
				`Gutscheincode ${code} rabattiert nur Rechnungen vor dem ${date}, daher kosten Rechnungen ab dann mehr als die für frühere Daten angezeigten Beträge.`,
			nextRenewal: (date: string, endsOn: string) =>
				`Nächste Verlängerung, ${date} · Abonnement gesamt, diese Plätze rabattiert bis ${endsOn}`,
			firstFullPrice: (date: string) =>
				`Erste Rechnung zum normalen Platzpreis, ${date} · Abonnement gesamt`,
			withdrawNote:
				"Eine Plan-, Preis- oder Intervalländerung zieht dieses Angebot und eine unbezahlte Rechnung dafür zurück.",
			offerSent: (offer: string) => `Platzangebot erstellt: ${offer}.`,
			waiting: (org: string) =>
				`Wartet darauf, dass ${org} es annimmt oder ablehnt. Bis dahin wird nichts hinzugefügt oder berechnet.`,
			acceptedWaiting:
				"Angenommen; die Plätze gelten, sobald die anteilige Rechnung bezahlt ist.",
			withdraw: "Angebot zurückziehen",
			withdrawTitle: (org: string) =>
				`Das Platzangebot an ${org} zurückziehen?`,
			withdrawAccepted:
				"Die unbezahlte Rechnung wird storniert, und es werden keine Plätze hinzugefügt oder berechnet.",
			withdrawOpen:
				"Der Kunde kann es nicht mehr annehmen. Es wurden keine Plätze hinzugefügt oder berechnet.",
			discountNotice: (discount: string) => `Platzrabatt: ${discount}.`,
			discountInDialog: (discount: string) =>
				`${discount}, danach zum normalen Preis berechnet, sofern nicht vorher entfernt.`,
			discountDrops: (seats: number, tier: string) =>
				`Der Platzrabatt sinkt auf ${seats} ${tier}-Plätze und kommt nicht zurück, wenn Sie später Plätze hinzufügen.`,
			discountRenews: (date: string, amount: string) =>
				`Ab ${date} bleiben diese Plätze zum normalen Preis von ${amount} im Plan und werden automatisch berechnet, solange das Abonnement aktiv ist. Um das zu vermeiden, entfernen Sie sie vor dem ${date} unter Plätze verwalten. Plätze, die Mitglieder oder offene Einladungen nutzen, können nicht entfernt werden.`,
			endMismatch:
				"Stripe beendet diesen Rabatt mit einer anderen Verlängerung als im Angebot vereinbart. Prüfen Sie den Rabatt der Abonnementposition in Stripe.",
			endDiscount: "Platzrabatt beenden",
			endDiscountTitle: (org: string) => `Den Platzrabatt von ${org} beenden?`,
			endDiscountDescription:
				"Die rabattierten Plätze bleiben im Plan und werden ab der nächsten Rechnung zum normalen Preis berechnet.",
			endDiscountConfirm: "Rabatt beenden",
			unavailableScheduled: (date: string) =>
				`Nicht verfügbar, solange eine Planänderung für den ${date} geplant ist. Warten Sie, bis sie gilt, oder brechen Sie sie ab.`,
			offerFrom: (offer: string, amount: string, date: string) =>
				`Platzangebot von gen.os.build: ${offer}. Nichts wird hinzugefügt oder berechnet, wenn Sie nicht annehmen. Nach der Annahme bleiben die Plätze im Plan und verlängern sich automatisch, solange das Abonnement aktiv ist, ab ${date} zu ${amount}, sofern Sie sie nicht vorher unter Plätze verwalten entfernen.`,
			acceptPay: (amount: string) => `Annehmen und ${amount} bezahlen`,
			acceptFree: "Annehmen · jetzt nichts fällig",
			accept: "Annehmen",
			acceptPaused: {
				seatDiscountUnavailable: (offer: string) =>
					`gen.os.build hat Ihnen ${offer} angeboten. Es kann erst angenommen werden, wenn der gen.os.build-Support ein Problem mit Ihrem Platzrabatt klärt, Sie können es aber ablehnen.`,
				reactivationCheck: (offer: string) =>
					`gen.os.build hat Ihnen ${offer} angeboten. Es kann erst angenommen werden, wenn das oben angezeigte Problem mit Ihrer Reaktivierung gelöst ist, Sie können es aber ablehnen.`,
				externalSchedule: (offer: string) =>
					`gen.os.build hat Ihnen ${offer} angeboten. Es kann nicht angenommen werden, solange für Ihr Abonnement eine außerhalb der App vorgenommene Änderung geplant ist. Bitten Sie den gen.os.build-Support, sie zu ändern oder aufzuheben, oder lehnen Sie das Angebot ab.`,
				seatChangePending: (offer: string) =>
					`gen.os.build hat Ihnen ${offer} angeboten. Es kann nicht angenommen werden, solange die oben angezeigte Platzänderung auf Zahlung wartet. Bezahlen Sie deren Rechnung oder lassen Sie sie ablaufen und nehmen Sie das Angebot dann an, oder lehnen Sie es ab.`,
			},
			decline: "Ablehnen",
			updateFailed: "Das Platzangebot konnte nicht aktualisiert werden.",
			quoteFailed:
				"Für das Platzangebot konnte keine Vorschau erstellt werden.",
			applyFailed: "Die Plätze konnten nicht hinzugefügt werden.",
			applyQuoteFailed:
				"Für das Hinzufügen der Plätze konnte keine Vorschau erstellt werden.",
		},
		adminUsers: {
			users: "Benutzer",
			user: "Benutzer",
			email: "E-Mail",
			status: "Status",
			created: "Erstellt",
			lastSignIn: "Letzte Anmeldung",
			never: "Nie",
			active: "Aktiv",
			appAdmin: "App-Admin",
			actions: "Aktionen",
			noUsers: "Noch keine Benutzer",
			loading: "Benutzer werden geladen…",
			loadFailed: "Benutzer konnten nicht geladen werden.",
			disable: "Deaktivieren",
			disableTitle: (name: string) => `${name} deaktivieren?`,
			disableDescription:
				"Die Person wird überall abgemeldet und kann sich nicht mehr anmelden. Ihre Mitgliedschaften und Plätze bleiben, daher zahlen Organisationen weiter für ihre Plätze, bis ein Admin sie entfernt. Sie können sie wieder aktivieren. Deaktivieren ist eine Clerk-Sperre, die in jeder Umgebung derselben Clerk-Instanz gilt (next, alle PR-Vorschauen und die lokale Entwicklung teilen eine).",
			enable: "Aktivieren",
			enableTitle: (name: string) => `${name} aktivieren?`,
			enableDescription: "Die Person kann sich wieder anmelden.",
			changeFailed:
				"Es konnte nicht geändert werden, ob der Benutzer deaktiviert ist.",
		},
		adminOrgs: {
			disableOrg: "Organisation deaktivieren",
			disablingOrg: "Organisation wird deaktiviert…",
			disableOrgTitle: (name: string) => `${name} deaktivieren?`,
			disableOrgDescription:
				"Ihre Mitglieder können die App nicht nutzen und keine Testphase, keinen Checkout und keine Reaktivierung starten; die Abrechnung bleibt sichtbar. Nichts wird gekündigt oder berechnet. Sie können sie wieder aktivieren. Die Kennzeichnung liegt in Clerk und gilt daher in jeder Umgebung derselben Clerk-Instanz (next, alle PR-Vorschauen und die lokale Entwicklung teilen eine).",
			disableOrgHint:
				"Beenden Sie ihre Testphase auf dieser Seite oder bitten Sie ihren Admin, den Plan unter Abrechnung zu kündigen, und warten Sie, bis das Abonnement beendet ist. Versuchen Sie es dann erneut.",
			enableOrg: "Organisation aktivieren",
			enablingOrg: "Organisation wird aktiviert…",
			orgAccess: "Zugang der Organisation",
			orgEnabled: "Aktiviert",
			refreshing: "Wird aktualisiert…",
			refreshFailed:
				"Konnte nicht aktualisiert werden, einige Angaben sind eventuell veraltet.",
			done: "Fertig",
			howSeatsWork: "So funktionieren Plätze",
			searchMembers: "Mitglieder suchen",
			searchMembersPlaceholder: "Name oder E-Mail",
			noMatchingMembers: "Keine Mitglieder passen zur Suche.",
			prepareSetupOpen: "Einrichtung vorbereiten…",
			prepareSetupTitle: (name: string) =>
				`Einrichtung für ${name} vorbereiten`,
			reactivationLinkOpen: "Reaktivierungslink erstellen…",
			reactivationLinkTitle: (name: string) => `Reaktivierungslink für ${name}`,
			enableOrgTitle: (name: string) => `${name} aktivieren?`,
			enableOrgDescription:
				"Ihre Mitglieder können die App wieder nutzen, sofern ihr Plan es erlaubt. Die Kennzeichnung liegt in Clerk und gilt daher in jeder Umgebung derselben Clerk-Instanz (next, alle PR-Vorschauen und die lokale Entwicklung teilen eine).",
			disabledNotice:
				"Diese Organisation ist deaktiviert: Ihre Mitglieder können die App nicht nutzen und keine Testphase, keinen Checkout und keine Reaktivierung starten.",
			disabledChangeFailed:
				"Es konnte nicht geändert werden, ob die Organisation deaktiviert ist.",
			reactivationCheckPending: (id: string) =>
				`Die Reaktivierung ${id} wurde noch nicht mit ihren bestätigten Bedingungen abgeglichen; die Organisation kann die Prüfung unter Abrechnung ausführen.`,
			reactivationCheckFailedUnpaid: (problem: string, id: string) =>
				`Die Reaktivierung ${id} ${getAdminTermsProblemDe(problem)}, und Stripe hat das Stornieren ihrer unbezahlten ersten Rechnung abgelehnt. Stornieren Sie sie im Stripe-Dashboard, was die Reaktivierung beendet; bis dahin werden Plan- und Platzänderungen der Organisation abgelehnt, und ihre Rechnung kann in der App nicht bezahlt werden.`,
			reactivationCheckFailed: (problem: string, id: string) =>
				`Die Reaktivierung ${id} ${getAdminTermsProblemDe(problem)}, und ihre erste Rechnung war bezahlt oder es war nichts fällig. Korrigieren Sie sie im Stripe-Dashboard und setzen Sie dann ihre termsCheck-Metadaten auf passed; bis dahin werden Plan- und Platzänderungen der Organisation abgelehnt.`,

			seatDiscountUnavailable: (tier: string, ids: string) =>
				`Der Stripe-Gutschein des Platzrabatts auf ${tier}-Plätze wurde gelöscht, daher sind seine Bedingungen nicht lesbar: ${ids}. Stripe wendet den Rabatt weiterhin an und die Organisation behält ihren Zugang. Platzangebote, Planänderungen und das Abgleichen einer geplanten Änderung, die den Rabatt übernehmen muss, sind pausiert, bis der Rabatt beendet ist. Beim Beenden werden die rabattierten Plätze ab der nächsten Rechnung zum normalen Preis berechnet; brechen Sie eine geplante Planänderung zuerst ab. Ein wartendes Angebot kann weiterhin zurückgezogen werden.`,
			conflictingIds: (ids: string) =>
				`Die Abrechnung der Organisation ist widersprüchlich, daher hat sie keinen Zugang: ${ids}. Beheben Sie Stripe-IDs in Stripe; bei einer Mitgliedschafts- oder Einladungs-ID korrigieren Sie deren Platzstufe in ihren privaten Clerk-Metadaten. Nichts wurde automatisch gekündigt.`,
			retry: "Erneut versuchen",
			scheduleSyncQueued: (date: string) =>
				`Seit ${date} warten die Plätze der geplanten Änderung darauf, in Stripe aktualisiert zu werden: Eine Platzänderung wartet auf Zahlung, die Aktualisierung konnte noch nicht laufen, Stripe hat sie abgelehnt, oder der Zeitplan hat Phasen, die die App nicht übernehmen kann. Bis dahin würde die Änderung mit ihren bisherigen Plätzen wirksam. Beim Aktualisieren steht der Grund, falls es weiterhin fehlschlägt, auch wenn die Änderung schon begonnen hat; prüfen Sie dann die Plätze in Stripe, korrigieren Sie sie bei Bedarf mit einer Platzänderung und markieren Sie es als erledigt.`,
			retryScheduleSync: "Geplante Plätze aktualisieren",
			resolveScheduleSync: "Als erledigt markieren",
			resolveScheduleSyncFailed:
				"Die wartende Platzaktualisierung konnte nicht entfernt werden.",
			scheduleSyncFailed:
				"Stripe konnte die geplanten Plätze nicht aktualisieren.",
			organisations: "Organisationen",
			previousPage: "Zurück",
			nextPage: "Weiter",
			pageOf: (page: number, pageCount: number) =>
				`Seite ${page} von ${pageCount}`,
			organisation: "Organisation",
			plan: "Plan",
			status: "Status",
			seatsUsed: "Belegte Plätze",
			seatsUsedOf: (used: number, paid: number) => `${used} von ${paid}`,
			planNamesFailed:
				"Die Plannamen konnten nicht geladen werden, daher werden Pläne mit ihrer Stripe-ID angezeigt. Laden Sie neu, um es erneut zu versuchen.",
			invalidSeatTiers: "Ungültige Platzstufen",
			seatsUsedCount: (used: number) => `${used} belegt`,
			billingUnavailable: "Abrechnung nicht verfügbar",
			retryBilling: "Erneut versuchen",
			manage: "Verwalten",
			noPlan: "Kein Plan",
			seatsColumn: "Plätze",
			seatsAssignedCovered: (assigned: number, covered: number) =>
				`${assigned}/${covered}`,
			seatsCellLabel: (
				assigned: number,
				covered: number,
				isMismatch: boolean,
			) =>
				`${assigned} Plätze vergeben, ${covered} gedeckt${isMismatch ? ". Für eine Platzart sind mehr Plätze vergeben, als das Abonnement bezahlt" : ""}. Plätze je Platzart anzeigen`,
			seatsTierAssignedCovered: (
				tier: string,
				assigned: number,
				covered: number,
			) => `${tier}: ${assigned} vergeben, ${covered} gedeckt`,
			seatsNotCovered: "Kein aktives Abonnement, daher ist kein Platz gedeckt.",
			seatsMismatch:
				"Für eine Platzart sind mehr Plätze vergeben, als das Abonnement bezahlt.",
			reviewIssue: "Problem prüfen",
			issues: "Probleme",
			issuesNotAllFixable:
				"Nicht jedes Problem lässt sich hier beheben: Abrechnungskonflikte und Probleme in Stripe brauchen das Stripe- oder Clerk-Dashboard.",
			issueSeatsExceeded: (tier: string, used: number, paid: number) =>
				`${tier}: ${used} Plätze vergeben, ${paid} bezahlt. Verschieben Sie Mitglieder auf eine Platzart mit freiem Platz, ändern Sie ihre Rolle zu Finanzen (Finanzen belegt keinen Platz), oder senden Sie ein Platzangebot, das die Organisation annimmt und bezahlt.`,
			issueBillingConflict: (ids: string) =>
				`Die Abrechnung ist inkonsistent. Lösen Sie ${ids} in Stripe oder Clerk; hier lässt es sich nicht beheben.`,
			issueReactivationCheck:
				"Eine Reaktivierung hat die Prüfung ihrer Bedingungen nicht bestanden.",
			issueScheduleSync:
				"Plätze einer geplanten Änderung warten noch auf die Synchronisierung mit Stripe.",
			issueSeatOperationStuck:
				"Eine Mitglieder- oder Platzänderung konnte nicht automatisch abgeschlossen werden.",
			issueNoCoverage:
				"Plätze sind vergeben, aber kein aktives, laufendes Test- oder überfälliges Abonnement deckt sie ab.",
			noCoverageStatus: (status: string) => `Abrechnungsstatus: ${status}.`,
			issueNoPlan:
				"Sie hat in dieser Umgebung keinen Plan, daher ist kein Platz gedeckt.",
			issuePlanNotActive:
				"Für sie wurde ein Plan gewählt, der aber noch nicht aktiv ist, daher ist kein Platz gedeckt.",
			setupTracked: ({
				plan,
				isTrial,
				isSupport,
				isCompleted,
				date,
			}: {
				plan: string;
				isTrial: boolean;
				isSupport: boolean;
				isCompleted: boolean;
				date: string;
			}) =>
				`${isSupport ? "Der Support hat" : "Ihr Ersteller hat"} ${isTrial ? `eine Testphase von ${plan}` : `${plan}, bezahlt,`} gewählt; ${isCompleted ? "diese Einrichtung ist abgeschlossen" : "noch nicht gestartet oder bezahlt"} (zuletzt geändert ${date}).`,
			setupUntracked:
				"Für sie ist in dieser Umgebung keine Planauswahl gespeichert, zum Beispiel weil sie ohne Planauswahl erstellt wurde.",
			setupMissing: (id: string) =>
				`Sie nennt die Einrichtung ${id}, die in dieser Umgebung nicht gespeichert ist. Der Eintrag muss repariert werden.`,
			setupOtherOrg: (id: string, orgId: string) =>
				`Sie nennt die Einrichtung ${id}, die zu ${orgId || "noch keiner Organisation"} gehört. Prüfen Sie beide Organisationen; nichts wird automatisch gelöscht.`,
			goToTrial: "Zur Testphase",
			goToPrepareSetup: "Einrichtung vorbereiten",
			prepareSetup: "Einrichtung vorbereiten",
			prepareSetupDescription:
				"Speichern Sie eine Planauswahl, die Admins und Finanzmitglieder der Organisation unter Abrechnung finden. Sie starten die Testphase oder bezahlen selbst; jetzt wird nichts an Stripe gesendet und niemandem etwas berechnet.",
			setupStartWith: "Starten mit",
			setupModeTrial: "Testphase",
			setupModePaid: "Bezahlung",
			prepareSetupSave: "Einrichtung speichern",
			prepareSetupFailed: "Die Einrichtung konnte nicht vorbereitet werden.",
			goToMembers: "Zu den Mitgliedern",
			goToSeats: "Zu den Plätzen",
			goToSubscription: "Zum Abonnement",
			goToSeatOperation: "Zur Änderung",
			members: "Mitglieder",
			membersLoadFailed:
				"Die Mitglieder konnten nicht geladen werden. Laden Sie neu, um es erneut zu versuchen.",
			membersConflict: (ids: string) =>
				`Mitglieder können nicht aufgelistet werden, solange diese Platzstufen ungültig sind: ${ids}.`,
			memberColumn: "Mitglied",
			roleColumn: "Rolle",
			seatColumn: "Platz",
			invited: "Eingeladen",
			tierUnsaved: "noch nicht gespeichert",
			noSeat: "Kein Platz",
			roleAccessFinance:
				"Finanzen ist eine Rolle, kein Platz: Sie belegt keinen Platz und hat nur Zugang zur Abrechnung.",
			noBillingChange:
				"Keine Abrechnungsänderung: Nichts wird berechnet, und weder Abonnement noch Anzahl bezahlter Plätze ändern sich. Wird ein Platz belegt, werden offene Checkouts der Organisation geschlossen, wie wenn ihre eigenen Admins Mitglieder ändern.",
			seatOnlyWherePaidFree:
				"Ein Platz kann nur belegt werden, wo ein bezahlter Platz frei ist.",
			memberChangeFailed: "Das Mitglied konnte nicht geändert werden.",
			cannotRemoveSelf: "Sie können sich nicht selbst entfernen.",
			inviteMember: "Einladen",
			inviteMemberTitle: (org: string) => `Zu ${org} einladen`,
			inviteMemberDescription:
				"Sendet dieselbe Einladungs-E-Mail wie die Admins der Organisation. Eine Einladung als Admin oder Mitglied reserviert einen Platz des gewählten Typs, bis sie angenommen oder storniert wird.",
			addExistingUser: "Bestehenden Benutzer hinzufügen",
			addExistingUserTitle: (org: string) =>
				`Bestehenden Benutzer zu ${org} hinzufügen`,
			addExistingUserDescription:
				"Fügt den Benutzer direkt hinzu, ohne Einladung. Ein Admin oder Mitglied belegt einen Platz des gewählten Typs.",
			searchUsers: "Benutzer suchen",
			searchUsersPlaceholder: "Name, E-Mail oder Benutzer-ID",
			search: "Suchen",
			select: "Auswählen",
			selected: "Ausgewählt",
			selectUser: (user: string) => `${user} auswählen`,
			selectedUser: (user: string) => `Ausgewählt: ${user}`,
			noMatchingUsers: "Keine Benutzer passen zur Suche.",
			alreadyMember: "Mitglied",
			disabledUser: "Deaktiviert",
			addMember: "Hinzufügen",
			seatTypeFull: (tier: string) => `${tier} (voll)`,
			orgDisabledNoAdd:
				"Die Organisation ist deaktiviert, daher kann niemand eingeladen oder hinzugefügt werden. Mitglieder und Einladungen können weiterhin entfernt werden.",
			removeMemberTitle: (name: string, org: string) =>
				`${name} aus ${org} entfernen?`,
			removeMemberDescription: (name: string, org: string) =>
				`Nur die Mitgliedschaft von ${name} in ${org} wird entfernt. Der Benutzer und seine anderen Organisationen bleiben unverändert.`,
			removeMemberSeatFreed:
				"Bezahlte Mengen und Abrechnung bleiben unverändert, daher wird der bezahlte Platz frei.",
			removeMemberNoSeat: "Bezahlte Mengen und Abrechnung bleiben unverändert.",
			removePerson: (name: string) => `${name} entfernen`,
			revokeInvitation: "Einladung stornieren",
			revokeInvitationOf: (email: string) => `Einladung stornieren: ${email}`,
			revokeInvitationTitle: (email: string, org: string) =>
				`Einladung von ${email} zu ${org} stornieren?`,
			revokeInvitationDescription:
				"Der Einladungslink funktioniert nicht mehr und der reservierte Platz wird frei. Bezahlte Mengen und Abrechnung bleiben unverändert.",
			reactivationLink: "Reaktivierungslink",
			reactivationLinkDescription:
				"Bereiten Sie Plan und Plätze für das Admin- oder Finanzmitglied der Organisation vor. Es öffnet den Link, prüft den Preis und bezahlt unter Abrechnung; nichts wird berechnet, bevor es bestätigt, und hier kann nicht für die Organisation bezahlt werden.",
			reactivationLinkPlanNote:
				"Angeboten werden nur Pläne, die die Organisation selbst wählen kann: öffentliche Pläne, die neuen Organisationen offenstehen, und ihr bisheriger Plan.",
			reactivationExtraSeats: (tier: string) => `Zusätzliche ${tier}-Plätze`,
			copyLink: "Link kopieren",
			linkCopied: "Link kopiert",
			linkCopyFailed:
				"Der Link konnte nicht kopiert werden. Markieren und kopieren Sie ihn von Hand.",
			loading: "Organisationen werden geladen…",
			loadingOrg: "Organisation wird geladen…",
			loadFailed: "Die Organisationen konnten nicht geladen werden.",
			orgLoadFailed:
				"Die Abrechnung der Organisation konnte nicht geladen werden, daher werden Plan, Plätze und Testphase nicht angezeigt. Laden Sie die Seite neu, um es erneut zu versuchen.",
			planCatalogFailed:
				"Der Plankatalog konnte nicht geladen werden, daher sind Plannamen, Planänderungen und neue Testphasen nicht verfügbar. Laden Sie die Seite neu, um es erneut zu versuchen.",
			seatOperationLoadFailed:
				"Der Stand der Mitglieder- und Platzänderungen konnte nicht geladen werden, daher kann eine hängende Änderung hier nicht geprüft oder freigegeben werden. Laden Sie die Seite neu, um es erneut zu versuchen.",
			subscription: "Abonnement",
			trialEnds: "Testphase endet",
			trial: "Testphase",
			trialCancelsAtEnd:
				"Bis die Organisation unter Abrechnung abonniert, ist die Testphase so eingestellt, dass sie an ihrem Ende gekündigt wird, daher wird eine hinterlegte Karte nicht belastet.",
			alreadySubscribed:
				"Bereits abonniert – nutzen Sie stattdessen einen Rabatt.",
			alreadyHadTrial:
				"Hatte bereits eine Testphase. Ablaufen oder Kündigen macht sie nicht wieder berechtigt.",
			subscribedTrial:
				"Die Organisation hat während der Testphase abonniert, daher kann diese hier weder verlängert noch beendet werden. Die erste Rechnung wird am Ende der Testphase belastet; bis dahin kann im Abrechnungsportal gekündigt werden.",
			unmanagedTrial:
				"Diese Testphase wurde nicht in der App gestartet und kann hier weder verlängert noch beendet werden. Verwalten Sie sie in Stripe.",
			alreadyPaid:
				"Hat bereits für einen Plan bezahlt und kann daher keine Testphase erhalten. Sie kann auf Stripe reaktivieren, bei Bedarf mit einem Gutschein.",
			extendTrial: "Testphase verlängern",
			extendTitle: (org: string) => `Testphase von ${org} verlängern`,
			days: "Tage",
			newTrialEnd: (date: string) => `Die Testphase endet dann am ${date}.`,
			daysInvalid: (maxDays: number) =>
				`Geben Sie eine ganze Zahl von Tagen von 1 bis ${maxDays} ein.`,
			extendTooLong: (limit: number, maxDays: number) =>
				`Stripe beendet eine Testphase höchstens ${limit} Tage nach heute, daher lässt sich diese höchstens um ${maxDays} ${maxDays === 1 ? "Tag" : "Tage"} verlängern.`,
			endTrialNow: "Testphase jetzt beenden",
			endTrialTitle: (org: string) => `Testphase von ${org} jetzt beenden?`,
			endTrialDescription:
				"Der Plan wird heute gekündigt und Projekte werden gesperrt. Eine hinterlegte Karte wird nicht belastet, da kein bezahlter Plan angenommen wurde. Ein Admin oder Finanzmitglied kann einen Plan wählen und bezahlen. Mitglieder, Einladungen und Plätze bleiben erhalten.",
			endTrial: "Testphase beenden",
			cancelSubscription: "Abo kündigen",
			cancelSubscriptionTitle: (org: string) => `Abo von ${org} kündigen`,
			cancelSubscriptionDescription:
				"Nur auf Wunsch der Organisation, außerhalb der App festgehalten. Mitglieder, Einladungen und Plätze bleiben erhalten, und die Organisation wird nicht deaktiviert. Das lässt sich hier nicht rückgängig machen.",
			cancellationMode: "Zeitpunkt und Erstattung",
			cancellationModes: {
				atRenewal: "Zur Verlängerung",
				now: "Sofort, ohne Erstattung",
				nowProrated: "Sofort, ungenutzte Zeit erstatten",
				nowFullRefund: "Sofort, letzte Rechnung erstatten",
			},
			cancellationModeHelp: {
				atRenewal:
					"Der Zugang bleibt bis zum Ende des aktuellen Zeitraums. Nichts wird erstattet.",
				now: "Der Zugang endet sofort. Nichts wird erstattet.",
				nowProrated:
					"Der Zugang endet sofort. Der ungenutzte Teil jeder bezahlten Rechnung des aktuellen Zeitraums, auch von Platzänderungen, wird in Geld erstattet.",
				nowFullRefund:
					"Der Zugang endet sofort. Was von der letzten bezahlten Rechnung, auch einer Platzänderung, noch erstattbar ist, wird in Geld erstattet.",
			},
			cancellationPreview: "Vorschau",
			cancellationPreviewing: "Vorschau wird geladen…",
			cancellationQuoteFailed: "Die Kündigung konnte nicht berechnet werden.",
			cancellationAccessEnds: (date: string) => `Zugang endet: ${date}.`,
			cancellationNoRefund: "Nichts wird erstattet.",
			cancellationInvoiceKinds: {
				period: "Abo-Rechnung",
				seatChange: "Rechnung einer Platzänderung",
			},
			cancellationRefund: (
				kind: string,
				invoice: string,
				paidAt: string,
				amount: string,
			) => `${kind} ${invoice}, bezahlt am ${paidAt}: ${amount} erstattet.`,
			cancellationRefundTotal: (amount: string) =>
				`Insgesamt erstattet: ${amount}.`,
			cancellationRefundNote:
				"Erstattungen gehen auf die Karte oder das Bankkonto, mit dem die Rechnung bezahlt wurde, nie als Guthaben. Sie können einige Tage dauern.",
			cancellationConsent: (org: string) =>
				`${org} hat diese Kündigung verlangt und der angezeigten Erstattung zugestimmt`,
			keepSubscription: "Abo behalten",
			cancelling: "Wird gekündigt…",
			cancellationFailed:
				"Die Kündigung wurde nicht wie angezeigt ausgeführt. Prüfe das Abonnement oben, bevor du sie neu berechnest: Wurde es schon gekündigt, kündige es nicht erneut. Hat Stripe einen Schritt nicht bestätigt, bleiben Mitglieder- und Platzänderungen pausiert, während er ohne doppelte Erstattung wiederholt wird; wende dich mit der ID dieser Organisation an den Support, wenn sie pausiert bleiben.",
			cancellationScheduled:
				"Das Abo endet zur Verlängerung. Nichts wurde erstattet.",
			cancellationCancelled: "Das Abo wurde gekündigt.",
			cancellationRefunded: (count: number) =>
				`${count} ${count === 1 ? "Erstattung wurde" : "Erstattungen wurden"} ausgeführt.`,
			cancellationRefundsPending: (count: number) =>
				`${count} ${count === 1 ? "Erstattung wird" : "Erstattungen werden"} noch vom Zahlungsanbieter bearbeitet. Die App verfolgt sie nicht weiter: Kommt eine nicht an, wende dich mit der ID dieser Organisation an den Support.`,
			cancellationRefundsFailed: (count: number, orgId: string) =>
				`${count} ${count === 1 ? "Erstattung ist" : "Erstattungen sind"} beim Zahlungsanbieter fehlgeschlagen. Wende dich mit der Organisation ${orgId} an den Support, damit sie erstattet ${count === 1 ? "wird" : "werden"}.`,
			addTrial: "Testphase hinzufügen",
			startTrialOnPlan: "Testphase mit einem Plan starten",
			startTrialTitle: (org: string) =>
				`Testphase mit einem Plan für ${org} starten`,
			startTrialDescription: "Jeder offene Plan, öffentlich oder privat.",
			trialPlan: "Plan der Testphase",
			billing: "Abrechnung",
			monthly: "Monatlich",
			yearly: "Jährlich",
			public: "Öffentlich",
			assigned: "Privat",
			startTrialNotice: (org: string, plan: string, date: string) =>
				`${org} erhält ${plan} als Testphase bis ${date}, ohne Karte. Die Plätze decken die aktuellen Mitglieder und Einladungen ab, die erhalten bleiben. Danach ist sie für keine weitere Testphase berechtigt.`,
			startTrial: "Testphase starten",
			cancel: "Abbrechen",
			back: "Organisationen",
			trialFailed: "Die Testphase konnte nicht geändert werden.",
			price: "Preis",
			priceVersion: (version: number, interval: string) =>
				`v${version} · ${interval}`,
			planChangeFailed: "Der Plan konnte nicht geändert werden.",
			releaseFailed: "Die Platzänderung konnte nicht abgeschlossen werden.",
			previewFailed: "Die Vorschau der Änderung ist fehlgeschlagen.",
			changePlan: "Plan oder Abrechnung ändern",
			changePlanTitle: (org: string) =>
				`Plan oder Abrechnung von ${org} ändern`,
			paidSeatsStay: (parts: SeatPart[]) =>
				`Bezahlte Plätze bleiben bei ${formatSeatPartsDe({ parts, joiner: " und " })}.`,
			seatMappingLegend: "Platztypen",
			trialSeatNote: (seatType: string) =>
				`Das einzige Mitglied der Organisation erhält bis zum Ende der Testphase den Testplatz des Plans, ${seatType}.`,
			seatMappingDescription: (plan: string) =>
				`${plan} bietet nicht jeden genutzten Platztyp an. Wählen Sie, zu welchem Platztyp jeder wechselt; mehrere können zum selben wechseln. Mitglieder und Einladungen wechseln mit ihren Plätzen.`,
			seatMappingFor: (name: string, paid: number, used: number) =>
				`${name} · ${paid} bezahlt, ${used} genutzt`,
			seatMappingTarget: (name: string) => `Platztyp ${name} wechselt zu`,
			chooseSeatType: "Platztyp wählen",
			seatMappingToday:
				"Plätze, die zu einem anderen Platztyp wechseln, wechseln heute. Setzen Sie das Datum des Inkrafttretens auf heute.",
			seatsAfterChange: (parts: SeatPart[]) =>
				`Nach der Änderung: ${formatSeatPartsDe({ parts, joiner: " und " })}.`,
			newPlan: "Neuer Plan",
			currentPlanNotOffered:
				"Der aktuelle Plan der Organisation ist deaktiviert und wird daher hier nicht angeboten. Wählen Sie den Plan, auf den sie wechseln soll.",
			planOption: (plan: string, version: number, visibility: string) =>
				`${plan} · v${version} · ${visibility}`,
			billingInterval: "Abrechnungsintervall",
			effectiveDate: "Gültig ab",
			defaultsToRenewal: (date: string) =>
				`Standardmäßig die nächste Verlängerung, ${date}.`,
			prorate: "Den Rest des Zeitraums anteilig berechnen",
			atRenewalNote: "Ab der Verlängerungsrechnung.",
			proratedNote:
				"Die Differenz für den Rest des Zeitraums wird anteilig auf der nächsten Rechnung berechnet.",
			notProratedNote:
				"Ohne anteilige Berechnung: keine Belastung oder Gutschrift für den Rest des Zeitraums.",
			intervalChangeNote: (
				interval: string,
				date: string,
				isProrated: boolean,
			) =>
				`Der Wechsel des Intervalls beginnt am ${date} einen neuen Abrechnungszeitraum: Stripe berechnet dann den vollen ${interval.toLowerCase()}en Preis, ${isProrated ? "mit Gutschrift für die ungenutzte Zeit des laufenden Zeitraums" : "ohne Gutschrift für die ungenutzte Zeit des laufenden Zeitraums"}.`,
			amountsEstimated:
				"Für ein späteres Datum sind das die heutigen Beträge; Stripe berechnet die Preise, Rabatte und Steuern, die an diesem Datum gelten.",
			chargeConsent: (total: string, date: string) =>
				`Ich bestätige, dass Stripe mit dieser Änderung am ${date} ${total} berechnet.`,
			trialAppliesNow: (date: string) =>
				`Gilt sofort: Während einer Testphase wird nichts berechnet; sie läuft bis ${date} mit dem neuen Plan weiter.`,
			alreadyOnPrice: (org: string) =>
				`${org} hat bereits diesen Preis und dieses Intervall.`,
			notAsked: (org: string, date: string, total: string) =>
				`${org} muss nicht zustimmen. Nächste Stripe-Rechnung mit der Änderung, am ${date}: ${total}.`,
			insteadOf: (total: string) => `Ohne sie: ${total}.`,
			replacesChange: (date: string) =>
				`Dies ersetzt die bereits für den ${date} geplante Änderung.`,
			voidsInvoice:
				"Eine Platzänderung wartet auf Zahlung. Eine Planänderung ab heute lässt Stripe ihre unbezahlte Rechnung stornieren, und Plätze können dann zum neuen Preis wieder hinzugefügt werden; ein späteres Datum lässt sich erst planen, wenn die Rechnung bezahlt oder verworfen ist.",
			voidsInvoiceAndInvite: (email: string) =>
				`Eine Platzänderung wartet auf Zahlung. Eine Planänderung ab heute lässt Stripe ihre unbezahlte Rechnung stornieren und zieht die darauf wartende Einladung für ${email} zurück, und Plätze können dann zum neuen Preis wieder hinzugefügt werden; ein späteres Datum lässt sich erst planen, wenn die Rechnung bezahlt oder verworfen ist.`,
			lostCode: (code: string) =>
				`Gutscheincode ${code} gilt nur für den aktuellen Plan und rabattiert daher keine Rechnungen mehr, sobald die Änderung gilt.`,
			changePlanConfirm: "Plan ändern",
			scheduledChange: (
				date: string,
				org: string,
				plan: string,
				version: number,
				interval: string,
				isProrated: boolean,
			) =>
				`Geplant für ${date}: ${org} wechselt zu ${plan} mit Preis v${version}, ${interval.toLowerCase()}, ${isProrated ? "mit anteiliger Berechnung für den Rest des Zeitraums" : "ohne anteilige Berechnung"}. Bis dahin ändert sich nichts.`,
			externalChange: (date: string) =>
				`Eine außerhalb der App, in Stripe, vorgenommene Änderung ist für den ${date} geplant.`,
			externalScheduleAttached:
				"Ein im Stripe-Dashboard angelegter Abonnementplan ist angehängt, daher können hier weder der Plan geändert noch die geplante Änderung abgebrochen werden, und die Organisation kann ihre Plätze nicht ändern. Ändern oder brechen Sie ihn im Stripe-Dashboard ab.",
			cancelScheduledChange: "Geplante Änderung abbrechen",
			cancelScheduledChangeTitle: "Geplante Änderung abbrechen?",
			cancelScheduledChangeDescription: (org: string) =>
				`${org} behält den aktuellen Plan, Preis und das Intervall.`,
			cancelChange: "Änderung abbrechen",
			seatOperation: "Mitglieder- oder Platzänderung",
			operationInProgress: "In Bearbeitung",
			unknownAction: "nicht lesbar",
			operationSummary: (action: string, stage: string, updatedAt: string) =>
				`${action} · ${stage} · letzter Schritt ${updatedAt}`,
			waitingInvitation: "Wartende Einladung",
			reservationSummary: (
				email: string,
				tier: string,
				invoiceId: string,
				target: string,
			) =>
				`${email} (${tier}) wartet auf Rechnung ${invoiceId}, die ${target} erhöht`,
			stripeRequests: "Gesendete Stripe-Anfragen",
			stripeSubscription: "Stripe-Abonnement",
			subscriptionEvidence: (
				status: string,
				items: string,
				hasPendingUpdate: boolean,
			) =>
				`${status} · ${items}${hasPendingUpdate ? " · Platzänderung wartet auf Zahlung" : ""}`,
			stripeInvoice: "Stripe-Rechnung",
			clerkPeople: "Clerk-Mitglieder und -Einladungen",
			personEvidence: (
				kind: string,
				email: string,
				role: string,
				tier: string,
			) => `${kind}: ${email} · ${role} · ${tier}`,
			releaseWarning:
				"Freigeben macht in Stripe oder Clerk nichts rückgängig und kann eine noch laufende Anfrage nicht stoppen. Geben Sie erst frei, wenn Stripe und Clerk oben zeigen, ob die Änderung gilt; wenn das nicht erkennbar ist, geben Sie nicht frei.",
			releaseOutcome: "Was Stripe und Clerk zeigen",
			chooseOutcome: "Ergebnis wählen",
			outcomeApplied: "Die Änderung gilt",
			outcomeNotApplied: "Die Änderung gilt nicht",
			outcomeInvite:
				"Der Platz ist bezahlt oder wurde hinzugefügt: Einladung senden",
			releaseNote: "Notiz (was Sie geprüft haben, mindestens 10 Zeichen)",
			release: "Freigeben",
		},
		subscriptionOrder: {
			progress: "Fortschritt des Abonnements",
			planStep: "Plan",
			seatsStep: "Plätze",
			detailsStep: "Zahlungsdaten",
			detailsShortStep: "Zahlung",
			reviewStep: "Prüfen",
			confirmedStep: "Bestätigt",
			completed: "(erledigt)",
			chooseSeatTitle: "Wählen Sie Ihren Platz",
			subscribeTo: (plan: string) => `${plan} abonnieren`,
			reviewTitle: "Prüfen und abonnieren",
			completeTitle: "Sie haben abonniert",
			summary: "Zusammenfassung des Abonnements",
			includes: (seats: string) => `Enthält ${seats}.`,
			seatLine: (count: number, tier: string, price: string) =>
				`${count} ${tier}-${count === 1 ? "Platz" : "Plätze"} × ${price}`,
			paidSeatLegend: (date: string) => `Ihr bezahlter Platz ab dem ${date}`,
			trialSeatSwitch: (trialTier: string, paidTier: string, date: string) =>
				`Ihr ${trialTier}-Platz bleibt bis zum Ende Ihrer Testphase am ${date} und wird dann zu ${paidTier}.`,
			totalExcludingTax: "Gesamt ohne Steuern",
			chargedWhenTrialEnds: "Die Abbuchung erfolgt am Ende Ihrer Testphase am",
			notChargedYet: "Es wird noch nichts berechnet",
			cardSavedNote:
				"Ihre Zahlungsdaten sind gespeichert. Prüfen Sie den genauen Betrag und abonnieren Sie dann.",
			continue: "Weiter",
			changeCode: "Code ändern",
			paymentDetails: "Zahlungsdaten",
			detailsSaved: "Rechnungsadresse und Karte gespeichert",
			codePending:
				"Wenden Sie den Gutscheincode an oder leeren Sie ihn, um zu abonnieren.",
			codeApplied: (code: string) => `${code} angewendet.`,
			discount: "Rabatt",
			tax: "Steuern",
			totalIncludingTax: "Gesamt inklusive Steuern",
			dueAfterCredit: "Fällig nach Ihrem Guthaben",
			thenAfterCode: (amount: string) =>
				`Danach ${amount}, sobald der Code endet.`,
			checking: "Der fällige Betrag wird geprüft…",
			subscribing: "Abonnement wird abgeschlossen…",
			confirmedTitle: "Abonnement bestätigt",
			confirmedNote:
				"Rechnungsadresse und Karte für künftige Rechnungen gespeichert.",
			goToBilling: "Zur Abrechnung",
		},
		orgBilling: {
			orgDisabled:
				"gen.os.build hat diese Organisation deaktiviert, daher können ihre Mitglieder die App nicht nutzen und keinen Plan und keine Testphase starten. Die Abrechnung bleibt sichtbar. Wenden Sie sich an den gen.os.build-Support, um sie wieder zu aktivieren.",
			billingConflict:
				"Die Abrechnung dieser Organisation ist widersprüchlich, daher kann niemand in ihr Projekte öffnen, bis der gen.os.build-Support das behebt. Deswegen wurde nichts gekündigt oder berechnet. Bitte wenden Sie sich an den Support.",
			conflictingIds: (ids: string) =>
				`IDs, die Sie dem Support nennen: ${ids}`,
			externalSchedulePausesSeats:
				"Für dieses Abonnement ist eine außerhalb der App vorgenommene Änderung geplant, daher können Plätze hier nicht geändert werden, bis der gen.os.build-Support sie ändert oder abbricht.",
			seatDiscountUnavailable: (tier: string) =>
				`Die Details Ihres Platzrabatts auf ${tier}-Plätze können nicht gelesen werden. Er gilt weiterhin für Ihre Rechnungen. Platzänderungen und Platzangebote sind pausiert, bis der gen.os.build-Support das behebt; eine ausstehende Änderung bezahlen oder verwerfen und das Abrechnungsportal funktionieren weiterhin.`,
			scheduledSeatDiscountUnknown:
				" Wie Ihr Platzrabatt in diese Änderung übernommen wird, kann nicht angezeigt werden.",
			choosePlan: "Plan wählen",
			firstSeatAsAdmin: "Sie belegen den ersten Platz als Admin.",
			yourSeat: "Ihr Platz",
			trialSeatNote: (seatType: string) =>
				`Testphase: Ihr Platz ist ${seatType}. Zusätzliche Plätze wählen Sie beim Abonnieren.`,
			paidSeats: "Plätze beim Abonnieren",
			yourSeatNote:
				"Sie sind das einzige Admin- oder Mitglied-Konto und wählen daher Ihren eigenen Platztyp.",
			seatDiscountKept: (seatDiscount: string) =>
				`Ihr Platzrabatt (${seatDiscount}) läuft bis zu seinem Enddatum weiter.`,
			promotionCodeOptional: "Gutscheincode (optional)",
			promotionCodeCombines:
				"Nur mit anderen Rabatten kombinierbare Codes gelten zusammen mit dem Platzrabatt.",
			continueToPayment: "Weiter zur Zahlung",
			reactivationConfirmTitle: "Mit Platzrabatt reaktivieren?",
			reactivationFirstInvoiceProrated: (amount: string, date: string) =>
				`Erste Rechnung: ${amount} inklusive Steuern, bis zum ${date}. Nächste Abbuchung: ${date}.`,
			reactivationFirstInvoicePeriod: (amount: string, date: string) =>
				`Erste Rechnung: ${amount} inklusive Steuern, bis zum ${date}. Nächste Abbuchung: ${date}.`,
			reactivationDueAfterBalance: (amount: string) =>
				`Nach Verrechnung mit Ihrem Kontosaldo jetzt fällig: ${amount}.`,
			reactivationNothingDue:
				"Jetzt ist nichts fällig, daher startet der Plan sofort.",
			reactivationBelowMinimum: (amount: string, minimum: string) =>
				`Das liegt unter dem Mindestbetrag von ${minimum}, daher wird jetzt nichts abgebucht: ${amount} kommen auf Ihre nächste Rechnung, und der Plan startet sofort.`,
			reactivationTermsRenewal: (date: string) =>
				`Die erste Rechnung umfasst die Zeit bis zum ${date}, Ihrer nächsten Abbuchung.`,
			reactivationTermsAmountLater: (minimum: string) =>
				`Sie sehen ihren Betrag inklusive Steuern vor dem Bezahlen; abgebucht wird erst, wenn Sie bezahlen. Sind weniger als ${minimum} fällig, startet der Plan sofort und der Betrag kommt auf Ihre nächste Rechnung.`,
			reactivationCreateInvoice: "Erste Rechnung erstellen",
			reactivationInvoiceAmount: (total: string, tax: string, date: string) =>
				`Die erste Rechnung beträgt ${total}, davon ${tax} Steuern, für die Zeit bis zum ${date}, an dem sich Ihr Abonnement verlängert.`,
			reactivationDiscountUntil: (date: string) =>
				`Ihr Platzrabatt gilt bis zum ${date}; ab dann kosten diese Plätze den normalen Preis.`,
			checkoutEndingDiscount: "Ohne Platzrabatt fortfahren",
			endSeatDiscountTitle: "Platzrabatt beenden?",
			endedSeatDiscountUnreadable: (tier: string) =>
				`Ihr früherer Platzrabatt auf ${tier}-Plätze kann hier nicht wiederhergestellt werden. Um ihn zu behalten, wenden Sie sich vor der Reaktivierung an den gen.os.build-Support. Ohne ihn werden diese Plätze zum normalen Preis abgerechnet.`,
			unreadableSeatDiscount: (tier: string) =>
				`auf ${tier}-Plätze, dessen Bedingungen nicht lesbar sind`,
			endSeatDiscountDescription: (seatDiscount: string) =>
				`Ihr Platzrabatt (${seatDiscount}) endet jetzt, und diese Plätze werden zum normalen Preis abgerechnet. Einen Gutscheincode können Sie beim Bezahlen eingeben.`,
			upgradePending: (date: string) =>
				`Der bezahlte Plan wartet auf die Zahlung seiner ersten Rechnung; die Testphase läuft bis dahin weiter. Unbezahlt wird der bezahlte Plan am ${date} storniert.`,
			upgradePendingAfterTrial: (date: string) =>
				`Die Testphase ist beendet, während die erste Rechnung des bezahlten Plans auf Zahlung wartet; Projekte bleiben gesperrt, bis sie bezahlt ist. Unbezahlt wird der bezahlte Plan am ${date} storniert.`,
			reactivationPending: (date: string) =>
				`Die Reaktivierung wartet auf die Zahlung ihrer ersten Rechnung. Bis dahin gibt es keinen Zugriff; sie wird storniert, wenn sie am ${date} noch unbezahlt ist.`,
			cancelReactivation: "Reaktivierung abbrechen",
			reactivationCheckPending:
				"Die Verlängerungstermine und der Platzrabatt der Reaktivierung wurden noch nicht mit den von Ihnen bestätigten abgeglichen. Bis dahin kann ihre Rechnung hier nicht bezahlt werden, und Plan- und Platzänderungen warten.",
			reactivationCheckPendingActive:
				"Ihr Plan ist aktiv, aber die Verlängerungstermine und der Platzrabatt der Reaktivierung wurden noch nicht mit den von Ihnen bestätigten abgeglichen. Bis dahin warten Plan- und Platzänderungen.",
			reactivationCheckFailed: (failure: "mismatch" | "unknown", id: string) =>
				`${failure === "unknown" ? "Der Platzrabatt der Reaktivierung kann nicht mit den von Ihnen bestätigten Bedingungen abgeglichen werden" : "Die Reaktivierung wurde mit anderen Verlängerungsterminen oder einem anderen Platzrabatt eingerichtet als von Ihnen bestätigt"}, und ihre erste Rechnung war bereits bezahlt oder es war nichts fällig. Ihr Plan bleibt aktiv, nichts wurde storniert, erstattet oder gutgeschrieben, aber Plan- und Platzänderungen warten, bis der Support von gen.os.build sie korrigiert. Wenden Sie sich mit der ID ${id} an den Support.`,
			reactivationCheckFailedUnpaid: (
				failure: "mismatch" | "unknown",
				id: string,
			) =>
				`${failure === "unknown" ? "Der Platzrabatt der Reaktivierung kann nicht mit den von Ihnen bestätigten Bedingungen abgeglichen werden" : "Die Reaktivierung wurde mit anderen Verlängerungsterminen oder einem anderen Platzrabatt eingerichtet als von Ihnen bestätigt"}, und ihre unbezahlte erste Rechnung konnte nicht storniert werden. Bezahlen Sie sie nicht: Es wurde nichts belastet. Reaktivierung prüfen versucht erneut, sie zu stornieren, was die Reaktivierung beendet; bis dahin warten Plan- und Platzänderungen. Wenden Sie sich mit der ID ${id} an den gen.os.build-Support, wenn es weiterhin fehlschlägt.`,
			checkReactivation: "Reaktivierung prüfen",
			checkReactivationFailed: "Die Reaktivierung konnte nicht geprüft werden.",
			cancelReactivationFailed:
				"Die Reaktivierung konnte nicht abgebrochen werden.",
			reactivateTitle: (org: string) => `${org} reaktivieren`,
			minSeatsNote:
				"Die Plätze können nicht unter die Plätze fallen, die Ihre Mitglieder und Einladungen belegen.",
			plan: "Plan",
			billing: "Abrechnung",
			billingInterval: "Abrechnungsintervall",
			intervalOnly: (interval: string) =>
				`Nur ${interval.toLowerCase()} für diesen Plan.`,
			yearlySaving: (amount: string) =>
				`Jährlich sparen Sie ${amount} bei der Grundgebühr.`,
			monthly: "Monatlich",
			yearly: "Jährlich",
			month: "Monat",
			year: "Jahr",
			additionalSeats: (tier: string) => `Zusätzliche ${tier}-Plätze`,
			perSeat: (price: string, interval: string) =>
				`${price} pro Platz / ${interval}`,
			planIncludes: (plan: string, seats: string) =>
				`${plan} enthält ${seats}.`,
			includedSeats: (parts: SeatPart[]) =>
				formatSeatPartsDe({ parts, joiner: " und " }),
			seatCounts: (parts: SeatPart[]) =>
				formatSeatPartsDe({ parts, joiner: " + " }),
			baseFee: (seats: string) => `Grundgebühr · enthält ${seats}`,
			extraLine: (count: number, tier: string, price: string) =>
				`${count} zusätzliche ${tier} × ${price}`,
			total: "Gesamt",
			perInterval: (amount: string, interval: string) =>
				`${amount} / ${interval}`,
			startTrial: (days: number) => `${days}-tägige Testphase starten`,
			startingTrial: "Testphase wird gestartet…",
			continueToCheckout: "Weiter zur Kasse",
			finishSetupTitle: (org: string) => `Einrichtung von ${org} abschließen`,
			finishSetupDescription:
				"Wählen Sie einen Plan, um gen.os.build mit dieser Organisation zu nutzen.",
			setupChoiceTitle: (plan: string) => `Weiter mit ${plan}`,
			setupPreparedBySupport:
				"Der gen.os.build-Support hat diese Planauswahl für Ihre Organisation vorbereitet.",
			setupTrialDescription: (days: number) =>
				`Ihre ${days}-tägige Testphase beginnt, wenn Sie unten klicken. Es ist keine Karte nötig und nichts wird berechnet.`,
			setupPaidDescription:
				"Prüfen Sie Ihre Auswahl und fahren Sie dann bei Stripe mit der Bezahlung fort. Nichts wird berechnet, bevor Sie dort bestätigen.",
			setupStartTrial: (days: number, plan: string) =>
				`${days}-tägige Testphase von ${plan} starten`,
			setupContinueToPayment: (plan: string) =>
				`Weiter zur Bezahlung von ${plan}`,
			setupChooseOther: "Anderen Plan wählen",
			setupPlanUnavailable:
				"Der gewählte Plan steht neuen Organisationen nicht mehr offen. Wählen Sie einen anderen Plan.",
			setupTrialUnavailable:
				"Eine Testphase dieses Plans ist nicht mehr verfügbar. Sie können ihn kaufen oder einen anderen Plan wählen.",
			setupSeatsChanged:
				"Ihre Platzauswahl passt nicht mehr zum aktuellen Preis dieses Plans. Prüfen Sie sie und wählen Sie erneut.",
			setupYourSeat: (tier: string) => `Ihr Platz: ${tier}`,
			linkOrgNotMember:
				"Dieser Abrechnungslink gehört zu einer Organisation, in der Sie nicht Mitglied sind, daher wird er hier nicht angewendet.",
			linkOrgSwitching:
				"Die Organisation dieses Abrechnungslinks wird geöffnet…",
			linkOrgSwitchFailed:
				"Die Organisation dieses Abrechnungslinks konnte nicht geöffnet werden.",
			linkOrgRetry: "Erneut versuchen",
			linkOrgShowCurrent: "Abrechnung meiner aktuellen Organisation anzeigen",
			noPlans:
				"Derzeit ist kein Plan für die Anmeldung offen. Wenden Sie sich an gen.os.build, um Zugang zu erhalten.",
			privatePlan: (plan: string, org: string) =>
				`${plan} ist privat: gen.os.build hat ihn ${org} zugewiesen, andere Organisationen können ihn nicht wählen.`,
			archivedPlan: (plan: string, org: string) =>
				`${plan} ist deaktiviert, daher kann keine Organisation ihn mehr beginnen. Die Testphase von ${org} begann vorher und gilt bis zu ihrem Ende.`,
			closedPlan: (plan: string, org: string) =>
				`${plan} ist für neue Organisationen geschlossen, ${org} behält aber den Zugang.`,
			reactivateKeepsPrice:
				"Die Reaktivierung startet ein neues Abonnement zum aktuellen Preis.",
			previousPlanUnavailable: (org: string) =>
				`Der bisherige Plan von ${org} wird nicht mehr angeboten, daher braucht die Reaktivierung einen der Pläne oben.`,
			subscribeNow: "Jetzt abonnieren",
			trialExpiresIn: (days: number) =>
				`Ihre Testphase endet in ${days} ${days === 1 ? "Tag" : "Tagen"}.`,
			subscribeToAvoidInterruption:
				"Abonnieren Sie jetzt, um eine Unterbrechung des Dienstes zu vermeiden.",
			subscribedTrialNote: (date: string) =>
				`Sie haben abonniert. Die Testphase läuft bis zum ${date}; dann wird die erste Zahlung belastet.`,
			subscribedTrialEnds: (date: string) => `Ihr Abonnement endet am ${date}.`,
			unmanagedTrialNote:
				"Diese Testphase wurde außerhalb der App eingerichtet, daher kann sie hier nicht abonniert werden. Wenden Sie sich an den Support, um sie zu ändern.",
			subscribeKeepsPlan: (plan: string) =>
				`Beim Abonnieren behalten Sie ${plan} zum Preis aus der Testphase.`,
			subscribeDetailsNeeded:
				"Hinterlegen Sie zuerst auf einer sicheren Seite eine Rechnungsadresse und eine Zahlungsmethode. Dabei wird nichts berechnet.",
			addPaymentDetails: "Zahlungsdaten hinzufügen",
			applyCode: "Anwenden",
			promotionCodeAcceptedBeforeDetails:
				"Code akzeptiert. Wir bestätigen den Rabatt, nachdem Sie Zahlungsdaten hinzugefügt haben.",
			confirmSubscribe: "Abonnieren",
			reactivationNotCheckedTitle: "Reaktivierung noch nicht geprüft",
			reactivationNeedsSupportTitle: "Reaktivierung braucht den Support",
			reactivationDontPayTitle: "Bezahlen Sie die Reaktivierungsrechnung nicht",
			reactivationWaitingTitle: "Reaktivierung wartet auf Zahlung",
			pendingCancelledOn: (date: string) =>
				`Bleibt es unbezahlt, wird dieses ausstehende Abonnement am ${date} storniert.`,
			upgradeWaitingTitle: "Bezahlter Plan wartet auf Zahlung",
			upgradeWaitingAfterTrialTitle:
				"Testphase beendet, bezahlter Plan wartet auf Zahlung",
			planExpiredTitle: "Der Plan Ihrer Organisation ist abgelaufen.",
			planCancelledTitle: "Der Plan Ihrer Organisation ist gekündigt.",
			reactivateNote:
				"Wählen Sie einen Plan, um zu reaktivieren. Mitglieder und Einladungen bleiben erhalten.",
			askToReactivate:
				"Bitten Sie eine Admin- oder Finanzperson, zu reaktivieren. Mitglieder und Einladungen bleiben erhalten.",
			nextInvoice: "Nächste Rechnung",
			projectedTotal: "Voraussichtlicher Betrag",
			earlierPrice: "Früherer Preis",
			earlierPriceNote:
				"Sie behalten den Preis und die enthaltenen Plätze, mit denen Sie sich angemeldet haben. Neue Kunden zahlen den aktuellen Preis.",
			seatSubscription: "Platz-Abonnement",
			seatColumn: "Platz",
			includedColumn: "Enthalten",
			paidExtraColumn: "Zusätzlich bezahlt",
			totalColumn: "Gesamt",
			usedColumn: "Belegt",
			manageExtraSeats: "Bezahlte zusätzliche Plätze verwalten",
			baseIncludes: (seats: string) => `Die Grundgebühr enthält ${seats}.`,
			extraTierSeats: (tier: string) => `Zusätzliche ${tier}-Plätze`,
			extraSeatsInUse: (count: number, tier: string) =>
				`Mindestens ${count} werden für die belegten ${tier}-Plätze benötigt. Geben Sie zuerst unter Mitglieder welche frei.`,
			eachPerInterval: (price: string, interval: string) =>
				`${price} pro Platz / ${interval}.`,
			activeSeatsNote:
				"Hinzugefügte Plätze gelten nach der Zahlung; weniger Plätze gelten sofort und ohne Erstattung, auch wenn in derselben Änderung andere Plätze hinzukommen.",
			dueNow: (amount: string, date: string) =>
				`${amount} sind jetzt fällig, anteilig bis ${date}.`,
			previewing: "Fälliger Betrag wird geprüft …",
			trialSeatsNote: "Während der Testphase gelten Platzänderungen sofort.",
			paymentPendingAmount: (
				pending: string,
				amount: string,
				date: string,
				current: string,
			) =>
				`Zahlung erforderlich: Die bezahlten zusätzlichen Plätze steigen auf ${pending}, sobald die Rechnung über ${amount}, anteilig bis ${date}, bezahlt ist. Bis dahin behalten Sie ${current}.`,
			payAmount: (amount: string) => `${amount} bezahlen`,
			discardChange: "Änderung verwerfen",
			discardFailed: "Die Änderung konnte nicht verworfen werden.",
			discountRedeemed: (
				code: string,
				discount: string,
				duration: string,
				date: string,
			) =>
				`Gutscheincode ${code} (${discount} ${duration}), eingelöst am ${date}`,
			discountPercent: (percent: number) => `${percent} % Rabatt`,
			discountAmount: (amount: string) => `${amount} Rabatt`,
			discountForever: "dauerhaft",
			discountOnce: "auf die erste Rechnung",
			discountMonths: (months: number) =>
				`für ${months} ${months === 1 ? "Monat" : "Monate"}`,
			discountOtherPlan:
				", gilt nur für einen anderen Plan und rabattiert daher keine Rechnungen mehr.",
			deletedCouponDiscount: (code: string, redeemedAt: string) =>
				`Gutscheincode ${code}, eingelöst am ${redeemedAt}, gilt weiterhin, aber seine Details können nicht angezeigt werden.`,
			deletedCouponDiscountEnds: (date: string) => ` Er endet am ${date}.`,
			discountEveryInvoice: ", gilt für jede Rechnung.",
			discountNextInvoice: ", gilt nur für die nächste Rechnung.",
			discountRunOut: (date: string) =>
				`, ist ausgeschöpft: Die Rechnung am ${date} kostet den vollen Preis.`,
			discountUntil: (date: string) =>
				`, gilt für Rechnungen vor dem ${date}, danach der volle Preis.`,
			scheduledChange: (
				date: string,
				org: string,
				plan: string,
				version: number,
				interval: string,
				isProrated: boolean,
			) =>
				`Geplant für ${date}: ${org} wechselt zu ${plan} mit Preis v${version}, ${interval}, ${isProrated ? "mit anteiliger Berechnung der Differenz für den Rest des Zeitraums auf der nächsten Rechnung" : "ohne anteilige Berechnung"}. Bis ${date} ändert sich nichts.`,
			scheduledExternal: (date: string) =>
				`Für dieses Abonnement ist eine Änderung für den ${date} geplant.`,
			scheduledRenewal: (date: string, amount: string) =>
				`Die nächste Rechnung mit der Änderung, am ${date}, beträgt ${amount} für dieselben Plätze.`,
			scheduledWithdrawal: ({
				isOfferOpen,
				isInvoicePending,
				inviteEmail,
			}: Withdrawal) => {
				const outcomes = [
					...(isOfferOpen ? ["zieht das offene Platzangebot zurück"] : []),
					...(isInvoicePending
						? ["storniert die unbezahlte Platzrechnung"]
						: []),
					...(inviteEmail
						? [`zieht die darauf wartende Einladung für ${inviteEmail} zurück`]
						: []),
				];
				if (outcomes.length === 0) return undefined;
				const list = new Intl.ListFormat("de").format(outcomes);
				return isInvoicePending
					? `${list}; Plätze können danach zum neuen Preis wieder hinzugefügt werden`
					: list;
			},
			scheduledOnDate: (note: string) =>
				` An diesem Datum gilt: Die Änderung ${note}.`,
			pendingBeforeChange: (date: string, note: string) =>
				` Ist sie am ${date} noch unbezahlt, gilt: Die geplante Planänderung ${note}.`,
			pendingEndsCodes: (codes: string) =>
				` Mit der Zahlung endet auch der Gutscheincode ${codes}, der nicht mit dem Platzrabatt kombinierbar ist.`,
			acceptedPending: (offer: string) =>
				` Sie haben das Platzangebot angenommen (${offer}). Verwerfen beendet das Angebot; gen.os.build kann Ihnen ein neues senden.`,
			discountEnded: (date: string) =>
				`, ist ausgeschöpft: Zuletzt rabattierte er die Rechnung vom ${date}.`,
			monthlyLower: "monatlich",
			yearlyLower: "jährlich",
			plansFailed: "Die Pläne konnten nicht geladen werden.",
			trialFailed: "Die Testphase konnte nicht gestartet werden.",
			subscribeFailed: "Das Abonnement konnte nicht abgeschlossen werden.",
			previewFailed: "Der fällige Betrag konnte nicht geprüft werden.",
			yourPlan: "Ihr Plan",
			status: "Status",
			noPlan: "Kein Plan",
			reactivate: "Reaktivieren",
			keptWhileInactive: "Mitglieder und Einladungen bleiben erhalten.",
			seats: "Plätze",
			seatsAvailable: (seats: number, available: number) =>
				`${seats} (${available} verfügbar)`,
			manageSeats: "Plätze verwalten",
			seatsDialogDescription: (used: number, seats: number) =>
				`${used} von ${seats} ${seats === 1 ? "Platz ist" : "Plätzen sind"} vergeben oder durch Einladungen reserviert.`,
			seatsInUse: (used: number) =>
				`${used} ${used === 1 ? "Platz ist" : "Plätze sind"} belegt. Entfernen Sie zuerst Mitglieder oder ziehen Sie Einladungen zurück.`,
			seatsChangeHint:
				"Zusätzliche Plätze werden sofort anteilig berechnet. Weniger Plätze gelten ohne Erstattung.",
			cancel: "Abbrechen",
			save: "Speichern",
			renewalDate: "Verlängerungsdatum",
			trialEnds: "Testphase endet",
			cancelAt: "Endet am",
			pendingInvite: (email: string, tier: string) =>
				`Enthält die Einladung für ${email} mit einem ${tier}-Platz; sie wird erst nach Bezahlung der Rechnung gesendet.`,
			paymentPending: (
				pending: number,
				seats: number,
				amount: string,
				date: string,
			) =>
				`Zahlung erforderlich: ${pending} ${pending === 1 ? "Platz wartet" : "Plätze warten"} auf die Bezahlung der Rechnung über ${amount}, anteilig bis ${date}. Bis dahin behalten Sie ${seats}.`,
			payInvoice: "Rechnung bezahlen",
			overUsed: (excess: number) =>
				`Es ${excess === 1 ? "ist 1 Platz" : `sind ${excess} Plätze`} mehr belegt als bezahlt, daher kann niemand in der Organisation Projekte öffnen. Kaufen Sie weitere Plätze oder entfernen bzw. stufen Sie Mitglieder herab oder ziehen Sie Einladungen unter Mitglieder zurück.`,
			overUsedMember: (excess: number) =>
				`Es ${excess === 1 ? "ist 1 Platz" : `sind ${excess} Plätze`} mehr belegt als bezahlt, daher kann niemand in der Organisation Projekte öffnen. Bitten Sie einen Organisationsadmin, weitere Plätze zu kaufen oder Mitglieder zu entfernen oder Einladungen zurückzuziehen.`,
			overUsedFinance: (excess: number) =>
				`Es ${excess === 1 ? "ist 1 Platz" : `sind ${excess} Plätze`} mehr belegt als bezahlt, daher kann niemand in der Organisation Projekte öffnen. Kaufen Sie weitere Plätze oder bitten Sie einen Organisationsadmin, Mitglieder zu entfernen oder Einladungen zurückzuziehen.`,
			paymentTitle: "Zahlung & Rechnungen",
			paymentDescription: "Zahlungsmethoden, Rechnungsdaten und Rechnungen.",
			openPortal: "Abrechnung verwalten",
			loading: "Abrechnung wird geladen…",
			loadFailed: "Der Organisationsplan konnte nicht geladen werden.",
			checkoutFailed:
				"Der Organisations-Checkout konnte nicht gestartet werden.",
			updateFailed: "Die Anzahl der Plätze konnte nicht geändert werden.",
			portalFailed: "Die Organisationsabrechnung konnte nicht geöffnet werden.",
			hasCompanySeat:
				"Ihre Organisation stellt Ihnen einen Platz bereit, Sie brauchen also kein eigenes Abonnement.",
			seatsExceeded:
				"Ihre Organisation belegt mehr Plätze als bezahlt, daher kann niemand in ihr Projekte öffnen. Ein Organisationsadmin kann das unter Organisationsabrechnung beheben.",
			personalPlanNoAccess:
				"Ein persönliches Abonnement gibt keinen Zugang mehr zur App, nur noch ein Organisationsplan.",
			noOrgPlan:
				"Ihre Organisation hat keinen aktiven Plan. Ein Organisationsadmin kann einen unter Organisationsabrechnung kaufen.",
			goToOrgBilling: "Organisationsabrechnung",
			accessFailed: "Ihr Zugang zur App konnte nicht geprüft werden.",
			retry: "Erneut versuchen",
		},
		onboarding: {
			title: "Organisation erstellen",
			choosePlan: "Plan wählen",
			choosePlanDescription:
				"Wählen Sie den Plan für Ihre neue Organisation. Die Testphase starten oder bezahlen Sie, nachdem Sie sie benannt haben.",
			noPlans: "Derzeit stehen neuen Organisationen keine Pläne offen.",
			plansFailed:
				"Die Pläne konnten nicht geladen werden. Laden Sie neu, um es erneut zu versuchen.",
			continueWithTrial: (days: number) =>
				`Weiter mit ${days}-tägiger Testphase`,
			continueWithPayment: "Weiter mit Bezahlung",
			nameOrg: "Organisation benennen",
			nameOrgTrial: (plan: string, days: number) =>
				`Sie haben eine ${days}-tägige Testphase von ${plan} gewählt. Sie startet ohne Karte, sobald die Organisation erstellt ist.`,
			nameOrgPaid: (plan: string) =>
				`Sie haben ${plan} gewählt. Nach dem Erstellen der Organisation fahren Sie mit der Bezahlung fort. Bis dahin wartet die Organisation mit dieser Auswahl auf die Bezahlung.`,
			orgName: "Name der Organisation",
			createAndStartTrial: (days: number) =>
				`Organisation erstellen und ${days}-tägige Testphase starten`,
			createAndPay: "Organisation erstellen und zur Bezahlung",
			retryContinue: "Erneut versuchen",
			setupUnconfirmed:
				"Noch nicht bestätigt, ob die Organisation erstellt wurde. Ein neuer Versuch sucht sie, statt eine weitere zu erstellen.",
			unfinishedSetups: "Nicht abgeschlossene Einrichtungen",
			resumeSetup: "Einrichtung fortsetzen",
			setupWaitsForPayment: "Wartet auf Bezahlung",
			setupWaitsForTrial: "Testphase nicht gestartet",
			setupNotCreated: "Noch nicht erstellt",
			creating: "Wird erstellt…",
			createFailed: "Die Organisation konnte nicht erstellt werden.",
			back: "Zurück",
			cancel: "Abbrechen",
			chooseOrg: "Organisation wählen",
			createOrg: "Organisation erstellen",
			yourOrgs: "Ihre Organisationen",
			noOrgs: "Sie sind noch in keiner Organisation Mitglied.",
			invitations: "Einladungen",
			accept: "Annehmen",
			acceptFailed: "Die Einladung konnte nicht angenommen werden.",
			suggestions: "Organisationen, denen Sie beitreten können",
			requestToJoin: "Beitritt anfragen",
			requested: "Angefragt",
			joinFailed: "Die Beitrittsanfrage konnte nicht gesendet werden.",
			showMore: "Mehr anzeigen",
		},
		invitation: {
			unavailable: "Diese Einladung kann nicht angenommen werden.",
			backToProjects: "Zurück zu Projekten",
		},
		prospectInvite: {
			invites: "Interessenten-Einladungen",
			invite: "Interessent einladen",
			plansLoading: "Pläne werden geladen…",
			plansLoadFailed:
				"Pläne konnten nicht geladen werden, daher kann kein Interessent eingeladen werden.",
			inviteDescription:
				"Die Person erhält einen Link, um ihre Organisation mit einer Testphase dieses Plans zu erstellen, ohne Karte. Nur wer mit dieser bestätigten E-Mail-Adresse angemeldet ist, kann sie annehmen, bis sie abläuft.",
			orgName: "Name der Organisation",
			email: "E-Mail",
			plan: "Plan",
			trialDays: "Testtage",
			trialDaysInvalid: (max: number) => `Geben Sie 1 bis ${max} Tage ein.`,
			expiresOn: "Läuft ab am",
			expiresOnDescription: (timeZone: string) =>
				`Der Link funktioniert bis zum Ende dieses Tages (${timeZone}).`,
			expiresOnInvalid: (last: string) =>
				`Wählen Sie einen Tag von heute bis ${last}.`,
			noPlans:
				"Kein für neue Organisationen offener Plan bietet eine Testphase.",
			create: "Einladung erstellen",
			createFailed: "Die Einladung konnte nicht erstellt werden.",
			invitesLoadFailed:
				"Interessenten-Einladungen konnten nicht geladen werden.",
			noInvites: "Noch keine Einladungen",
			emailInvalid: "Geben Sie eine gültige E-Mail-Adresse ein.",
			invitee: "Eingeladen",
			delivery: "E-Mail",
			status: "Status",
			expires: "Läuft ab",
			trialOf: (plan: string, days: number) =>
				`${plan} · ${days}-tägige Testphase`,
			copyLink: "Link kopieren",
			linkCopied: "Link kopiert",
			copyFailed: "Der Link konnte nicht kopiert werden.",
			retryEmail: "E-Mail erneut anfordern",
			retryFailed: "Die E-Mail konnte nicht angefordert werden.",
			revoke: "Widerrufen",
			revokeTitle: (email: string) => `Einladung für ${email} widerrufen?`,
			revokeDescription:
				"Ihr Link funktioniert nicht mehr. Ein Konto anlegen kann trotzdem jeder, nur diese Einladung nicht mehr nutzen. Eine bereits angenommene Einladung kann nicht widerrufen werden.",
			revokeFailed: "Die Einladung konnte nicht widerrufen werden.",
			deliveries: {
				requested: "E-Mail angefordert",
				accountExists: "Hat ein Konto",
				alreadyInvited: "Bereits eingeladen",
				refused: "Nicht gesendet",
				unconfirmed: "Nicht bestätigt",
			},
			deliveryNotes: {
				requested:
					"Clerk wurde gebeten, den Link per E-Mail zu senden; ob sie ankommt, ist damit nicht bestätigt. Sie können ihn auch kopieren.",
				accountExists:
					"Nicht gesendet, da diese Adresse bereits ein Konto hat. Kopieren Sie den Link und senden Sie ihn selbst.",
				alreadyInvited:
					"Nicht gesendet, da diese Adresse bereits eine offene Einladung hat. Kopieren Sie den Link und senden Sie ihn selbst.",
				refused:
					"Clerk hat sie nicht gesendet, meist weil diese Adresse bereits ein Konto oder eine offene Einladung hat. Kopieren Sie den Link und senden Sie ihn selbst.",
				unconfirmed:
					"Clerk hat die E-Mail nicht bestätigt. Fordern Sie sie erneut an oder kopieren Sie den Link und senden Sie ihn selbst.",
			},
			statuses: {
				pending: "Offen",
				expired: "Abgelaufen",
				revoked: "Widerrufen",
				claimed: "Angenommen",
				completed: "Testphase gestartet",
			},
			title: "Sie sind eingeladen",
			acceptTitle: "Testphase starten",
			acceptDescription: (org: string, plan: string, days: number) =>
				`Erstellen Sie ${org} mit einer ${days}-tägigen Testphase von ${plan}. Es ist keine Karte nötig.`,
			startTrial: "Organisation erstellen und Testphase starten",
			starting: "Wird gestartet…",
			startFailed:
				"Die Testphase konnte nicht gestartet werden. Versuchen Sie es erneut.",
			wrongAccount: "Diese Einladung ist für ein anderes Konto",
			wrongAccountDescription:
				"Melden Sie sich ab und melden Sie sich mit der E-Mail-Adresse an oder registrieren Sie sich, an die die Einladung ging.",
			unavailable: "Diese Einladung ist nicht verfügbar",
			unavailableDescription:
				"Sie ist eventuell abgelaufen oder wurde widerrufen. Bitten Sie die einladende Person um eine neue.",
			loadFailed: "Die Einladung konnte nicht geladen werden.",
			retry: "Erneut versuchen",
		},
		errorMessages: {
			whoops: "Hoppla!",
			goToProjects: "Zu Projekten gehen",
			errors: {
				generic: "Etwas ist schief gelaufen.",
				401: "Sie sind nicht berechtigt, diese Seite anzuzeigen.",
				403: "Sie haben keine Berechtigung, auf diese Seite zuzugreifen.",
				404: "Die gesuchte Seite existiert nicht.",
				410: "Die gesuchte Seite wurde dauerhaft entfernt.",
				429: "Sie haben zu viele Anfragen gestellt. Bitte versuchen Sie es später erneut.",
				500: "Etwas ist schief gelaufen.",
				503: "Etwas ist schief gelaufen.",
			},
		},
	},
};

function formatSeatParts({
	parts,
	joiner,
}: {
	parts: SeatPart[];
	joiner: string;
}) {
	const counted = parts.filter(({ count }) => count > 0);
	if (counted.length === 0) return "no seats";
	const total = counted.reduce((sum, { count }) => sum + count, 0);
	return `${counted.map(({ count, name }) => `${count} ${name}`).join(joiner)} ${total === 1 ? "seat" : "seats"}`;
}

function formatSeatPartsDe({
	parts,
	joiner,
}: {
	parts: SeatPart[];
	joiner: string;
}) {
	const counted = parts.filter(({ count }) => count > 0);
	if (counted.length === 0) return "keine Plätze";
	return counted
		.map(
			({ count, name }) =>
				`${count} ${name}-${count === 1 ? "Platz" : "Plätze"}`,
		)
		.join(joiner);
}

interface SeatPart {
	count: number;
	name: string;
}

interface Withdrawal {
	isOfferOpen: boolean;
	isInvoicePending: boolean;
	inviteEmail?: string;
}

function getAdminTermsProblem(problem: string) {
	if (problem === "deleted coupon")
		return "has a seat discount whose Stripe coupon was deleted, so it can't be checked against its confirmed terms";
	const term =
		{
			currency: "currency",
			"renewal dates": "renewal dates",
			"seat discount": "seat discount",
		}[problem] ?? "terms";
	return `doesn't match its confirmed ${term}`;
}

function getAdminTermsProblemDe(problem: string) {
	if (problem === "deleted coupon")
		return "hat einen Platzrabatt, dessen Stripe-Gutschein gelöscht wurde, daher kann sie nicht mit ihren bestätigten Bedingungen abgeglichen werden";
	const term =
		{
			currency: "Währung",
			"renewal dates": "Verlängerungstermine",
			"seat discount": "Platzrabatt",
		}[problem] ?? "Bedingungen";
	return `entspricht nicht den bestätigten Bedingungen (${term})`;
}
